import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from './AuthContext';
import Training from './Training';
import Ctf from './Ctf';
import ContributorsAdmin from './ContributorsAdmin';
import ScreeningAdmin from './ScreeningAdmin';
import MailCampaignAdmin from './MailCampaignAdmin';
import AboutGalleryAdmin from './AboutGalleryAdmin';
import EventCalendar from './EventCalendar';
import AvatarCropper from './AvatarCropper';
import { generatePreviewEffects } from '../utils/profileEffects';
import { formatDate as fmtDMY, formatDateTime as fmtDMYTime } from '../utils/dateFmt';
import DateInput from './DateInput';
import Paginator, { usePaginator } from './Paginator';
import TwoFactorPanel from './TwoFactorPanel';
import useBottomBoundedViewport from '../utils/useBottomBoundedViewport';
import useBodyScrollLock from '../utils/useBodyScrollLock';
import '../styles/Dashboard.css';
import '../styles/Profile.css';

// Map admin tab keys to their dedicated deep-link routes so the URL stays in
// sync as the user switches management panels in /setting.
const ADMIN_TAB_ROUTES = {
  members: '/membermanager',
  blogs: '/blogmanager',
  events_admin: '/eventmanager',
  achievements: '/achievementmanager',
  recruitment_campaigns: '/recruitmentmanager',
  recruitment_applicants: '/recruitmentapplicantsmanager',
  recruitment_questions: '/recruitmentquestionsmanager',
  recruitment_email: '/recruitmentemailmanager',
  contributors: '/contributormanager',
  forum_admin: '/forummanager',
  resources_admin: '/resourcemanager',
  training: '/trainingmanager',
  screening: '/screeningmanager',
  nomination_admin: '/nominationmanager',
  mail_campaign: '/mailmanager',
  about_gallery: '/clubinfomanager',
  event_calendar: '/calendarmanager',
  smtp: '/smtpmanager',
};

// Map member-facing (/dashboard) tab keys to their dedicated deep-link routes.
const MEMBER_TAB_ROUTES = {
  overview: '/dashboard',
  profile: '/profile-settings',
  security: '/security',
  forum: '/qa',
  training: '/training-area',
  resources: '/resources',
  event_calendar: '/calendar',
  nomination: '/nomination',
};

const HOME_TAB_KEYS = new Set([
  'achievements', 'blogs', 'events_admin', 'contributors', 'about_gallery'
]);
const TRAINING_TAB_KEYS = new Set(['training', 'screening', 'nomination_admin']);
const RECRUITMENT_TAB_KEYS = new Set([
  'recruitment_campaigns', 'recruitment_applicants', 'recruitment_questions', 'recruitment_email'
]);

const cleanHomeTabLabel = (label) => String(label || '')
  .replace(/^Quản\s+Lý\s+/i, '')
  .replace(/\s*\(home\)\s*/ig, '')
  .trim();

const ROLE_LABELS = { ban_dieu_hanh: 'Ban Điều Hành', thanh_vien: 'Thành Viên', ky_thuat: 'Kỹ Thuật', ban_su_kien: 'Ban Sự Kiện', ban_dao_tao: 'Ban Đào Tạo' };
const POSITION_LABELS = { mam_non: 'Mầm Non', tap_su: 'Tập Sự', culi_cap_cao: 'Culi Cấp Cao', bo_lao: 'Bô Lão', founder: 'Founder' };
const TITLE_LABELS = {
  founder: 'Founder',
  chu_nhiem: 'Chủ Nhiệm',
  pho_chu_nhiem: 'Phó Chủ Nhiệm',
  truong_ban_dao_tao: 'Trưởng Ban Đào Tạo',
  pho_ban_dao_tao: 'Phó Ban Đào Tạo',
  truong_ban_su_kien: 'Trưởng Ban Sự Kiện',
  pho_ban_su_kien: 'Phó Ban Sự Kiện'
};
const CTF_FIELDS = { reverse_engineering: 'Reverse Engineering', pwnable: 'Binary Exploitation', web_exploitation: 'Web Exploitation', cryptography: 'Cryptography', forensics: 'Digital Forensics', osint: 'Open Source Intelligence' };
const PROFILE_THEMES = ['hacker', 'dreamy', 'cute', 'classic', 'strong', 'space', 'nebula', 'sakura', 'miku', 'orange'];
const PROFILE_EFFECTS = ['snowflakes', 'particles', 'stars', 'soundwave', 'streaks', 'bubbles', 'matrix', 'fireflies', 'warpspeed', 'none'];
const PROFILE_CURSORS = ['default', 'miku', 'miku1', 'miku3', 'sparkle', 'heart', 'neon', 'hacker', 'star', 'sakura', 'rem', 'flame'];
const PROFILE_CURSOR_LABELS = {
  default: 'Mặc định',
  miku: 'Miku 2 (hoa lá)',
  miku1: 'Miku 1 (cổ điển)',
  miku3: 'Miku 3 (nhảy múa)',
  sparkle: 'Lấp lánh hồng',
  heart: 'Trái tim hồng',
  neon: 'Neon cyber',
  hacker: 'Mũi tên hacker',
  star: 'Ngôi sao vàng',
  sakura: 'Hoa anh đào',
  rem: 'Rem (Re:Zero)',
  flame: 'Lửa'
};

// Preview panel for theme / effect / cursor choices.
// Uses the same effect generators as the public profile page so every effect
// renders identically in the preview (previously the dashboard had its own
// simplified loop that left half the effects invisible or misshapen).
const ProfilePreview = ({ theme, effect, cursor, user }) => {
  const effectEls = React.useMemo(
    () => generatePreviewEffects(effect || 'none'),
    [effect]
  );

  return (
    <div className={`theme-preview theme-${theme} cursor-${cursor}`}>
      <div className="theme-preview-label">Xem trước</div>
      <div className={`profile-effects effect-${effect}`}>
        {effectEls.map(el => (
          <div key={el.key} className={el.cls} style={el.style}>{el.text}</div>
        ))}
      </div>
      <div className="theme-preview-inner">
        <div className="theme-preview-avatar">
          {user?.avatar ? (
            <img src={`/uploads/profiles/${user.avatar}`} alt="" />
          ) : (
            <span>{(user?.fullName || user?.username || 'A').charAt(0).toUpperCase()}</span>
          )}
        </div>
        <div className="pf-hero-eyebrow">ISP Profile</div>
        <div className="pf-hero-name">{user?.fullName || user?.username || 'Your name'}</div>
        <div className="pf-hero-username"><span>@{user?.username || 'username'}</span></div>
        <div className="theme-preview-chips">
          <span className="pf-tag pf-tag-role">Theme: {theme}</span>
          <span className="pf-tag">Effect: {effect}</span>
          <span className="pf-tag">Cursor: {cursor}</span>
        </div>
        <button type="button" className="pf-btn pf-btn-primary">Hover me</button>
      </div>
    </div>
  );
};

// Resource categories list with built-in pagination. Splits the (potentially
// long) list of categories per active type into pages so admins managing a
// large kho tài liệu aren't forced to scroll endlessly. Items inside each
// expanded category are rendered in full because they're already grouped
// (and usually a handful per category).
const ResourceListPaged = ({ cats, emptyText, fmtDMYTime, POSITION_LABELS, onCatEdit, onCatDelete, onItemEdit, onItemDelete }) => {
  const pg = usePaginator(cats, 10);
  const offset = pg.pageSize === 0 ? 0 : (pg.page - 1) * pg.pageSize;
  if (cats.length === 0) return <p className="empty-text">{emptyText}</p>;
  return (
    <>
      <Paginator total={pg.total} page={pg.page} pageSize={pg.pageSize} onChange={pg.onChange} position="top" />
      <div className="resource-admin-list">
        {pg.paged.map((cat, i) => (
          <div key={cat.id} className="resource-admin-cat">
            <div className="resource-admin-cat-head">
              <div>
                <h4>
                  <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginRight: 8 }}>#{offset + i + 1}</span>
                  {cat.name}{' '}
                  <span className="mono" style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>({(cat.items || []).length})</span>
                </h4>
                {cat.description && <p className="form-hint" style={{ margin: 0 }}>{cat.description}</p>}
              </div>
              <div>
                <button className="btn btn-outline btn-sm" onClick={() => onCatEdit(cat)}>Sửa</button>{' '}
                <button className="btn btn-danger btn-sm" onClick={() => onCatDelete(cat.id)}>Xoá</button>
              </div>
            </div>
            {(cat.items || []).length > 0 && (
              <div className="resource-admin-items">
                {cat.items.map(item => (
                  <div key={item.id} className="resource-admin-item">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong>{item.title}</strong>
                      {item.description && <div className="form-hint">{item.description}</div>}
                      <div className="resource-item-meta">
                        {item.fileName && <span>📦 {item.originalName || item.fileName}</span>}
                        {item.url && <span>🔗 URL</span>}
                        <span>👤 {item.addedByName}</span>
                        <span>🕒 {fmtDMYTime(item.createdAt)}</span>
                      </div>
                      <div className="resource-item-roles">
                        {item.allowedPositions && item.allowedPositions.length > 0 ? (
                          item.allowedPositions.map(p => (
                            <span key={p} className="badge">🔒 {POSITION_LABELS[p] || p}</span>
                          ))
                        ) : (
                          <span className="badge badge-thanh_vien">🔓 Tất cả thành viên</span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button className="btn btn-outline btn-sm" onClick={() => onItemEdit(cat.id, item)}>Sửa</button>
                      <button className="btn btn-danger btn-sm" onClick={() => onItemDelete(cat.id, item.id)}>Xoá</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <Paginator total={pg.total} page={pg.page} pageSize={pg.pageSize} onChange={pg.onChange} position="bottom" />
    </>
  );
};

const NominationUser = ({ status, authHeaders, onRefresh, showMsg }) => {
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  if (!status) return <p className="empty-text">Đang tải…</p>;
  if (!status.isOpen) {
    return (
      <div className="admin-form-card">
        <h3>Ứng Cử</h3>
        <p className="form-hint">Tính năng hiện chưa mở. Hãy quay lại khi Ban Điều Hành / Kỹ Thuật bật chức năng này.</p>
      </div>
    );
  }
  if (status.hasSubmitted) {
    return (
      <div className="admin-form-card">
        <h3>Ứng Cử</h3>
        <p className="form-hint">✅ Bạn đã nộp lựa chọn rồi. Cảm ơn bạn đã tham gia!</p>
      </div>
    );
  }
  const pass = status.passCount || 0;
  const need = Math.max(0, pass - 1);
  const toggle = (id) => {
    setPicked(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= need) return prev;
      return [...prev, id];
    });
  };
  const submit = async () => {
    if (picked.length !== need) return;
    setBusy(true);
    try {
      const res = await fetch('/api/nominations/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ choices: picked })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Lỗi nộp.');
      showMsg('success', 'Đã nộp lựa chọn của bạn.');
      onRefresh();
    } catch (err) { showMsg('error', err.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="admin-form-card">
      <h3>Ứng Cử</h3>
      <p className="form-hint" style={{ lineHeight: 1.6 }}>
        Giả sử bạn là 1 trong <strong>{pass}</strong> thành viên sẽ vượt qua kỳ training, hãy lựa chọn ra <strong>{need}</strong> bạn mà bạn cảm thấy là xứng đáng đồng hành tiếp trên con đường tới mái nhà ISP cùng bạn.
      </p>
      <p className="form-hint">Đã chọn <strong>{picked.length}</strong> / {need}. Bạn chỉ được nộp <strong>một lần duy nhất</strong>.</p>
      {status.candidates.length === 0 && (
        <p className="empty-text">Không có thành viên nào cùng gen để bầu chọn.</p>
      )}
      <div className="nomination-grid">
        {status.candidates.map(c => {
          const on = picked.includes(c.id);
          return (
            <button
              key={c.id}
              type="button"
              className={`nomination-card ${on ? 'is-on' : ''}`}
              onClick={() => toggle(c.id)}
            >
              <div className="nomination-avatar">
                {c.avatar ? (
                  <img src={`/uploads/profiles/${c.avatar}`} alt="" />
                ) : (
                  <span>{(c.fullName || '?').charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div className="nomination-info">
                <div className="nomination-name">{c.fullName}</div>
                <div className="nomination-username">@{c.username}</div>
              </div>
              <div className="nomination-check">{on ? '✓' : ''}</div>
            </button>
          );
        })}
      </div>
      <div className="admin-form-actions">
        <button type="button" className="btn btn-primary" disabled={busy || picked.length !== need} onClick={submit}>
          {busy ? 'Đang nộp…' : `Nộp lựa chọn (${picked.length}/${need})`}
        </button>
      </div>
    </div>
  );
};

const NominationAdmin = ({ data, authHeaders, onRefresh, showMsg }) => {
  const [passInput, setPassInput] = useState('');
  React.useEffect(() => {
    if (data?.passCount != null) setPassInput(String(data.passCount));
  }, [data?.passCount]);
  if (!data) return <p className="empty-text">Đang tải…</p>;
  const toggle = async () => {
    const p = parseInt(passInput, 10);
    if (!p || p < 1) { showMsg('error', 'Số người pass training phải ≥ 1.'); return; }
    try {
      const res = await fetch('/api/nominations/toggle', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ isOpen: !data.isOpen, passCount: p })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      showMsg('success', d.isOpen ? 'Đã bật Ứng Cử.' : 'Đã tắt Ứng Cử.');
      onRefresh();
    } catch (err) { showMsg('error', err.message); }
  };
  const savePass = async () => {
    const p = parseInt(passInput, 10);
    if (!p || p < 1) { showMsg('error', 'Số người pass training phải ≥ 1.'); return; }
    try {
      const res = await fetch('/api/nominations/toggle', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ passCount: p })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      showMsg('success', 'Đã cập nhật số người pass training.');
      onRefresh();
    } catch (err) { showMsg('error', err.message); }
  };
  const removeCandidate = async (gen, id) => {
    if (!window.confirm('Loại thành viên này khỏi biểu đồ bình chọn?')) return;
    try {
      const res = await fetch(`/api/nominations/candidate/${gen}/${id}`, { method: 'DELETE', headers: authHeaders });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      onRefresh();
    } catch (err) { showMsg('error', err.message); }
  };
  const resetAll = async () => {
    if (!window.confirm('Reset TOÀN BỘ cuộc bình chọn (xoá tất cả submissions & danh sách loại trừ)?')) return;
    try {
      const res = await fetch('/api/nominations/reset', { method: 'DELETE', headers: authHeaders });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      showMsg('success', 'Đã reset cuộc bình chọn.');
      onRefresh();
    } catch (err) { showMsg('error', err.message); }
  };
  const resetGen = async (gen) => {
    if (!window.confirm(`Reset cuộc bình chọn Gen ${gen}?`)) return;
    try {
      const res = await fetch(`/api/nominations/reset?gen=${encodeURIComponent(gen)}`, { method: 'DELETE', headers: authHeaders });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      onRefresh();
    } catch (err) { showMsg('error', err.message); }
  };
  const gens = Object.keys(data.results || {}).sort((a, b) => Number(b) - Number(a));
  return (
    <div>
      <div className="admin-form-card">
        <h3>Ứng Cử</h3>
        <div className="admin-form-grid">
          <div className="form-group">
            <label>Trạng thái</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span className={data.isOpen ? 'twofa-on' : 'twofa-off'}>{data.isOpen ? 'Đang mở' : 'Đã tắt'}</span>
              <button type="button" className={`btn ${data.isOpen ? 'btn-danger' : 'btn-primary'} btn-sm`} onClick={toggle}>
                {data.isOpen ? 'Tắt Ứng Cử' : 'Bật Ứng Cử'}
              </button>
            </div>
          </div>
          <div className="form-group">
            <label>Số người pass training (n)</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input type="number" min="1" value={passInput} onChange={e => setPassInput(e.target.value)} style={{ maxWidth: 120 }} />
              <button type="button" className="btn btn-outline btn-sm" onClick={savePass}>Lưu</button>
            </div>
            <p className="form-hint" style={{ marginTop: 4 }}>Mỗi mầm non sẽ chọn <strong>{Math.max(0, (parseInt(passInput, 10) || 0) - 1)}</strong> bạn đồng hành.</p>
          </div>
          <div className="form-group">
            <label>Hành động chung</label>
            <button type="button" className="btn btn-danger btn-sm" onClick={resetAll}>Reset toàn bộ</button>
          </div>
        </div>
      </div>

      {gens.length === 0 && <p className="empty-text">Chưa có mầm non nào để bình chọn.</p>}
      {gens.map(gen => {
        const g = data.results[gen];
        return (
          <div key={gen} className="admin-form-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <h3 style={{ margin: 0 }}>Gen {gen}</h3>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span className="form-hint">Đã nộp: <strong>{g.totalVoters}</strong> / {g.totalMembers}</span>
                <button type="button" className="btn btn-danger btn-sm" onClick={() => resetGen(gen)}>Reset Gen</button>
              </div>
            </div>
            <div className="nomination-chart">
              {g.tally.length === 0 && <p className="form-hint">Không có dữ liệu.</p>}
              {g.tally.map(t => (
                <div key={t.id} className="nomination-bar-row">
                  <div className="nomination-bar-info">
                    <strong>{t.fullName}</strong>
                    <span className="form-hint"> @{t.username} — {t.votes} phiếu</span>
                  </div>
                  <div className="nomination-bar-track">
                    <div className="nomination-bar-fill" style={{ width: `${t.percent}%` }} />
                    <span className="nomination-bar-pct">{t.percent}%</span>
                  </div>
                  <button type="button" className="btn btn-danger btn-sm" onClick={() => removeCandidate(gen, t.id)}>Loại</button>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Dashboard = ({ lang, adminMode = false, initialTab = null, initialTrainingMode = null }) => {
  const navigate = useNavigate();
  const bbv = useBottomBoundedViewport({ topOffset: 90, bottomGap: 16 });
  // eslint-disable-next-line no-unused-vars
  const { user, token, isKyThuat, hasRole, logout, updateProfile, changePassword, refreshUser } = useAuth();
  const isMamNon = user?.position === 'mam_non' && !isKyThuat;
  const canEditProfile = !isMamNon;

  // ===== ADMIN PERMISSION MATRIX =====
  // ky_thuat                              -> ALL admin features
  // ban_dieu_hanh + culi_cap_cao          -> members, achievements, forum_admin, blogs, resources_admin, events_admin, recruitment
  // ban_su_kien   + (tap_su|culi_cap_cao) -> members, achievements, events_admin, blogs, recruitment
  // ban_dao_tao                            -> members, recruitment
  const userRoles = Array.isArray(user?.role) ? user.role : (user?.role ? [user.role] : []);
  const userPos = user?.position;
  const has = (r) => userRoles.includes(r);
  const isBanDieuHanhFull = has('ban_dieu_hanh') && userPos === 'culi_cap_cao';
  const isBanSuKienActive = has('ban_su_kien') && (userPos === 'tap_su' || userPos === 'culi_cap_cao');
  const isBanDaoTao = has('ban_dao_tao');

  const can = {
    // Member management: only ky_thuat OR ban_dieu_hanh (culi_cap_cao).
    members: isKyThuat || isBanDieuHanhFull,
    achievements: isKyThuat || isBanDieuHanhFull || isBanSuKienActive,
    forumAdmin: isKyThuat || isBanDieuHanhFull,
    blogs: isKyThuat || isBanDieuHanhFull || isBanSuKienActive,
    resourcesAdmin: isKyThuat || isBanDieuHanhFull,
    eventsAdmin: isKyThuat || isBanDieuHanhFull || isBanSuKienActive,
    // Recruitment viewers match the server policy; campaign/form changes remain
    // restricted to ky_thuat and full ban_dieu_hanh.
    recruitment: isKyThuat || isBanDieuHanhFull || isBanSuKienActive || isBanDaoTao,
    recruitmentCanToggle: isKyThuat || isBanDieuHanhFull,
    smtp: isKyThuat,
    // Training admin (Online Judge + CTF) shares one tab; CTF sub-mode adds ban_dieu_hanh.
    trainingAdmin: isKyThuat || isBanDaoTao,
    // CTF management (sub-mode of Training): ky_thuat / ban_dao_tao / ban_dieu_hanh.
    ctfAdmin: isKyThuat || isBanDaoTao || has('ban_dieu_hanh'),
    contributors: isKyThuat,
    screening: isKyThuat || has('ban_su_kien'),
    mailCampaign: isKyThuat || has('ban_su_kien'),
    aboutGallery: isKyThuat || has('ban_su_kien') || has('ban_dieu_hanh'),
    eventCalendar: isKyThuat || has('ban_dieu_hanh') || has('ban_dao_tao') || has('ban_su_kien'),
    nominationAdmin: isKyThuat || isBanDieuHanhFull,
  };
  const hasAnyAdminPower = Object.values(can).some(Boolean);

  // ===== FORUM RENDER HELPERS =====
  // Pick a deterministic, friendly avatar for an Anonymous#N (color-coded silhouette).
  const ANON_PALETTE = ['#5eb4ff', '#ff7eb6', '#a78bfa', '#facc15', '#34d399', '#fb923c', '#f472b6', '#22d3ee'];
  const renderForumAvatar = (entry) => {
    if (entry.anonymous) {
      const idx = ((entry.anonIndex || 1) - 1) % ANON_PALETTE.length;
      const color = ANON_PALETTE[idx];
      return (
        <div className="forum-avatar forum-avatar-anon" style={{ background: color }}>
          <span aria-hidden="true">🕵</span>
          <small>#{entry.anonIndex || '?'}</small>
        </div>
      );
    }
    const snap = entry.authorSnapshot || {};
    if (snap.avatar) {
      return (
        <div className="forum-avatar">
          <img src={`/uploads/profiles/${snap.avatar}`} alt={snap.fullName || 'avatar'} />
        </div>
      );
    }
    const initials = (snap.fullName || snap.username || '?').trim().split(/\s+/).slice(-2).map(s => s[0] || '').join('').toUpperCase();
    return (
      <div className="forum-avatar forum-avatar-initials">{initials || '?'}</div>
    );
  };
  const renderForumMeta = (entry) => {
    if (entry.anonymous) {
      return (
        <div className="forum-meta">
          <strong className="forum-meta-name">Anonymous#{entry.anonIndex || '?'}</strong>
          <div className="forum-meta-line">
            <span className="forum-meta-pill anon">Ẩn danh</span>
          </div>
        </div>
      );
    }
    const snap = entry.authorSnapshot || {};
    const roles = Array.isArray(snap.roles) ? snap.roles : [];
    const roleLabels = roles.map(r => ROLE_LABELS[r] || r);
    const titleLabel = snap.title ? (TITLE_LABELS[snap.title] || snap.title) : '';
    const posLabel = snap.position ? (POSITION_LABELS[snap.position] || snap.position) : '';
    return (
      <div className="forum-meta">
        <strong className="forum-meta-name" title={`@${snap.username}`}>{snap.fullName || snap.username || 'Member'}</strong>
        <div className="forum-meta-line">
          {snap.gen && <span className="forum-meta-pill">Gen {snap.gen}</span>}
          {posLabel && <span className="forum-meta-pill">{posLabel}</span>}
          {titleLabel && <span className="forum-meta-pill highlight">{titleLabel}</span>}
        </div>
        {roleLabels.length > 0 && (
          <div className="forum-meta-line">
            {roleLabels.map((rl, i) => (
              <span key={i} className="forum-meta-pill role">{rl}</span>
            ))}
          </div>
        )}
      </div>
    );
  };
  const formatForumDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'vừa xong';
    if (diff < 3600) return `${Math.floor(diff / 60)} phút trước`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} giờ trước`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} ngày trước`;
    return fmtDMYTime(d);
  };
  const [activeTab, setActiveTab] = useState(initialTab || 'overview');
  const [homeGroupOpen, setHomeGroupOpen] = useState(HOME_TAB_KEYS.has(initialTab));
  const [trainingGroupOpen, setTrainingGroupOpen] = useState(TRAINING_TAB_KEYS.has(initialTab));
  const [recruitmentGroupOpen, setRecruitmentGroupOpen] = useState(RECRUITMENT_TAB_KEYS.has(initialTab));
  // Training tab has two sub-modes (categories): Online Judge or CTF.
  const [trainingMode, setTrainingMode] = useState(initialTrainingMode === 'ctf' ? 'ctf' : 'oj'); // 'oj' | 'ctf'
  const [profileData, setProfileData] = useState({
    fullName: user?.fullName || '', bio: user?.bio || '', major: user?.major || '',
    phone: user?.phone || '', ctfField: user?.ctfField || '',
    website: user?.website || '', profileTheme: user?.profileTheme || '',
    profileEffect: user?.profileEffect || '', profileCursor: user?.profileCursor || '',
    websites: Array.isArray(user?.websites) ? user.websites : []
  });
  // Music upload
  const musicInputRef = useRef(null);
  const [musicTitle, setMusicTitle] = useState('');
  // Pending (not-yet-uploaded) music files: [{id, file, title, progress (0-100 | 'done' | 'error' | 'uploading'), error}]
  const [pendingMusic, setPendingMusic] = useState([]);
  const pendingMusicRef = useRef([]);
  // Keep ref in sync so async handlers (setTimeout, XHR callbacks) always
  // read the latest queue instead of a stale closure capture.
  useEffect(() => { pendingMusicRef.current = pendingMusic; }, [pendingMusic]);
  const [savingMusic, setSavingMusic] = useState(false);
  const savingMusicRef = useRef(false);
  // Music settings (autoPlay, loopMode)
  const [musicSettings, setMusicSettings] = useState({
    autoPlay: user?.musicSettings?.autoPlay ?? false,
    loopMode: user?.musicSettings?.loopMode ?? 'none'
  });
  // Avoid PUT-ing music settings on the very first hydration from the user object.
  const musicSettingsHydrated = useRef(false);
  // Avatar crop modal: holds the picked File until user confirms / cancels.
  const [avatarCropFile, setAvatarCropFile] = useState(null);
  // Custom cursor
  const cursorInputRef = useRef(null);
  // cursor hotspot removed — only `cursorSize` is configurable.
  // Personal blog
  const [personalPosts, setPersonalPosts] = useState([]);
  const [personalPostForm, setPersonalPostForm] = useState({ title: '', excerpt: '', content: '', tagsStr: '' });
  const [personalPostEditing, setPersonalPostEditing] = useState(null);
  // Personal achievements (own profile)
  const personalAchievementImagesRef = useRef(null);
  const [personalAchievements, setPersonalAchievements] = useState([]);
  const [personalAchievementForm, setPersonalAchievementForm] = useState({ title: '', description: '', date: '' });
  const [personalAchievementEditing, setPersonalAchievementEditing] = useState(null);
  const [personalAchievementRemoveImages, setPersonalAchievementRemoveImages] = useState([]);
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [emailChangeData, setEmailChangeData] = useState({ newEmail: user?.email || '', currentPassword: '' });
  const [usernameChangeData, setUsernameChangeData] = useState({ newUsername: user?.username || '', currentPassword: '' });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [toast, setToast] = useState({ visible: false, type: 'success', text: '' });
  const toastTimerRef = useRef(null);

  // Top-down popup toast (auto-dismiss). Use this for important actions like Save.
  const showToast = (type, text, duration = 3500) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ visible: true, type, text });
    toastTimerRef.current = setTimeout(() => {
      setToast(t => ({ ...t, visible: false }));
    }, duration);
  };

  // Photo upload state
  const photoInputRef = useRef(null);
  const [userPhotos, setUserPhotos] = useState(user?.photos || []);

  // Avatar state
  const avatarInputRef = useRef(null);
  const [userAvatar, setUserAvatar] = useState(user?.avatar || '');

  // Member management state
  const [members, setMembers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberFilterBan, setMemberFilterBan] = useState('');
  const [memberFilterPosition, setMemberFilterPosition] = useState('');
  const [memberFilterGen, setMemberFilterGen] = useState('');
  const [nominationStatus, setNominationStatus] = useState(null);
  const [nominationAdminData, setNominationAdminData] = useState(null);
  const showNominationTab = !adminMode && user?.position === 'mam_non' && nominationStatus?.isOpen && !nominationStatus?.hasSubmitted;
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [editingMember, setEditingMember] = useState(null); // member object being edited
  const [newMember, setNewMember] = useState({
    fullName: '', username: '', phone: '', email: '',
    studentClass: '', studentId: '', gen: '',
    password: '', role: ['thanh_vien'], position: 'mam_non', title: '',
    ctfField: '', major: '', bio: '', joinDate: ''
  });
  const [bulkPassword, setBulkPassword] = useState('Isp@2024');
  const [bulkResult, setBulkResult] = useState(null);
  const fileInputRef = useRef(null);

  // Bulk edit state
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkEditData, setBulkEditData] = useState({ gen: '', position: '', password: '', joinDate: '', addBans: [], removeBans: [] });

  // Achievements state
  const [achievements, setAchievements] = useState([]);
  const [achievementForm, setAchievementForm] = useState({ title: '', description: '', date: '', tier: 'notable', rank: '', prize: '' });
  const [editingAchievement, setEditingAchievement] = useState(null);
  const achievementImagesRef = useRef(null);

  // Blog state
  const [blogs, setBlogs] = useState([]);
  const [blogEditing, setBlogEditing] = useState(null);
  const [blogForm, setBlogForm] = useState({ title: '', content: '', tags: '' });
  const [showBlogForm, setShowBlogForm] = useState(false);
  // Toggles for "treat-add-as-feature" sections — clicking the add button
  // hides the underlying list and only the form is shown, mirroring the
  // edit-mode behaviour. Each section uses its own boolean.
  const [showAchievementForm, setShowAchievementForm] = useState(false);
  const [showResourceItemForm, setShowResourceItemForm] = useState(false);
  const [showResourceCatForm, setShowResourceCatForm] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [showForumTopicForm, setShowForumTopicForm] = useState(false);
  const [showTrainingModuleForm, setShowTrainingModuleForm] = useState(false);

  // Recruitment state
  const [recruitmentOpen, setRecruitmentOpen] = useState(false);
  const [recruitQuestions, setRecruitQuestions] = useState([]);
  const [dragQuestionIndex, setDragQuestionIndex] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmissionIds, setSelectedSubmissionIds] = useState([]);
  const [recruitmentCampaigns, setRecruitmentCampaigns] = useState([]);
  const [recruitmentTemplates, setRecruitmentTemplates] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [recruitmentStatuses, setRecruitmentStatuses] = useState({});
  const [recruitmentSearch, setRecruitmentSearch] = useState('');
  const [recruitmentStatusFilter, setRecruitmentStatusFilter] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [campaignForm, setCampaignForm] = useState({ name: '', gen: '', templateId: '' });
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [editingTemplateId, setEditingTemplateId] = useState('');
  const [editingTemplateName, setEditingTemplateName] = useState('');
  const [recruitmentEmailTemplate, setRecruitmentEmailTemplate] = useState(() => {
    try { return JSON.parse(localStorage.getItem('recruitmentEmailTemplate')) || { subject: '[ISP Club] Kết quả {status}', html: '<p>Xin chào <strong>{fullName}</strong>,</p><p>Kết quả của bạn: <strong>{status}</strong>.</p><p>Trân trọng,<br>ISP Club.</p>', senderName: '', senderEmail: '', extras: [] }; }
    catch (_) { return { subject: '[ISP Club] Kết quả {status}', html: '<p>Xin chào <strong>{fullName}</strong>,</p><p>Kết quả của bạn: <strong>{status}</strong>.</p><p>Trân trọng,<br>ISP Club.</p>', senderName: '', senderEmail: '', extras: [] }; }
  });
  const [recruitmentEmailPreview, setRecruitmentEmailPreview] = useState(false);
  const [recruitmentTestEmail, setRecruitmentTestEmail] = useState('');
  const [recruitmentTestSending, setRecruitmentTestSending] = useState(false);
  const [recruitmentTestResult, setRecruitmentTestResult] = useState({ type: '', text: '' });
  const [recruitmentEmailSearch, setRecruitmentEmailSearch] = useState('');
  const [recruitmentEmailSelectedIds, setRecruitmentEmailSelectedIds] = useState([]);
  const [recruitmentEmailImages, setRecruitmentEmailImages] = useState([]);
  const [recruitmentEmailImageUploading, setRecruitmentEmailImageUploading] = useState(false);
  const [recruitmentBulkSending, setRecruitmentBulkSending] = useState(false);
  const [recruitmentBulkResult, setRecruitmentBulkResult] = useState({ type: '', text: '' });
  const [recruitmentNotify, setRecruitmentNotify] = useState(null);

  useEffect(() => {
    localStorage.setItem('recruitmentEmailTemplate', JSON.stringify(recruitmentEmailTemplate));
  }, [recruitmentEmailTemplate]);

  // SMTP state
  const [smtpConfig, setSmtpConfig] = useState({
    host: '', port: 587, secure: false, user: '', password: '',
    fromName: 'ISP Club', fromEmail: ''
  });
  const [smtpTestTo, setSmtpTestTo] = useState('');
  const [smtpTesting, setSmtpTesting] = useState(false);

  // Forum (Q&A) state
  const isPrivileged = can.forumAdmin;
  const [forumTopics, setForumTopics] = useState([]);
  const [forumSelectedTopic, setForumSelectedTopic] = useState(null);
  const [forumPosts, setForumPosts] = useState([]);
  const [forumViewMode, setForumViewMode] = useState('public'); // 'public' | 'anonymous'
  const [forumShowComposer, setForumShowComposer] = useState(false);
  const [forumNewPost, setForumNewPost] = useState({ title: '', content: '', anonymous: false, images: [], imageFiles: [] });
  const [forumThread, setForumThread] = useState(null); // { post, replies }
  const [forumNewReply, setForumNewReply] = useState({ content: '', anonymous: false, imageFiles: [] });
  const [forumLoading, setForumLoading] = useState(false);
  // Admin-only
  const [forumSelectedPost, setForumSelectedPost] = useState(null); // for admin log view
  const [forumAdminThread, setForumAdminThread] = useState(null); // { post, replies } — full privileged view
  // Lock background scroll whenever any floating viewer is open so the user
  // can only scroll the modal content, never the page underneath.
  useBodyScrollLock(!!(forumSelectedPost || forumAdminThread || selectedSubmission || recruitmentNotify));
  useEffect(() => {
    if (!selectedSubmission) return undefined;
    const closeOnEscape = e => { if (e.key === 'Escape') setSelectedSubmission(null); };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [selectedSubmission]);
  const [forumAdminTopicForm, setForumAdminTopicForm] = useState({ name: '', description: '' });
  const [forumAdminEditingTopic, setForumAdminEditingTopic] = useState(null);
  const [forumAdminLogs, setForumAdminLogs] = useState([]);
  const [forumAdminFilter, setForumAdminFilter] = useState({ topicId: '', mode: 'all' });

  // Resources (learning hub) state
  const [resources, setResources] = useState({ document: [], video: [], tool: [] });
  const [resourceActiveType, setResourceActiveType] = useState('document');
  const [resourceSelectedCat, setResourceSelectedCat] = useState(null);
  // Admin panels
  const [resourceAdminType, setResourceAdminType] = useState('document');
  const [resourceCatForm, setResourceCatForm] = useState({ name: '', description: '' });
  const [resourceCatEditing, setResourceCatEditing] = useState(null);
  const [resourceItemForm, setResourceItemForm] = useState({ title: '', description: '', url: '', categoryId: '', allowedPositions: [] });
  const [resourceItemEditing, setResourceItemEditing] = useState(null);
  const resourceFileRef = useRef(null);

  // User upload (non-admin) state for Kho Tài Liệu
  const resourceUserFileRef = useRef(null);
  const [showResourceUserUpload, setShowResourceUserUpload] = useState(false);
  const [resourceUserForm, setResourceUserForm] = useState({ title: '', description: '', url: '', categoryId: '' });
  const [resourceUploadProgress, setResourceUploadProgress] = useState(null); // null = no upload, 0-100 = progress
  const [resourceAdminUploadProgress, setResourceAdminUploadProgress] = useState(null);

  // Events (ky_thuat manage)
  const [events, setEvents] = useState([]);

  // Pagination state for every admin list. Default page size 10.
  // Centralizing them here avoids a forest of inline useState pairs.
  const filteredMembers = React.useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    const normalizeGen = value => String(value || '').trim().replace(/^gen\s*/i, '').toLowerCase();
    return members.filter(m => {
      if (memberFilterBan) {
        const roles = Array.isArray(m.role) ? m.role : (m.role ? [m.role] : []);
        if (!roles.includes(memberFilterBan)) return false;
      }
      if (memberFilterPosition && m.position !== memberFilterPosition) return false;
      if (memberFilterGen && normalizeGen(m.gen) !== memberFilterGen) return false;
      if (!q) return true;
      const fields = [m.fullName, m.username, m.email, m.phone, m.studentClass, m.studentId];
      return fields.some(f => String(f || '').toLowerCase().includes(q));
    });
  }, [members, memberSearch, memberFilterBan, memberFilterPosition, memberFilterGen]);
  const memberGenOptions = React.useMemo(() => {
    const values = new Set();
    members.forEach(m => {
      const value = String(m.gen || '').trim().replace(/^gen\s*/i, '').toLowerCase();
      if (value) values.add(value);
    });
    return [...values].sort((a, b) => a.localeCompare(b, 'vi', { numeric: true }));
  }, [members]);
  const membersPg = usePaginator(filteredMembers, 10);
  const achievementsPg = usePaginator(achievements, 10);
  const blogsPg = usePaginator(blogs, 10);
  const eventsPg = usePaginator(events, 10);
  const filteredSubmissions = React.useMemo(() => {
    const q = recruitmentSearch.trim().toLowerCase();
    return submissions.filter(s => {
      if (recruitmentStatusFilter && (s.status || 'submitted') !== recruitmentStatusFilter) return false;
      return !q || [s.fullName, s.email].some(v => String(v || '').toLowerCase().includes(q));
    });
  }, [submissions, recruitmentSearch, recruitmentStatusFilter]);
  const recruitmentEmailRecipients = React.useMemo(() => {
    const q = recruitmentEmailSearch.trim().toLowerCase();
    return submissions.filter(sub => {
      if (!['cv_pass', 'cv_fail', 'interview_pass', 'interview_fail'].includes(sub.status)) return false;
      if (recruitmentStatusFilter && sub.status !== recruitmentStatusFilter) return false;
      return !q || [sub.fullName, sub.email].some(value => String(value || '').toLowerCase().includes(q));
    });
  }, [submissions, recruitmentStatusFilter, recruitmentEmailSearch]);
  const submissionsPg = usePaginator(filteredSubmissions, 10);
  const forumTopicsPg = usePaginator(forumTopics, 10);
  const forumLogsPg = usePaginator(forumAdminLogs, 10);
  const [eventForm, setEventForm] = useState({
    title: '', excerpt: '', content: '', eventDate: '', location: '', status: 'upcoming'
  });
  const [eventEditing, setEventEditing] = useState(null);
  const [eventCoverPreview, setEventCoverPreview] = useState('');
  const [eventRemoveCover, setEventRemoveCover] = useState(false);
  const [eventPreviewMode, setEventPreviewMode] = useState(false);
  const eventCoverRef = useRef(null);
  const eventInlineRef = useRef(null);
  const eventContentRef = useRef(null);

  // Load members for admin (any role allowed to manage members, not just ky_thuat)
  useEffect(() => {
    if (can.members && activeTab === 'members') {
      fetchMembers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [can.members, activeTab]);

  useEffect(() => {
    if (!user) return;
    if (adminMode) return;
    if (user.position !== 'mam_non') return;
    fetch('/api/nominations/status', { headers: authHeaders })
      .then(r => r.json()).then(d => setNominationStatus(d)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, adminMode, activeTab]);

  useEffect(() => {
    if (!adminMode || !can.nominationAdmin) return;
    if (activeTab !== 'nomination_admin') return;
    fetch('/api/nominations/admin', { headers: authHeaders })
      .then(r => r.json()).then(d => setNominationAdminData(d)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, adminMode]);

  // Load achievements
  useEffect(() => {
    if (can.achievements && activeTab === 'achievements') {
      fetchAchievements();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [can.achievements, activeTab]);

  // Load blogs
  useEffect(() => {
    if (activeTab === 'blogs') {
      fetchBlogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Load recruitment data (all four management roles, so status stays synced)
  useEffect(() => {
    if (can.recruitment && RECRUITMENT_TAB_KEYS.has(activeTab)) {
      fetchRecruitment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [can.recruitment, activeTab]);

  // Load SMTP config
  useEffect(() => {
    if (isKyThuat && activeTab === 'smtp') {
      fetchSmtp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isKyThuat, activeTab]);

  // Load forum topics when entering forum-related tabs
  useEffect(() => {
    if (activeTab === 'forum' || activeTab === 'forum_admin') {
      fetchForumTopics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Load posts when topic changes
  useEffect(() => {
    if (activeTab === 'forum' && forumSelectedTopic) {
      fetchForumPosts(forumSelectedTopic.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, forumSelectedTopic]);

  // Load admin logs when filters change
  useEffect(() => {
    if (activeTab === 'forum_admin' && isPrivileged) {
      fetchForumAdminLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, forumAdminFilter, isPrivileged]);

  // Load resources
  useEffect(() => {
    if (activeTab === 'resources') {
      fetchResources(false);
    } else if (activeTab === 'resources_admin') {
      fetchResources(true);
    }
    if (activeTab === 'events_admin' && can.eventsAdmin) {
      fetchEvents();
    }
    if (activeTab === 'profile') {
      fetchPersonalPosts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Sync profileData when user object changes
  useEffect(() => {
    if (user) {
      setProfileData(prev => ({
        ...prev,
        fullName: user.fullName || prev.fullName,
        bio: user.bio ?? prev.bio,
        major: user.major ?? prev.major,
        phone: user.phone ?? prev.phone,
        ctfField: user.ctfField ?? prev.ctfField,
        website: user.website ?? prev.website,
        websites: Array.isArray(user.websites) ? user.websites : prev.websites,
        profileTheme: user.profileTheme ?? prev.profileTheme,
        profileEffect: user.profileEffect ?? prev.profileEffect,
        profileCursor: user.profileCursor ?? prev.profileCursor
      }));
      if (user.musicSettings) {
        setMusicSettings({
          autoPlay: !!user.musicSettings.autoPlay,
          loopMode: user.musicSettings.loopMode || 'none'
        });
        musicSettingsHydrated.current = true;
      }
      if (Array.isArray(user.personalAchievements)) {
        setPersonalAchievements(user.personalAchievements);
      }
      if (user.customCursor) {
        setCursorSize(user.customCursor.cursorSize || 32);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const authHeaders = { Authorization: 'Bearer ' + token };

  // === FETCH FUNCTIONS ===

  const fetchMembers = async () => {
    try {
      const res = await fetch('/api/admin/members', { headers: authHeaders });
      const data = await res.json();
      if (res.ok) setMembers(data.members);
    } catch (err) { console.error(err); }
  };

  const fetchAchievements = async () => {
    try {
      const res = await fetch('/api/achievements', { headers: authHeaders });
      const data = await res.json();
      if (res.ok) setAchievements(data.achievements || data);
    } catch (err) { console.error(err); }
  };

  const fetchBlogs = async () => {
    try {
      const res = await fetch('/api/blogs', { headers: authHeaders });
      const data = await res.json();
      if (res.ok) setBlogs(data.blogs || data);
    } catch (err) { console.error(err); }
  };

  const fetchRecruitment = async (campaignId = selectedCampaignId) => {
    try {
      const query = campaignId ? `?campaignId=${encodeURIComponent(campaignId)}` : '';
      const res = await fetch(`/api/recruitment/admin${query}`, { headers: authHeaders });
      const data = await res.json();
      if (res.ok) {
        setRecruitmentCampaigns(data.campaigns || []);
        setRecruitmentTemplates(data.templates || []);
        setRecruitmentStatuses(data.statuses || {});
        const currentId = data.selectedCampaign?.id || data.activeCampaignId || '';
        setSelectedCampaignId(currentId);
        setRecruitmentOpen(!!data.isOpen);
        const rawQs = data.form?.questions || data.questions || [];
        // Normalize loaded questions: map server `question` field to local `text`
        setRecruitQuestions(rawQs.map((q, i) => ({
          id: q.id || `q_${Date.now()}_${i}`,
          text: q.text || q.question || '',
          question: q.question || q.text || '',
          type: q.type || 'text',
          required: !!q.required,
          options: Array.isArray(q.options) ? q.options : []
        })));
        setSubmissions(data.submissions || []);
      }
    } catch (err) { console.error(err); }
  };

  // === HANDLERS ===

  const handleProfileChange = (e) => setProfileData({ ...profileData, [e.target.name]: e.target.value });
  const handlePasswordChange = (e) => setPasswordData({ ...passwordData, [e.target.name]: e.target.value });

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    try {
      await updateProfile(profileData);
      showToast('success', '✅ ' + (lang.DASHBOARD_PROFILE_SUCCESS || 'Đã lưu thay đổi hồ sơ thành công!'));
    } catch (err) {
      showToast('error', '❌ ' + err.message);
    }
  };

  // === MUSIC HANDLERS ===
  // refreshUser is now provided by AuthContext (no full page reload).

  // === MUSIC PLAYLIST HANDLERS ===
  // Add a file to local pending queue (NOT uploaded yet — uploaded on Save)
  const handleMusicQueue = () => {
    const files = Array.from(musicInputRef.current?.files || []);
    if (!files.length) return showMsg('error', 'Vui lòng chọn ít nhất một file nhạc.');
    const items = [];
    for (const f of files) {
      if (f.size > 200 * 1024 * 1024) {
        showMsg('error', `File ${f.name} vượt quá 200MB.`);
        continue;
      }
      const okType = /^audio\//.test(f.type) || /\.(mp3|wav|ogg|m4a|flac|aac)$/i.test(f.name);
      if (!okType) { showMsg('error', `File ${f.name} không phải audio.`); continue; }
      items.push({
        id: Date.now() + Math.random(),
        file: f,
        title: musicTitle.trim() || f.name.replace(/\.[^.]+$/, ''),
        progress: 0,
        status: 'queued'
      });
    }
    if (!items.length) return;
    setPendingMusic(prev => [...prev, ...items]);
    setMusicTitle('');
    if (musicInputRef.current) musicInputRef.current.value = '';
    showMsg('success', `Đã thêm ${items.length} bài vào hàng đợi, đang tự động upload...`);
    // Auto-kick upload (non-blocking). handleMusicSaveAll guards against concurrent runs.
    setTimeout(() => handleMusicSaveAll(), 50);
  };

  const handleMusicQueueRemove = (id) => {
    setPendingMusic(prev => prev.filter(it => it.id !== id));
  };

  // Auto-save music settings (debounced) — replaces the old "Lưu thay đổi nhạc" button.
  useEffect(() => {
    if (!musicSettingsHydrated.current) return;
    const t = setTimeout(() => {
      fetch('/api/auth/music-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(musicSettings)
      }).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [musicSettings.autoPlay, musicSettings.loopMode]);

  // Upload one pending file with XHR for progress tracking
  const uploadOnePending = (item) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const fd = new FormData();
      fd.append('music', item.file);
      if (item.title) fd.append('title', item.title);

      xhr.upload.addEventListener('progress', (ev) => {
        if (ev.lengthComputable) {
          const pct = Math.round((ev.loaded / ev.total) * 100);
          setPendingMusic(prev => prev.map(it => it.id === item.id ? { ...it, progress: pct, status: 'uploading' } : it));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setPendingMusic(prev => prev.map(it => it.id === item.id ? { ...it, progress: 100, status: 'done' } : it));
          resolve();
        } else {
          let errMsg = 'Upload failed';
          try { errMsg = JSON.parse(xhr.responseText).error || errMsg; } catch (_) {}
          setPendingMusic(prev => prev.map(it => it.id === item.id ? { ...it, status: 'error', error: errMsg } : it));
          reject(new Error(errMsg));
        }
      });
      xhr.addEventListener('error', () => {
        setPendingMusic(prev => prev.map(it => it.id === item.id ? { ...it, status: 'error', error: 'Network error' } : it));
        reject(new Error('Network error'));
      });

      xhr.open('POST', '/api/auth/music');
      xhr.setRequestHeader('Authorization', authHeaders.Authorization);
      xhr.send(fd);
    });
  };

  // Save music changes: upload all pending, save settings
  const handleMusicSaveAll = async () => {
    if (savingMusicRef.current) return;
    savingMusicRef.current = true;
    setSavingMusic(true);
    let anyError = false;
    try {
      // Read from ref to avoid stale closure when called via setTimeout.
      const queue = pendingMusicRef.current;
      for (const item of queue) {
        if (item.status === 'done') continue;
        try {
          await uploadOnePending(item);
        } catch (err) {
          anyError = true;
        }
      }
      if (anyError) {
        showMsg('error', 'Một số bài upload thất bại. Vui lòng kiểm tra danh sách bên dưới.');
      }
      // Clear successfully uploaded items from pending after a delay
      setTimeout(() => {
        setPendingMusic(prev => prev.filter(it => it.status !== 'done'));
        refreshUser();
      }, 600);
    } finally {
      savingMusicRef.current = false;
      setSavingMusic(false);
    }
  };

  const handleMusicTrackRename = async (track) => {
    const current = track.title || track.originalName || '';
    const newTitle = window.prompt(lang.DASHBOARD_MUSIC_RENAME_PROMPT || 'Tên mới cho bài nhạc:', current);
    if (newTitle === null) return;
    const trimmed = String(newTitle).trim();
    if (!trimmed) return showMsg('error', 'Tên bài không được để trống.');
    if (trimmed === current) return;
    try {
      const res = await fetch(`/api/auth/music/${track.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ title: trimmed })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Rename failed');
      showMsg('success', 'Đã đổi tên.');
      await refreshUser();
    } catch (err) { showMsg('error', err.message); }
  };

  const handleMusicTrackDelete = async (track) => {
    if (!window.confirm(lang.DASHBOARD_MUSIC_DELETE_CONFIRM || `Xoá bài "${track.title}"?`)) return;
    try {
      const res = await fetch(`/api/auth/music/${track.id}`, { method: 'DELETE', headers: authHeaders });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      showMsg('success', 'Đã xoá bài.');
      refreshUser();
    } catch (err) { showMsg('error', err.message); }
  };

  const handleMusicClearAll = async () => {
    if (!window.confirm(lang.DASHBOARD_MUSIC_DELETE_ALL_CONFIRM || 'Xoá TẤT CẢ bài nhạc?')) return;
    try {
      const res = await fetch('/api/auth/music', { method: 'DELETE', headers: authHeaders });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      showMsg('success', 'Đã xoá toàn bộ playlist.');
      refreshUser();
    } catch (err) { showMsg('error', err.message); }
  };

  // === PERSONAL ACHIEVEMENTS HANDLERS ===
  const resetPersonalAchievementForm = () => {
    setPersonalAchievementForm({ title: '', description: '', date: '' });
    setPersonalAchievementEditing(null);
    setPersonalAchievementRemoveImages([]);
    if (personalAchievementImagesRef.current) personalAchievementImagesRef.current.value = '';
  };

  const handlePersonalAchievementSubmit = async (e) => {
    e.preventDefault();
    if (!personalAchievementForm.title.trim()) return showMsg('error', 'Tiêu đề thành tựu là bắt buộc.');
    try {
      const fd = new FormData();
      fd.append('title', personalAchievementForm.title.trim());
      fd.append('description', personalAchievementForm.description.trim());
      fd.append('date', personalAchievementForm.date);
      const files = Array.from(personalAchievementImagesRef.current?.files || []);
      files.forEach(f => fd.append('images', f));
      if (personalAchievementEditing && personalAchievementRemoveImages.length) {
        fd.append('removeImages', JSON.stringify(personalAchievementRemoveImages));
      }
      const url = personalAchievementEditing
        ? `/api/auth/personal-achievements/${personalAchievementEditing}`
        : '/api/auth/personal-achievements';
      const method = personalAchievementEditing ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: authHeaders, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      showMsg('success', personalAchievementEditing ? 'Đã cập nhật thành tựu.' : 'Đã thêm thành tựu.');
      resetPersonalAchievementForm();
      refreshUser();
    } catch (err) { showMsg('error', err.message); }
  };

  const handlePersonalAchievementEdit = (item) => {
    setPersonalAchievementEditing(item.id);
    setPersonalAchievementForm({
      title: item.title || '',
      description: item.description || '',
      date: item.date ? item.date.substring(0, 10) : ''
    });
    setPersonalAchievementRemoveImages([]);
    if (personalAchievementImagesRef.current) personalAchievementImagesRef.current.value = '';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePersonalAchievementDelete = async (id) => {
    if (!window.confirm(lang.DASHBOARD_ACHIEVEMENT_DELETE_CONFIRM || 'Xoá thành tựu này?')) return;
    try {
      const res = await fetch(`/api/auth/personal-achievements/${id}`, {
        method: 'DELETE', headers: authHeaders
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      showMsg('success', 'Đã xoá thành tựu.');
      refreshUser();
    } catch (err) { showMsg('error', err.message); }
  };

  const togglePersonalAchievementRemoveImage = (filename) => {
    setPersonalAchievementRemoveImages(prev =>
      prev.includes(filename) ? prev.filter(f => f !== filename) : [...prev, filename]
    );
  };

  // === CUSTOM CURSOR HANDLERS ===
  const [cursorSize, setCursorSize] = useState(user?.customCursor?.cursorSize || 32);

  const handleCursorUpload = async () => {
    const file = cursorInputRef.current?.files?.[0];
    if (!file) return showMsg('error', 'Vui lòng chọn file .ani / .cur / PNG / GIF / SVG.');
    if (file.size > 1024 * 1024) return showMsg('error', 'File con trỏ tối đa 1MB.');
    const okType = /\.(ani|cur|png|gif|svg)$/i.test(file.name)
      || /^(image\/png|image\/gif|image\/svg\+xml|application\/octet-stream|image\/vnd\.microsoft\.icon|image\/x-icon)$/i.test(file.type);
    if (!okType) return showMsg('error', 'Chỉ chấp nhận .ani, .cur, PNG, GIF hoặc SVG.');
    try {
      const fd = new FormData();
      fd.append('cursor', file);
      fd.append('hotspotX', '0');
      fd.append('hotspotY', '0');
      fd.append('cursorSize', String(cursorSize));
      fd.append('activate', '1');
      const res = await fetch('/api/auth/cursor', { method: 'POST', headers: authHeaders, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      if (data.customCursor?.kind === 'ani') {
        showMsg('success', '⚠ Đã tải lên file .ani thành công, NHƯNG trình duyệt KHÔNG hỗ trợ .ani cho con trỏ web — con trỏ sẽ không hiển thị trên hồ sơ. Hãy dùng .cur, PNG, GIF hoặc SVG.');
      } else {
        showMsg('success', 'Đã tải con trỏ tuỳ chỉnh và áp dụng. Mở hồ sơ cá nhân để xem hiệu ứng.');
      }
      if (cursorInputRef.current) cursorInputRef.current.value = '';
      await refreshUser();
    } catch (err) { showMsg('error', err.message); }
  };

  // Update size/hotspot WITHOUT re-uploading
  const handleCursorUpdateSettings = async () => {
    try {
      const res = await fetch('/api/auth/cursor', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          hotspotX: 0,
          hotspotY: 0,
          cursorSize: cursorSize
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      showMsg('success', 'Đã cập nhật kích thước con trỏ.');
      refreshUser();
    } catch (err) { showMsg('error', err.message); }
  };

  const handleCursorDelete = async () => {
    if (!window.confirm(lang.DASHBOARD_CURSOR_DELETE_CONFIRM || 'Xoá con trỏ tuỳ chỉnh?')) return;
    try {
      const res = await fetch('/api/auth/cursor', { method: 'DELETE', headers: authHeaders });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      showMsg('success', 'Đã xoá con trỏ tuỳ chỉnh.');
      refreshUser();
    } catch (err) { showMsg('error', err.message); }
  };

  // === PERSONAL POSTS HANDLERS ===
  const fetchPersonalPosts = async () => {
    if (!user?.username) return;
    try {
      const res = await fetch(`/api/profile/${user.username}/posts`);
      const data = await res.json();
      if (res.ok) setPersonalPosts(data.posts || []);
    } catch (_) {}
  };

  const resetPersonalPostForm = () => {
    setPersonalPostForm({ title: '', excerpt: '', content: '', tagsStr: '' });
    setPersonalPostEditing(null);
  };

  const handlePersonalPostSubmit = async (e) => {
    e.preventDefault();
    if (!personalPostForm.title.trim()) return showMsg('error', 'Tiêu đề bắt buộc.');
    const tags = personalPostForm.tagsStr.split(',').map(t => t.trim()).filter(Boolean);
    try {
      const url = personalPostEditing
        ? `/api/auth/personal-posts/${personalPostEditing}`
        : '/api/auth/personal-posts';
      const method = personalPostEditing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          title: personalPostForm.title.trim(),
          excerpt: personalPostForm.excerpt.trim(),
          content: personalPostForm.content,
          tags
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      showMsg('success', personalPostEditing ? 'Đã cập nhật bài viết.' : 'Đã đăng bài viết.');
      resetPersonalPostForm();
      fetchPersonalPosts();
    } catch (err) { showMsg('error', err.message); }
  };

  const handlePersonalPostEdit = (p) => {
    setPersonalPostEditing(p.id);
    setPersonalPostForm({
      title: p.title || '',
      excerpt: p.excerpt || '',
      content: p.content || '',
      tagsStr: (p.tags || []).join(', ')
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePersonalPostDelete = async (id) => {
    if (!window.confirm(lang.DASHBOARD_POST_DELETE_CONFIRM || 'Xoá bài viết này?')) return;
    try {
      const res = await fetch(`/api/auth/personal-posts/${id}`, { method: 'DELETE', headers: authHeaders });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      showMsg('success', 'Đã xoá bài viết.');
      fetchPersonalPosts();
    } catch (err) { showMsg('error', err.message); }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      showMsg('error', 'Ảnh quá lớn (tối đa 20 MB).');
      if (avatarInputRef.current) avatarInputRef.current.value = '';
      return;
    }
    setAvatarCropFile(file);
  };

  const handleAvatarCropConfirm = async (blob) => {
    const formData = new FormData();
    formData.append('avatar', blob, 'avatar.png');
    try {
      const res = await fetch('/api/auth/avatar', {
        method: 'POST',
        headers: authHeaders,
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setUserAvatar(data.avatar);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
      setAvatarCropFile(null);
      showMsg('success', lang.DASHBOARD_AVATAR_SUCCESS || 'Avatar updated!');
    } catch (err) {
      showMsg('error', err.message);
      throw err;
    }
  };

  const handleAvatarDelete = async () => {
    if (!window.confirm(lang.DASHBOARD_AVATAR_CONFIRM || 'Remove your avatar?')) return;
    try {
      const res = await fetch('/api/auth/avatar', {
        method: 'DELETE',
        headers: authHeaders
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setUserAvatar('');
      showMsg('success', lang.DASHBOARD_AVATAR_REMOVED || 'Avatar removed.');
    } catch (err) { showMsg('error', err.message); }
  };

  const handlePhotoUpload = async () => {
    const files = photoInputRef.current?.files;
    if (!files || files.length === 0) return;
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('photos', files[i]);
    }
    try {
      const res = await fetch('/api/auth/photos', {
        method: 'POST',
        headers: authHeaders,
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setUserPhotos(data.photos || []);
      photoInputRef.current.value = '';
      showMsg('success', lang.DASHBOARD_PHOTOS_SUCCESS || 'Photos uploaded!');
    } catch (err) { showMsg('error', err.message); }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      return showMsg('error', lang.DASHBOARD_PASSWORD_MISMATCH || 'Passwords do not match.');
    }
    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      showMsg('success', lang.DASHBOARD_PASSWORD_SUCCESS || 'Password changed!');
    } catch (err) { showMsg('error', err.message); }
  };

  const handleEmailChange = (e) => setEmailChangeData({ ...emailChangeData, [e.target.name]: e.target.value });

  const handleEmailSave = async (e) => {
    e.preventDefault();
    if (!emailChangeData.newEmail || !emailChangeData.currentPassword) {
      return showMsg('error', lang.DASHBOARD_EMAIL_REQUIRED || 'Email and password are required.');
    }
    try {
      const res = await fetch('/api/auth/email', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          newEmail: emailChangeData.newEmail.trim(),
          currentPassword: emailChangeData.currentPassword
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setEmailChangeData({ newEmail: data.user.email, currentPassword: '' });
      showMsg('success', lang.DASHBOARD_EMAIL_SUCCESS || 'Email updated successfully!');
      // Soft refresh — no full page reload needed
      await refreshUser();
    } catch (err) { showMsg('error', err.message); }
  };

  // Username self-edit (mirrors handleEmailSave). Server enforces uniqueness
  // & format; profileEditGate blocks mam_non, so any tap_su+ member can use
  // this form. Existing JWT remains valid because the token is keyed by `id`,
  // not by username — no logout required after rename.
  const handleUsernameChange = (e) => setUsernameChangeData({ ...usernameChangeData, [e.target.name]: e.target.value });

  const handleUsernameSave = async (e) => {
    e.preventDefault();
    const next = (usernameChangeData.newUsername || '').trim();
    if (!next || !usernameChangeData.currentPassword) {
      return showMsg('error', lang.DASHBOARD_USERNAME_REQUIRED || 'Username và mật khẩu là bắt buộc.');
    }
    if (!/^[A-Za-z0-9_]{3,30}$/.test(next)) {
      return showMsg('error', lang.DASHBOARD_USERNAME_INVALID || 'Username 3-30 ký tự, chỉ chữ/số/gạch dưới.');
    }
    try {
      const res = await fetch('/api/auth/username', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          newUsername: next,
          currentPassword: usernameChangeData.currentPassword
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setUsernameChangeData({ newUsername: data.user.username, currentPassword: '' });
      showMsg('success', lang.DASHBOARD_USERNAME_SUCCESS || 'Đã đổi tên đăng nhập.');
      await refreshUser();
    } catch (err) { showMsg('error', err.message); }
  };

  // === MEMBER MANAGEMENT ===

  const handleNewMemberRoleToggle = (roleKey) => {
    setNewMember(prev => {
      const roles = prev.role.includes(roleKey)
        ? prev.role.filter(r => r !== roleKey)
        : [...prev.role, roleKey];
      return { ...prev, role: roles.length > 0 ? roles : prev.role };
    });
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/admin/members', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(newMember)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      showMsg('success', `Đã thêm thành viên "${data.member.fullName}"`);
      setNewMember({
        fullName: '', username: '', phone: '', email: '',
        studentClass: '', studentId: '', gen: '',
        password: '', role: ['thanh_vien'], position: 'mam_non',
        ctfField: '', major: '', bio: ''
      });
      setShowAddForm(false);
      fetchMembers();
    } catch (err) { showMsg('error', err.message); }
  };

  const handleBulkImport = async () => {
    const file = fileInputRef.current?.files[0];
    if (!file) return showMsg('error', 'Vui lòng chọn file.');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('defaultPassword', bulkPassword);
    try {
      const res = await fetch('/api/admin/members/bulk', {
        method: 'POST',
        headers: authHeaders,
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setBulkResult(data);
      showMsg('success', data.message);
      fileInputRef.current.value = '';
      fetchMembers();
    } catch (err) { showMsg('error', err.message); }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(lang.DASHBOARD_MEMBER_BULK_DELETE_CONFIRM || `Xoá ${selectedIds.length} thành viên?`)) return;
    try {
      const res = await fetch('/api/admin/members', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ ids: selectedIds })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      showMsg('success', data.message);
      setSelectedIds([]);
      fetchMembers();
    } catch (err) { showMsg('error', err.message); }
  };

  const handleBulkEdit = async () => {
    if (selectedIds.length === 0) return;
    const { gen, position, password, joinDate, addBans, removeBans } = bulkEditData;
    const wantsBan = (addBans && addBans.length) || (removeBans && removeBans.length);
    if (!gen && !position && !password && !joinDate && !wantsBan) {
      return showMsg('error', 'Hãy nhập ít nhất một trường (Gen, Position, Password, Ngày gia nhập, hoặc Ban).');
    }
    if (password && password.length < 6) {
      return showMsg('error', 'Mật khẩu phải có ít nhất 6 ký tự.');
    }
    if (joinDate && !/^\d{4}-\d{2}-\d{2}$/.test(joinDate)) {
      return showMsg('error', 'Ngày gia nhập phải đúng định dạng YYYY-MM-DD.');
    }
    if (!window.confirm(lang.DASHBOARD_MEMBER_BULK_EDIT_CONFIRM || `Áp dụng chỉnh sửa hàng loạt cho ${selectedIds.length} thành viên?`)) return;
    try {
      const res = await fetch('/api/admin/members/bulk-edit', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ ids: selectedIds, gen, position, password, joinDate, addBans, removeBans })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      showMsg('success', data.message);
      setShowBulkEdit(false);
      setBulkEditData({ gen: '', position: '', password: '', joinDate: '', addBans: [], removeBans: [] });
      setSelectedIds([]);
      fetchMembers();
    } catch (err) { showMsg('error', err.message); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    const visible = filteredMembers || members;
    if (selectedIds.length === visible.length) setSelectedIds([]);
    else setSelectedIds(visible.map(m => m.id));
  };

  // === EDIT MEMBER ===

  const handleStartEdit = (member) => {
    setEditingMember({
      ...member,
      role: Array.isArray(member.role) ? [...member.role] : [member.role || 'thanh_vien'],
      position: member.position || 'mam_non',
      title: member.title || '',
      status: member.status || 'active',
      genHistory: Array.isArray(member.genHistory)
        ? member.genHistory.map(h => ({
            gen: String(h.gen || ''),
            title: h.title || '',
            role: Array.isArray(h.role) ? [...h.role] : (h.role ? [h.role] : []),
            position: h.position || ''
          }))
        : [],
      password: ''
    });
  };

  const addGenHistoryEntry = () => {
    setEditingMember(prev => ({
      ...prev,
      genHistory: [...(prev.genHistory || []), { gen: '', title: '', role: [], position: '' }]
    }));
  };

  const removeGenHistoryEntry = (i) => {
    setEditingMember(prev => ({
      ...prev,
      genHistory: (prev.genHistory || []).filter((_, idx) => idx !== i)
    }));
  };

  const updateGenHistoryEntry = (i, patch) => {
    setEditingMember(prev => ({
      ...prev,
      genHistory: (prev.genHistory || []).map((h, idx) => (idx === i ? { ...h, ...patch } : h))
    }));
  };

  const toggleGenHistoryRole = (i, roleKey) => {
    setEditingMember(prev => ({
      ...prev,
      genHistory: (prev.genHistory || []).map((h, idx) => {
        if (idx !== i) return h;
        const set = new Set(Array.isArray(h.role) ? h.role : []);
        if (set.has(roleKey)) set.delete(roleKey); else set.add(roleKey);
        return { ...h, role: Array.from(set) };
      })
    }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditingMember(prev => ({ ...prev, [name]: value }));
  };

  const handleEditRoleToggle = (roleKey) => {
    setEditingMember(prev => {
      const roles = prev.role.includes(roleKey)
        ? prev.role.filter(r => r !== roleKey)
        : [...prev.role, roleKey];
      return { ...prev, role: roles.length > 0 ? roles : prev.role };
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingMember) return;
    try {
      const payload = { ...editingMember };
      // Only send password if not blank
      if (!payload.password) delete payload.password;
      const res = await fetch(`/api/admin/members/${editingMember.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      showMsg('success', `Đã cập nhật thành viên "${data.member.fullName}"`);
      setEditingMember(null);
      fetchMembers();
    } catch (err) { showMsg('error', err.message); }
  };

  const handleCancelEdit = () => setEditingMember(null);

  // === ACHIEVEMENTS ===

  const handleAddAchievement = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('title', achievementForm.title);
    formData.append('description', achievementForm.description);
    formData.append('date', achievementForm.date);
    formData.append('tier', achievementForm.tier || 'notable');
    formData.append('rank', achievementForm.rank || '0');
    formData.append('prize', achievementForm.prize || '');
    const files = achievementImagesRef.current?.files;
    if (files) {
      for (let i = 0; i < files.length; i++) {
        formData.append('images', files[i]);
      }
    }
    try {
      const url = editingAchievement
        ? `/api/achievements/${editingAchievement.id || editingAchievement._id}`
        : '/api/achievements';
      const method = editingAchievement ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: authHeaders,
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      showMsg('success', editingAchievement
        ? (lang.DASHBOARD_ACHIEVEMENT_UPDATED || 'Achievement updated!')
        : (lang.DASHBOARD_ACHIEVEMENT_ADDED || 'Achievement added!'));
      setAchievementForm({ title: '', description: '', date: '', tier: 'notable', rank: '', prize: '' });
      setEditingAchievement(null);
      setShowAchievementForm(false);
      if (achievementImagesRef.current) achievementImagesRef.current.value = '';
      fetchAchievements();
    } catch (err) { showMsg('error', err.message); }
  };

  const handleStartEditAchievement = (ach) => {
    setEditingAchievement(ach);
    setShowAchievementForm(true);
    setAchievementForm({
      title: ach.title || '',
      description: ach.description || '',
      date: ach.date ? String(ach.date).slice(0, 10) : '',
      tier: ach.tier || 'notable',
      rank: ach.rank || '',
      prize: ach.prize || ''
    });
    if (achievementImagesRef.current) achievementImagesRef.current.value = '';
    if (typeof window !== 'undefined') {
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {}
    }
  };

  const handleCancelEditAchievement = () => {
    setEditingAchievement(null);
    setShowAchievementForm(false);
    setAchievementForm({ title: '', description: '', date: '', tier: 'notable', rank: '', prize: '' });
    if (achievementImagesRef.current) achievementImagesRef.current.value = '';
  };

  const handleDeleteAchievement = async (id) => {
    if (!window.confirm(lang.DASHBOARD_CONFIRM_DELETE || 'Are you sure?')) return;
    try {
      const res = await fetch(`/api/achievements/${id}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error);
      }
      showMsg('success', lang.DASHBOARD_ACHIEVEMENT_DELETED || 'Achievement deleted!');
      fetchAchievements();
    } catch (err) { showMsg('error', err.message); }
  };

  // === BLOGS ===

  const handleBlogSubmit = async (e) => {
    e.preventDefault();
    const method = blogEditing ? 'PUT' : 'POST';
    const url = blogEditing ? `/api/blogs/${blogEditing}` : '/api/blogs';
    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(blogForm)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      showMsg('success', blogEditing
        ? (lang.DASHBOARD_BLOG_UPDATED || 'Blog updated!')
        : (lang.DASHBOARD_BLOG_CREATED || 'Blog created!'));
      setBlogForm({ title: '', content: '', tags: '' });
      setBlogEditing(null);
      setShowBlogForm(false);
      fetchBlogs();
    } catch (err) { showMsg('error', err.message); }
  };

  const handleEditBlog = (blog) => {
    setBlogForm({ title: blog.title, content: blog.content, tags: blog.tags || '' });
    setBlogEditing(blog.id || blog._id);
    setShowBlogForm(true);
  };

  const handleDeleteBlog = async (id) => {
    if (!window.confirm(lang.DASHBOARD_CONFIRM_DELETE || 'Are you sure?')) return;
    try {
      const res = await fetch(`/api/blogs/${id}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error);
      }
      showMsg('success', lang.DASHBOARD_BLOG_DELETED || 'Blog deleted!');
      fetchBlogs();
    } catch (err) { showMsg('error', err.message); }
  };

  // === RECRUITMENT ===

  const handleToggleRecruitment = async () => {
    try {
      const res = await fetch('/api/recruitment/toggle', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ campaignId: selectedCampaignId, isOpen: !recruitmentOpen })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setRecruitmentOpen(!!data.isOpen);
      showMsg('success', lang.DASHBOARD_RECRUIT_TOGGLED || 'Recruitment status updated!');
    } catch (err) { showMsg('error', err.message); }
  };

  const handleAddQuestion = () => {
    const newId = `q_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    setRecruitQuestions([...recruitQuestions, { id: newId, text: '', type: 'text', required: false, options: [] }]);
  };

  const handleRemoveQuestion = (index) => {
    setRecruitQuestions(recruitQuestions.filter((_, i) => i !== index));
  };

  const moveRecruitQuestion = (from, to) => {
    if (from === to || from < 0 || to < 0 || from >= recruitQuestions.length || to >= recruitQuestions.length) return;
    setRecruitQuestions(prev => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const handleQuestionDrop = (to) => {
    if (dragQuestionIndex !== null) moveRecruitQuestion(dragQuestionIndex, to);
    setDragQuestionIndex(null);
  };

  const handleQuestionChange = (index, field, value) => {
    const updated = [...recruitQuestions];
    updated[index] = { ...updated[index], [field]: value };
    setRecruitQuestions(updated);
  };

  const handleAddOption = (qIndex) => {
    const updated = [...recruitQuestions];
    updated[qIndex].options = [...(updated[qIndex].options || []), ''];
    setRecruitQuestions(updated);
  };

  const handleOptionChange = (qIndex, oIndex, value) => {
    const updated = [...recruitQuestions];
    updated[qIndex].options[oIndex] = value;
    setRecruitQuestions(updated);
  };

  const handleRemoveOption = (qIndex, oIndex) => {
    const updated = [...recruitQuestions];
    updated[qIndex].options = updated[qIndex].options.filter((_, i) => i !== oIndex);
    setRecruitQuestions(updated);
  };

  const handleSaveQuestions = async (saveTemplateName = '') => {
    try {
      // Normalize: server expects { id, question, type, required, options }
      const payload = recruitQuestions.map((q, i) => ({
        id: q.id || `q_${Date.now()}_${i}`,
        question: (q.question || q.text || '').trim(),
        type: q.type || 'text',
        required: !!q.required,
        options: Array.isArray(q.options) ? q.options.filter(o => o && o.trim() !== '') : []
      }));
      // Validate before sending
      for (const q of payload) {
        if (!q.question) {
          showMsg('error', lang.DASHBOARD_QUESTION_EMPTY || 'Question text cannot be empty.');
          return;
        }
        if (q.type === 'multiple_choice' && q.options.length === 0) {
          showMsg('error', `Multiple choice question "${q.question}" must have at least one option.`);
          return;
        }
      }
      const res = await fetch('/api/recruitment/form', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ campaignId: selectedCampaignId, questions: payload, saveTemplateName })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      // Sync local state with normalized data so UI keeps ids
      setRecruitQuestions(payload.map(q => ({ ...q, text: q.question })));
      showMsg('success', lang.DASHBOARD_QUESTIONS_SAVED || 'Questions saved!');
    } catch (err) { showMsg('error', err.message); }
  };

  const handleExportSubmissions = async () => {
    try {
      const res = await fetch(`/api/recruitment/export?campaignId=${encodeURIComponent(selectedCampaignId)}`, { headers: authHeaders });
      if (!res.ok) throw new Error(lang.DASHBOARD_EXPORT_FAILED || 'Export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'submissions.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { showMsg('error', err.message); }
  };

  // Bulk delete recruitment submissions. Uses the array of currently
  // selected ids; safe to call with 0 selected (we just no-op).
  const handleBulkDeleteSubmissions = async () => {
    if (selectedSubmissionIds.length === 0) return;
    if (!window.confirm(`Xoá ${selectedSubmissionIds.length} câu trả lời đã chọn?`)) return;
    try {
      const res = await fetch('/api/recruitment/submissions', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ ids: selectedSubmissionIds, campaignId: selectedCampaignId })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      showMsg('success', `Đã xoá ${data.removed} câu trả lời.`);
      setSelectedSubmissionIds([]);
      fetchRecruitment();
    } catch (err) { showMsg('error', err.message); }
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/recruitment/campaigns', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(campaignForm)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setCampaignForm({ name: '', gen: '', templateId: '' });
      setSelectedCampaignId(data.campaign.id);
      await fetchRecruitment(data.campaign.id);
      showMsg('success', 'Đã tạo đợt tuyển mới.');
    } catch (err) { showMsg('error', err.message); }
  };

  const handleUpdateCampaign = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`/api/recruitment/campaigns/${editingCampaign.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ name: editingCampaign.name, gen: editingCampaign.gen })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setEditingCampaign(null);
      await fetchRecruitment(data.campaign.id);
      showMsg('success', 'Đã cập nhật đợt tuyển.');
    } catch (err) { showMsg('error', err.message); }
  };

  const handleDeleteCampaign = async (campaign) => {
    const confirmName = window.prompt(`Nhập chính xác tên đợt để xóa toàn bộ hồ sơ:\n${campaign.name}`);
    if (confirmName === null) return;
    try {
      const res = await fetch(`/api/recruitment/campaigns/${campaign.id}`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ confirmName })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setEditingCampaign(null);
      await fetchRecruitment(data.activeCampaignId || '');
      showMsg('success', `Đã xóa đợt tuyển và ${data.removedSubmissions} hồ sơ.`);
    } catch (err) { showMsg('error', err.message); }
  };

  const handleLoadTemplateEditor = (templateId) => {
    setEditingTemplateId(templateId);
    const template = recruitmentTemplates.find(t => t.id === templateId);
    if (!template) return;
    setEditingTemplateName(template.name || '');
    setRecruitQuestions((template.questions || []).map(q => ({ ...q, text: q.question || q.text || '' })));
  };

  const handleUpdateTemplate = async () => {
    const template = recruitmentTemplates.find(t => t.id === editingTemplateId);
    if (!template) return;
    const questions = recruitQuestions.map((q, i) => ({
      id: q.id || `q_${Date.now()}_${i}`, question: (q.question || q.text || '').trim(),
      type: q.type || 'text', required: !!q.required,
      options: Array.isArray(q.options) ? q.options.filter(x => String(x).trim()) : []
    }));
    try {
      const res = await fetch(`/api/recruitment/templates/${template.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ name: editingTemplateName || template.name, questions })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setRecruitmentTemplates(prev => prev.map(t => t.id === data.template.id ? data.template : t));
      showMsg('success', 'Đã cập nhật bộ câu hỏi mẫu.');
    } catch (err) { showMsg('error', err.message); }
  };

  const handleDeleteTemplate = async () => {
    const template = recruitmentTemplates.find(t => t.id === editingTemplateId);
    if (!template) return;
    const confirmName = window.prompt(`Nhập chính xác tên bộ câu hỏi để xóa:\n${template.name}`);
    if (confirmName === null) return;
    try {
      const res = await fetch(`/api/recruitment/templates/${template.id}`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ confirmName })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setRecruitmentTemplates(prev => prev.filter(t => t.id !== template.id));
      setEditingTemplateId(''); setEditingTemplateName('');
      await fetchRecruitment(selectedCampaignId);
      showMsg('success', 'Đã xóa bộ câu hỏi mẫu. Các đợt đã sao chép mẫu không bị thay đổi.');
    } catch (err) { showMsg('error', err.message); }
  };

  const recruitmentExtraFields = () => Object.fromEntries(
    (recruitmentEmailTemplate.extras || []).filter(x => String(x.key || '').trim()).map(x => [String(x.key).trim(), x.value || ''])
  );

  const renderRecruitmentPreview = (value) => {
    const imageVars = Object.fromEntries(recruitmentEmailImages.map((image, i) => [`image${i + 1}`, `/uploads/recruitment-email/${image.name}`]));
    const vars = { ...recruitmentExtraFields(), ...imageVars, fullName: 'Nguyễn Văn A', recipient_name: 'Nguyễn Văn A', email: 'nguyenvana@example.com', recipient_email: 'nguyenvana@example.com', status: 'Qua vòng CV', username: 'nguyenvana', password: 'MatKhauTam123', loginUrl: `${window.location.origin}/login` };
    return Object.entries(vars).reduce((out, [key, val]) => out.split(`{${key}}`).join(String(val)), String(value || ''));
  };

  const handleRecruitmentTestEmail = async () => {
    if (!recruitmentTestEmail.trim()) {
      setRecruitmentTestResult({ type: 'error', text: 'Vui lòng nhập email test.' });
      return;
    }
    setRecruitmentTestResult({ type: 'loading', text: `Đang gửi email test tới ${recruitmentTestEmail.trim()}...` });
    setRecruitmentTestSending(true);
    try {
      const res = await fetch('/api/recruitment/email/test', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          testEmail: recruitmentTestEmail.trim(), ...recruitmentEmailTemplate,
          extraFields: recruitmentExtraFields(), images: recruitmentEmailImages.map(x => x.name)
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setRecruitmentTestResult({ type: 'success', text: data.message || `Đã gửi email test tới ${recruitmentTestEmail.trim()}.` });
      showMsg('success', data.message);
    } catch (err) {
      setRecruitmentTestResult({ type: 'error', text: err.message || 'Gửi email test thất bại.' });
      showMsg('error', err.message);
    }
    finally { setRecruitmentTestSending(false); }
  };

  const handleRecruitmentImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const form = new FormData();
    files.slice(0, Math.max(0, 10 - recruitmentEmailImages.length)).forEach(file => form.append('images', file));
    setRecruitmentEmailImageUploading(true);
    try {
      const res = await fetch('/api/recruitment/email/images', { method: 'POST', headers: authHeaders, body: form });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setRecruitmentEmailImages(prev => [...prev, ...(data.images || [])].slice(0, 10));
      showMsg('success', `Đã tải lên ${(data.images || []).length} ảnh.`);
    } catch (err) { showMsg('error', err.message); }
    finally { setRecruitmentEmailImageUploading(false); }
  };

  const handleRecruitmentImageDelete = async (image) => {
    try {
      const res = await fetch(`/api/recruitment/email/images/${encodeURIComponent(image.name)}`, { method: 'DELETE', headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setRecruitmentEmailImages(prev => prev.filter(x => x.name !== image.name));
    } catch (err) { showMsg('error', err.message); }
  };

  const handleRecruitmentBulkEmail = async () => {
    if (!recruitmentEmailSelectedIds.length) return;
    if (!window.confirm(`Gửi email đồng thời cho ${recruitmentEmailSelectedIds.length} ứng viên đã chọn?`)) return;
    setRecruitmentBulkSending(true);
    setRecruitmentBulkResult({ type: 'loading', text: 'Đang gửi email hàng loạt...' });
    try {
      const res = await fetch('/api/recruitment/email/bulk', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          ids: recruitmentEmailSelectedIds, campaignId: selectedCampaignId,
          ...recruitmentEmailTemplate, extraFields: recruitmentExtraFields(),
          images: recruitmentEmailImages.map(x => x.name)
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setRecruitmentBulkResult({ type: data.failed ? 'error' : 'success', text: `Đã gửi ${data.sent}/${data.total}; thất bại ${data.failed}.` });
      setRecruitmentEmailSelectedIds([]);
      fetchRecruitment(selectedCampaignId);
    } catch (err) { setRecruitmentBulkResult({ type: 'error', text: err.message }); }
    finally { setRecruitmentBulkSending(false); }
  };

  const handleRecruitmentStatus = async (submission, status) => {
    try {
      const res = await fetch(`/api/recruitment/submissions/${submission.id}/status`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ status })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSubmissions(prev => prev.map(s => String(s.id) === String(submission.id) ? data.submission : s));
    } catch (err) { showMsg('error', err.message); }
  };

  const handleRecruitmentNotify = async (e) => {
    e.preventDefault();
    const target = recruitmentNotify;
    try {
      const res = await fetch(`/api/recruitment/submissions/${target.id}/notify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          createAccount: !!target.createAccount,
          username: target.username || '', password: target.password || '',
          subject: target.subject || recruitmentEmailTemplate.subject,
          html: target.html || recruitmentEmailTemplate.html,
          senderName: target.senderName || recruitmentEmailTemplate.senderName || '',
          senderEmail: target.senderEmail || recruitmentEmailTemplate.senderEmail || '',
          extraFields: recruitmentExtraFields(), images: recruitmentEmailImages.map(x => x.name)
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      showMsg('success', 'Đã gửi email cho ứng viên.');
      setRecruitmentNotify(null);
      fetchRecruitment();
    } catch (err) { showMsg('error', err.message); }
  };

  // === SMTP HANDLERS ===
  const fetchSmtp = async () => {
    try {
      const res = await fetch('/api/admin/smtp', { headers: authHeaders });
      const data = await res.json();
      if (res.ok) setSmtpConfig({ ...data.smtp, password: '' });
    } catch (err) { console.error(err); }
  };

  const handleSmtpChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSmtpConfig({ ...smtpConfig, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSaveSmtp = async () => {
    try {
      const payload = { ...smtpConfig };
      // Don't send empty password (means keep existing)
      if (!payload.password) delete payload.password;
      const res = await fetch('/api/admin/smtp', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSmtpConfig({ ...data.smtp, password: '' });
      showMsg('success', lang.DASHBOARD_SMTP_SAVED || 'SMTP configuration saved.');
    } catch (err) { showMsg('error', err.message); }
  };

  const handleTestSmtp = async () => {
    setSmtpTesting(true);
    try {
      const res = await fetch('/api/admin/smtp/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ to: smtpTestTo })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      showMsg('success', data.message || 'SMTP OK');
    } catch (err) { showMsg('error', err.message); }
    finally { setSmtpTesting(false); }
  };

  // === FORUM (Q&A) HANDLERS ===
  const fetchForumTopics = async () => {
    try {
      const res = await fetch('/api/forum/topics', { headers: authHeaders });
      const data = await res.json();
      if (res.ok) setForumTopics(data.topics || []);
    } catch (err) { console.error(err); }
  };

  const fetchForumPosts = async (topicId) => {
    try {
      const res = await fetch(`/api/forum/posts?topicId=${topicId}`, { headers: authHeaders });
      const data = await res.json();
      if (res.ok) setForumPosts(data.posts || []);
    } catch (err) { console.error(err); }
  };

  const openForumThread = async (postId) => {
    setForumLoading(true);
    setForumThread(null);
    try {
      const res = await fetch(`/api/forum/posts/${postId}`, { headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setForumThread(data);
    } catch (err) { showMsg('error', err.message); }
    setForumLoading(false);
  };

  // Admin "Xem" — fetch full thread (post + all replies) with privileged fields
  // (real author identity behind anonymous, IP, user-agent, etc.)
  const openForumAdminThread = async (postId) => {
    setForumAdminThread(null);
    try {
      const res = await fetch(`/api/forum/posts/${postId}`, { headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setForumAdminThread(data);
    } catch (err) { showMsg('error', err.message); }
  };

  const refreshForumThread = async () => {
    if (!forumThread?.post?.id) return;
    try {
      const res = await fetch(`/api/forum/posts/${forumThread.post.id}`, { headers: authHeaders });
      const data = await res.json();
      if (res.ok) setForumThread(data);
    } catch (_) {}
  };

  const fetchForumAdminLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (forumAdminFilter.topicId) params.set('topicId', forumAdminFilter.topicId);
      if (forumAdminFilter.mode && forumAdminFilter.mode !== 'all') params.set('mode', forumAdminFilter.mode);
      const url = '/api/forum/admin/posts' + (params.toString() ? '?' + params.toString() : '');
      const res = await fetch(url, { headers: authHeaders });
      const data = await res.json();
      if (res.ok) setForumAdminLogs(data.posts || []);
    } catch (err) { console.error(err); }
  };

  const handleForumCreatePost = async (e) => {
    e.preventDefault();
    if (!forumSelectedTopic) return showMsg('error', 'Vui lòng chọn chủ đề.');
    if (!forumNewPost.title.trim() || !forumNewPost.content.trim()) {
      return showMsg('error', 'Tiêu đề và nội dung là bắt buộc.');
    }
    try {
      let images = [];
      if (forumNewPost.imageFiles && forumNewPost.imageFiles.length > 0) {
        images = await uploadForumImages(forumNewPost.imageFiles);
      }
      const res = await fetch('/api/forum/posts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          topicId: forumSelectedTopic.id,
          title: forumNewPost.title.trim(),
          content: forumNewPost.content.trim(),
          anonymous: forumNewPost.anonymous,
          images
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      showMsg('success', forumNewPost.anonymous ? 'Đăng ẩn danh thành công!' : 'Đăng bài thành công!');
      setForumViewMode(forumNewPost.anonymous ? 'anonymous' : 'public');
      setForumNewPost({ title: '', content: '', anonymous: false, images: [], imageFiles: [] });
      setForumShowComposer(false);
      fetchForumPosts(forumSelectedTopic.id);
      fetchForumTopics();
      if (data.post?.id) openForumThread(data.post.id);
    } catch (err) { showMsg('error', err.message); }
  };

  const handleForumCreateReply = async (e) => {
    e.preventDefault();
    if (!forumThread?.post?.id) return;
    if (!forumNewReply.content.trim() && (!forumNewReply.imageFiles || forumNewReply.imageFiles.length === 0)) {
      return showMsg('error', 'Cần nhập nội dung hoặc đính kèm ít nhất 1 ảnh.');
    }
    try {
      let images = [];
      if (forumNewReply.imageFiles && forumNewReply.imageFiles.length > 0) {
        images = await uploadForumImages(forumNewReply.imageFiles);
      }
      const res = await fetch(`/api/forum/posts/${forumThread.post.id}/replies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          content: forumNewReply.content.trim(),
          anonymous: forumNewReply.anonymous,
          images
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setForumNewReply({ content: '', anonymous: false, imageFiles: [] });
      await refreshForumThread();
      if (forumSelectedTopic) fetchForumPosts(forumSelectedTopic.id);
    } catch (err) { showMsg('error', err.message); }
  };

  // Helper: upload multiple image files and return stored filenames
  const uploadForumImages = async (files) => {
    const fd = new FormData();
    Array.from(files).forEach(f => fd.append('images', f));
    const res = await fetch('/api/forum/images', {
      method: 'POST',
      headers: { ...authHeaders },
      body: fd
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Upload ảnh thất bại');
    return data.images || [];
  };

  const handleForumDeleteReply = async (id) => {
    if (!window.confirm(lang.DASHBOARD_COMMENT_DELETE_CONFIRM || 'Xoá bình luận này?')) return;
    try {
      const res = await fetch(`/api/forum/replies/${id}`, { method: 'DELETE', headers: authHeaders });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      await refreshForumThread();
    } catch (err) { showMsg('error', err.message); }
  };

  const handleForumDeletePost = async (id) => {
    if (!window.confirm(lang.DASHBOARD_POST_DELETE_CONFIRM || 'Xoá bài viết này (kèm toàn bộ bình luận)?')) return;
    try {
      const res = await fetch(`/api/forum/posts/${id}`, { method: 'DELETE', headers: authHeaders });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      showMsg('success', 'Đã xoá bài viết.');
      if (forumSelectedTopic) fetchForumPosts(forumSelectedTopic.id);
      if (activeTab === 'forum_admin') fetchForumAdminLogs();
      setForumThread(null);
      setForumSelectedPost(null);
    } catch (err) { showMsg('error', err.message); }
  };

  // Admin: topics CRUD
  const handleForumAdminCreateTopic = async (e) => {
    e.preventDefault();
    if (!forumAdminTopicForm.name.trim()) return showMsg('error', 'Tên chủ đề là bắt buộc.');
    try {
      const isEdit = !!forumAdminEditingTopic;
      const url = isEdit ? `/api/forum/topics/${forumAdminEditingTopic}` : '/api/forum/topics';
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(forumAdminTopicForm)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      showMsg('success', isEdit ? 'Đã cập nhật topic.' : 'Đã tạo topic.');
      setForumAdminTopicForm({ name: '', description: '' });
      setForumAdminEditingTopic(null);
      setShowForumTopicForm(false);
      fetchForumTopics();
    } catch (err) { showMsg('error', err.message); }
  };

  const handleForumAdminEditTopic = (topic) => {
    setForumAdminEditingTopic(topic.id);
    setShowForumTopicForm(true);
    setForumAdminTopicForm({ name: topic.name || '', description: topic.description || '' });
  };

  const handleForumAdminDeleteTopic = async (id) => {
    if (!window.confirm(lang.DASHBOARD_TOPIC_DELETE_CONFIRM || 'Xoá topic này (sẽ xoá toàn bộ bài viết bên trong)?')) return;
    try {
      const res = await fetch(`/api/forum/topics/${id}`, { method: 'DELETE', headers: authHeaders });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      showMsg('success', 'Đã xoá topic.');
      fetchForumTopics();
      fetchForumAdminLogs();
    } catch (err) { showMsg('error', err.message); }
  };

  // === RESOURCES (Learning Hub) HANDLERS ===
  const fetchResources = async (adminView = false) => {
    try {
      const url = adminView ? '/api/resources?admin=1' : '/api/resources';
      const res = await fetch(url, { headers: authHeaders });
      const data = await res.json();
      if (res.ok) setResources(data.categories || { document: [], video: [], tool: [] });
    } catch (err) { console.error(err); }
  };

  const handleResourceCatSubmit = async (e) => {
    e.preventDefault();
    if (!resourceCatForm.name.trim()) return showMsg('error', 'Tên mục không được để trống.');
    try {
      const isEdit = !!resourceCatEditing;
      const url = isEdit
        ? `/api/resources/${resourceAdminType}/categories/${resourceCatEditing}`
        : `/api/resources/${resourceAdminType}/categories`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(resourceCatForm)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      showMsg('success', isEdit ? 'Đã cập nhật mục.' : 'Đã thêm mục.');
      setResourceCatForm({ name: '', description: '' });
      setResourceCatEditing(null);
      setShowResourceCatForm(false);
      fetchResources(true);
    } catch (err) { showMsg('error', err.message); }
  };

  const handleResourceCatEdit = (cat) => {
    setResourceCatEditing(cat.id);
    setShowResourceCatForm(true);
    setResourceCatForm({ name: cat.name || '', description: cat.description || '' });
  };

  const handleResourceCatDelete = async (catId) => {
    if (!window.confirm(lang.DASHBOARD_RESOURCE_CAT_DELETE_CONFIRM || 'Xoá mục này và toàn bộ tài liệu bên trong?')) return;
    try {
      const res = await fetch(`/api/resources/${resourceAdminType}/categories/${catId}`, {
        method: 'DELETE', headers: authHeaders
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      showMsg('success', 'Đã xoá mục.');
      fetchResources(true);
    } catch (err) { showMsg('error', err.message); }
  };

  const handleResourceItemSubmit = async (e) => {
    e.preventDefault();
    if (!resourceItemForm.categoryId) return showMsg('error', 'Vui lòng chọn mục.');
    if (!resourceItemForm.title.trim()) return showMsg('error', 'Tiêu đề không được để trống.');
    const file = resourceFileRef.current?.files?.[0];
    const isEdit = !!resourceItemEditing;
    if (!isEdit && !file && !resourceItemForm.url.trim()) {
      return showMsg('error', 'Hãy tải file lên hoặc cung cấp URL.');
    }
    try {
      let res;
      if (isEdit) {
        // PUT metadata only (cannot replace file from dashboard)
        res = await fetch(
          `/api/resources/${resourceAdminType}/categories/${resourceItemEditing.catId}/items/${resourceItemEditing.itemId}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...authHeaders },
            body: JSON.stringify({
              title: resourceItemForm.title.trim(),
              description: resourceItemForm.description.trim(),
              url: resourceItemForm.url.trim(),
              allowedPositions: resourceItemForm.allowedPositions || []
            })
          }
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
        showMsg('success', 'Đã cập nhật tài liệu.');
      } else {
        // Use XHR for upload progress
        const formData = new FormData();
        formData.append('title', resourceItemForm.title.trim());
        formData.append('description', resourceItemForm.description.trim());
        formData.append('url', resourceItemForm.url.trim());
        formData.append('allowedPositions', JSON.stringify(resourceItemForm.allowedPositions || []));
        if (file) formData.append('file', file);
        setResourceAdminUploadProgress(0);
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', `/api/resources/${resourceAdminType}/categories/${resourceItemForm.categoryId}/items`);
          if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          xhr.upload.addEventListener('progress', (ev) => {
            if (ev.lengthComputable) {
              setResourceAdminUploadProgress(Math.round((ev.loaded / ev.total) * 100));
            }
          });
          xhr.onload = () => {
            try {
              const d = JSON.parse(xhr.responseText);
              if (xhr.status >= 200 && xhr.status < 300) resolve(d);
              else reject(new Error(d.error || 'Upload failed'));
            } catch (_) { reject(new Error('Upload failed')); }
          };
          xhr.onerror = () => reject(new Error('Network error'));
          xhr.send(formData);
        });
        setResourceAdminUploadProgress(null);
        showMsg('success', 'Đã thêm tài liệu.');
      }
      setResourceItemForm({ title: '', description: '', url: '', categoryId: '', allowedPositions: [] });
      setResourceItemEditing(null);
      setShowResourceItemForm(false);
      if (resourceFileRef.current) resourceFileRef.current.value = '';
      fetchResources(true);
    } catch (err) {
      setResourceAdminUploadProgress(null);
      showMsg('error', err.message);
    }
  };

  const handleResourceItemEdit = (catId, item) => {
    setResourceItemEditing({ catId, itemId: item.id });
    setShowResourceItemForm(true);
    setResourceItemForm({
      title: item.title || '',
      description: item.description || '',
      url: item.url || '',
      categoryId: catId,
      allowedPositions: Array.isArray(item.allowedPositions) ? item.allowedPositions : []
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleResourceItemDelete = async (catId, itemId) => {
    if (!window.confirm(lang.DASHBOARD_RESOURCE_DELETE_CONFIRM || 'Xoá tài liệu này?')) return;
    try {
      const res = await fetch(`/api/resources/${resourceAdminType}/categories/${catId}/items/${itemId}`, {
        method: 'DELETE', headers: authHeaders
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      showMsg('success', 'Đã xoá tài liệu.');
      fetchResources(true);
    } catch (err) { showMsg('error', err.message); }
  };

  const toggleResourceAllowedPosition = (pos) => {
    setResourceItemForm(prev => ({
      ...prev,
      allowedPositions: (prev.allowedPositions || []).includes(pos)
        ? prev.allowedPositions.filter(p => p !== pos)
        : [...(prev.allowedPositions || []), pos]
    }));
  };

  // === USER UPLOAD to Kho Tài Liệu (non-mam_non) ===
  const handleResourceUserUpload = async (e) => {
    e.preventDefault();
    if (!resourceUserForm.categoryId) return showMsg('error', 'Vui lòng chọn mục.');
    if (!resourceUserForm.title.trim()) return showMsg('error', 'Tiêu đề không được để trống.');
    const file = resourceUserFileRef.current?.files?.[0];
    if (!file && !resourceUserForm.url.trim()) {
      return showMsg('error', 'Hãy tải file lên hoặc cung cấp URL.');
    }
    try {
      const formData = new FormData();
      formData.append('title', resourceUserForm.title.trim());
      formData.append('description', resourceUserForm.description.trim());
      formData.append('url', resourceUserForm.url.trim());
      if (file) formData.append('file', file);
      setResourceUploadProgress(0);
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `/api/resources/${resourceActiveType}/categories/${resourceUserForm.categoryId}/items/user-upload`);
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.upload.addEventListener('progress', (ev) => {
          if (ev.lengthComputable) {
            setResourceUploadProgress(Math.round((ev.loaded / ev.total) * 100));
          }
        });
        xhr.onload = () => {
          try {
            const d = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) resolve(d);
            else reject(new Error(d.error || 'Upload thất bại'));
          } catch (_) { reject(new Error('Upload thất bại')); }
        };
        xhr.onerror = () => reject(new Error('Lỗi mạng'));
        xhr.send(formData);
      });
      setResourceUploadProgress(null);
      showMsg('success', 'Đã tải tài liệu lên thành công.');
      setResourceUserForm({ title: '', description: '', url: '', categoryId: '' });
      setShowResourceUserUpload(false);
      if (resourceUserFileRef.current) resourceUserFileRef.current.value = '';
      fetchResources(false);
    } catch (err) {
      setResourceUploadProgress(null);
      showMsg('error', err.message);
    }
  };

  // === EVENTS HANDLERS (ky_thuat) ===
  const fetchEvents = async () => {
    try {
      const res = await fetch('/api/events');
      const data = await res.json();
      if (res.ok) setEvents(data.events || []);
    } catch (err) { console.error(err); }
  };

  const resetEventForm = () => {
    setEventForm({ title: '', excerpt: '', content: '', eventDate: '', location: '', status: 'upcoming' });
    setEventEditing(null);
    setShowEventForm(false);
    setEventCoverPreview('');
    setEventRemoveCover(false);
    setEventPreviewMode(false);
    if (eventCoverRef.current) eventCoverRef.current.value = '';
  };

  const handleEventEdit = (ev) => {
    setEventEditing(ev.id);
    setShowEventForm(true);
    setEventForm({
      title: ev.title || '',
      excerpt: ev.excerpt || '',
      content: ev.content || '',
      eventDate: ev.eventDate ? ev.eventDate.substring(0, 10) : '',
      location: ev.location || '',
      status: ev.status || 'upcoming'
    });
    setEventCoverPreview(ev.coverImage ? `/uploads/events/${ev.coverImage}` : '');
    setEventRemoveCover(false);
    setEventPreviewMode(false);
    if (eventCoverRef.current) eventCoverRef.current.value = '';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    if (!eventForm.title.trim()) return showMsg('error', 'Tiêu đề sự kiện là bắt buộc.');
    try {
      const formData = new FormData();
      formData.append('title', eventForm.title.trim());
      formData.append('excerpt', eventForm.excerpt.trim());
      formData.append('content', eventForm.content);
      formData.append('eventDate', eventForm.eventDate);
      formData.append('location', eventForm.location.trim());
      formData.append('status', eventForm.status);
      const file = eventCoverRef.current?.files?.[0];
      if (file) formData.append('cover', file);
      if (eventEditing && eventRemoveCover && !file) formData.append('removeCover', '1');

      const url = eventEditing ? `/api/events/${eventEditing}` : '/api/events';
      const method = eventEditing ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: authHeaders, body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      showMsg('success', eventEditing ? 'Đã cập nhật sự kiện.' : 'Đã tạo sự kiện.');
      resetEventForm();
      fetchEvents();
    } catch (err) { showMsg('error', err.message); }
  };

  const handleEventDelete = async (id) => {
    if (!window.confirm(lang.DASHBOARD_EVENT_DELETE_CONFIRM || 'Xoá sự kiện này?')) return;
    try {
      const res = await fetch(`/api/events/${id}`, { method: 'DELETE', headers: authHeaders });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      showMsg('success', 'Đã xoá sự kiện.');
      fetchEvents();
    } catch (err) { showMsg('error', err.message); }
  };

  // Insert markdown image at cursor position
  const handleEventInlineImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await fetch('/api/events/upload-image', {
        method: 'POST', headers: authHeaders, body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      // Insert markdown at cursor in content textarea
      const ta = eventContentRef.current;
      const md = `\n![${file.name}](${data.url})\n`;
      if (ta) {
        const start = ta.selectionStart || 0;
        const end = ta.selectionEnd || 0;
        const newContent = eventForm.content.substring(0, start) + md + eventForm.content.substring(end);
        setEventForm(prev => ({ ...prev, content: newContent }));
        // restore cursor after re-render
        setTimeout(() => {
          ta.focus();
          ta.selectionStart = ta.selectionEnd = start + md.length;
        }, 50);
      } else {
        setEventForm(prev => ({ ...prev, content: prev.content + md }));
      }
      showMsg('success', 'Đã chèn ảnh vào nội dung.');
    } catch (err) { showMsg('error', err.message); }
    finally { e.target.value = ''; }
  };

  const handleEventCoverChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) { setEventCoverPreview(eventEditing ? eventCoverPreview : ''); return; }
    setEventCoverPreview(URL.createObjectURL(file));
    setEventRemoveCover(false);
  };

  // === UTILITY ===

  const getInitials = (name) => (name || 'U').split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();

  // Display priority: "ban_*" first (leadership / committees), then thanh_vien / other.
  // Within the "ban" tier ky_thuat is shown first since it has the highest authority.
  const ROLE_DISPLAY_ORDER = ['ky_thuat', 'ban_dieu_hanh', 'ban_dao_tao', 'ban_su_kien', 'thanh_vien'];
  const getRoleBadges = (roles) => {
    if (!roles) return null;
    const arr = Array.isArray(roles) ? [...roles] : [roles];
    arr.sort((a, b) => {
      const ia = ROLE_DISPLAY_ORDER.indexOf(a);
      const ib = ROLE_DISPLAY_ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
    return arr.map((r, i) => (
      <span key={i} className={`badge badge-${r}`}>{ROLE_LABELS[r] || r}</span>
    ));
  };

  const canManageBlogs = can.blogs;

  const homeTabs = adminMode
    ? [
        ...(can.achievements ? [{ key: 'achievements', label: cleanHomeTabLabel(lang.DASHBOARD_TAB_ACHIEVEMENTS || 'Thành Tích (Home)') }] : []),
        ...(can.blogs ? [{ key: 'blogs', label: cleanHomeTabLabel(lang.DASHBOARD_TAB_BLOGS || 'Quản Lý Blog (Home)') }] : []),
        ...(can.eventsAdmin ? [{ key: 'events_admin', label: cleanHomeTabLabel(lang.DASHBOARD_TAB_EVENTS_ADMIN || 'Quản Lý Sự Kiện (Home)') }] : []),
        ...(can.contributors ? [{ key: 'contributors', label: cleanHomeTabLabel(lang.DASHBOARD_TAB_CONTRIBUTORS || 'Quản Lý Đóng Góp (Home)') }] : []),
        ...(can.aboutGallery ? [{ key: 'about_gallery', label: cleanHomeTabLabel(lang.DASHBOARD_TAB_ABOUT_GALLERY || 'Quản Lý Thông Tin CLB (Home)') }] : []),
      ]
    : [];

  const otherTabs = adminMode
    ? [
        ...(can.members ? [{ key: 'members', label: lang.DASHBOARD_TAB_MEMBERS || 'Member Management' }] : []),
        ...(can.forumAdmin ? [{ key: 'forum_admin', label: 'Diễn đàn' }] : []),
        ...(can.resourcesAdmin ? [{ key: 'resources_admin', label: 'Tài liệu' }] : []),
        ...(can.mailCampaign ? [{ key: 'mail_campaign', label: 'Gửi Mail Sự Kiện' }] : []),
        ...(can.eventCalendar ? [{ key: 'event_calendar', label: 'Lịch Sự Kiện CLB' }] : []),
        ...(can.smtp ? [{ key: 'smtp', label: lang.DASHBOARD_TAB_SMTP || 'SMTP Settings' }] : []),
      ]
    : [
        { key: 'overview', label: lang.DASHBOARD_TAB_OVERVIEW || 'Overview' },
        { key: 'profile', label: lang.DASHBOARD_TAB_PROFILE || 'Edit Profile' },
        { key: 'security', label: lang.DASHBOARD_TAB_SECURITY || 'Security' },
        { key: 'forum', label: lang.DASHBOARD_TAB_FORUM || 'Hỏi Đáp' },
        { key: 'training', label: lang.DASHBOARD_TAB_TRAINING || 'Training' },
        { key: 'resources', label: lang.DASHBOARD_TAB_RESOURCES || 'Kho Tài Liệu' },
        { key: 'event_calendar', label: 'Lịch Sự Kiện CLB' },
        ...(showNominationTab ? [{ key: 'nomination', label: 'Ứng Cử' }] : []),
      ];

  const trainingTabs = adminMode
    ? [
        ...((can.trainingAdmin || can.ctfAdmin) ? [{ key: 'training', label: 'Training' }] : []),
        ...(can.screening ? [{ key: 'screening', label: 'Thông Báo Kết Quả Training' }] : []),
        ...(can.nominationAdmin ? [{ key: 'nomination_admin', label: 'Ứng Cử' }] : []),
      ]
    : [];

  const recruitmentTabs = adminMode && can.recruitment
    ? [
        { key: 'recruitment_campaigns', label: 'Đợt tuyển' },
        { key: 'recruitment_applicants', label: 'Hồ sơ ứng viên' },
        ...(can.recruitmentCanToggle ? [{ key: 'recruitment_questions', label: 'Bộ câu hỏi' }] : []),
        { key: 'recruitment_email', label: 'Gửi Email' },
      ]
    : [];

  const tabs = [...homeTabs, ...trainingTabs, ...recruitmentTabs, ...otherTabs];

  // Honor a deep-link initialTab (from a dedicated route like /blogmanager)
  // whenever it's provided and valid for the current permission set.
  useEffect(() => {
    if (initialTab && tabs.find(t => t.key === initialTab)) {
      setActiveTab(initialTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab]);

  // Pick first valid tab when switching between /dashboard and /setting
  useEffect(() => {
    if (tabs.length > 0 && !tabs.find(t => t.key === activeTab)) {
      setActiveTab(tabs[0].key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminMode]);

  // /setting page is gated to users with any admin power
  if (adminMode && !hasAnyAdminPower) {
    return (
      <section className="dashboard-page">
        <div className="container">
          <div className="dashboard-wrapper" style={{ padding: 60, textAlign: 'center' }}>
            <h2>🚫 Truy cập bị từ chối</h2>
            <p className="form-hint">{lang.DASHBOARD_ADMIN_ONLY_HINT || 'Trang /setting chỉ dành cho thành viên có vai trò quản trị (Kỹ thuật / Chủ nhiệm / Phó chủ nhiệm / Trưởng - Phó ban).'}</p>
            <Link to="/dashboard" className="btn btn-primary" style={{ marginTop: 16, display: 'inline-block' }}>
              ← Về Dashboard
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="dashboard-page">
      {avatarCropFile && (
        <AvatarCropper
          file={avatarCropFile}
          onCancel={() => { setAvatarCropFile(null); if (avatarInputRef.current) avatarInputRef.current.value = ''; }}
          onConfirm={handleAvatarCropConfirm}
        />
      )}
      {/* Top-down toast popup */}
      <AnimatePresence>
        {toast.visible && (
          <motion.div
            key="dash-toast"
            className={`dash-toast dash-toast-${toast.type}`}
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -80, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 320, damping: 26 }}
            role="status"
            aria-live="polite"
          >
            <span className="dash-toast-text">{toast.text}</span>
            <button
              type="button"
              className="dash-toast-close"
              onClick={() => setToast(t => ({ ...t, visible: false }))}
              aria-label={lang.COMMON_CLOSE || 'Đóng'}
            >×</button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="container">
        <motion.div className="dashboard-wrapper" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          {/* Header */}
          <div className="dashboard-header">
            <div className="dashboard-header-profile">
              <div className="dashboard-avatar" style={!user?.avatar ? { background: 'var(--gradient-primary)' } : undefined}>
                {user?.avatar ? <img src={`/uploads/profiles/${user.avatar}`} alt={user.fullName} /> : <span>{getInitials(user?.fullName)}</span>}
              </div>
              <div className="dashboard-user-details">
                <h1 className="dashboard-username">{user?.fullName || user?.username}</h1>
                <div className="dashboard-role-badges">{getRoleBadges(user?.role)}</div>
                <div className="dashboard-member-meta">
                  <span className="dashboard-position">{POSITION_LABELS[user?.position] || user?.position}</span>
                  {user?.gen && <span className="dashboard-gen">Gen {user.gen}</span>}
                </div>
              </div>
            </div>
            {hasAnyAdminPower && (
              <Link
                to={adminMode ? '/dashboard' : '/setting'}
                className="btn btn-outline btn-sm"
                style={{ alignSelf: 'center' }}
              >
                {adminMode ? '← Về Dashboard' : '⚙ Panel Quản Trị →'}
              </Link>
            )}
          </div>

          {/* Tabs */}
          <div className="dashboard-tabs">
            <div className="dashboard-sidebar-scroll">
            {adminMode && homeTabs.length > 0 && (
              <div className={`dashboard-tab-group ${homeTabs.some(tab => tab.key === activeTab) ? 'active' : ''}`}>
                <button
                  type="button"
                  className="dashboard-tab dashboard-tab-group-toggle"
                  onClick={() => setHomeGroupOpen(open => !open)}
                  aria-expanded={homeGroupOpen}
                >
                  <span>Giao diện chính</span>
                  <span className={`dashboard-tab-chevron ${homeGroupOpen ? 'open' : ''}`} aria-hidden="true">⌄</span>
                </button>
                {homeGroupOpen && (
                  <div className="dashboard-tab-children">
                    {homeTabs.map(tab => (
                      <button key={tab.key} className={`dashboard-tab dashboard-tab-child ${activeTab === tab.key ? 'active' : ''}`}
                        onClick={() => {
                          setActiveTab(tab.key);
                          setMessage({ type: '', text: '' });
                          if (ADMIN_TAB_ROUTES[tab.key]) navigate(ADMIN_TAB_ROUTES[tab.key], { replace: true });
                        }}>
                        {tab.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {adminMode && trainingTabs.length > 0 && (
              <div className={`dashboard-tab-group ${trainingTabs.some(tab => tab.key === activeTab) ? 'active' : ''}`}>
                <button
                  type="button"
                  className="dashboard-tab dashboard-tab-group-toggle"
                  onClick={() => setTrainingGroupOpen(open => !open)}
                  aria-expanded={trainingGroupOpen}
                >
                  <span>Training</span>
                  <span className={`dashboard-tab-chevron ${trainingGroupOpen ? 'open' : ''}`} aria-hidden="true">⌄</span>
                </button>
                {trainingGroupOpen && (
                  <div className="dashboard-tab-children">
                    {trainingTabs.map(tab => (
                      <button key={tab.key} className={`dashboard-tab dashboard-tab-child ${activeTab === tab.key ? 'active' : ''}`}
                        onClick={() => {
                          setActiveTab(tab.key);
                          setMessage({ type: '', text: '' });
                          if (ADMIN_TAB_ROUTES[tab.key]) navigate(ADMIN_TAB_ROUTES[tab.key], { replace: true });
                        }}>
                        {tab.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {adminMode && recruitmentTabs.length > 0 && (
              <div className={`dashboard-tab-group ${recruitmentTabs.some(tab => tab.key === activeTab) ? 'active' : ''}`}>
                <button type="button" className="dashboard-tab dashboard-tab-group-toggle"
                  onClick={() => setRecruitmentGroupOpen(open => !open)} aria-expanded={recruitmentGroupOpen}>
                  <span>Tuyển thành viên</span>
                  <span className={`dashboard-tab-chevron ${recruitmentGroupOpen ? 'open' : ''}`} aria-hidden="true">⌄</span>
                </button>
                {recruitmentGroupOpen && (
                  <div className="dashboard-tab-children">
                    {recruitmentTabs.map(tab => (
                      <button key={tab.key} className={`dashboard-tab dashboard-tab-child ${activeTab === tab.key ? 'active' : ''}`}
                        onClick={() => {
                          setActiveTab(tab.key);
                          setMessage({ type: '', text: '' });
                          if (ADMIN_TAB_ROUTES[tab.key]) navigate(ADMIN_TAB_ROUTES[tab.key], { replace: true });
                        }}>
                        {tab.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {otherTabs.map(tab => (
              <button key={tab.key} className={`dashboard-tab ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab(tab.key);
                  setMessage({ type: '', text: '' });
                  // Keep the URL in sync with the chosen panel (deep-link).
                  const routeMap = adminMode ? ADMIN_TAB_ROUTES : MEMBER_TAB_ROUTES;
                  if (routeMap[tab.key]) {
                    navigate(routeMap[tab.key], { replace: true });
                  }
                }}>
                {tab.label}
              </button>
            ))}
            </div>
          </div>

          {/* Content */}
          <div className="dashboard-content">

            {/* ==================== OVERVIEW ==================== */}
            {activeTab === 'overview' && (
              <motion.div className="dashboard-overview" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="overview-grid">
                  {[
                    { icon: 'user', label: lang.DASHBOARD_FULL_NAME || 'Full Name', value: user?.fullName },
                    { icon: 'mail', label: lang.DASHBOARD_EMAIL || 'Email', value: user?.email },
                    { icon: 'phone', label: lang.DASHBOARD_PHONE || 'Phone', value: user?.phone || '-' },
                    { icon: 'shield', label: lang.DASHBOARD_ROLE || 'Role', value: Array.isArray(user?.role) ? user.role.map(r => ROLE_LABELS[r] || r).join(', ') : (ROLE_LABELS[user?.role] || user?.role) },
                    { icon: 'star', label: lang.DASHBOARD_POSITION || 'Cấp Thành Viên', value: POSITION_LABELS[user?.position] || user?.position },
                    ...(user?.title ? [{ icon: 'shield', label: lang.DASHBOARD_TITLE || 'Chức Vụ', value: TITLE_LABELS[user?.title] || user?.title }] : []),
                    { icon: 'gen', label: lang.DASHBOARD_GEN || 'Gen', value: user?.gen ? `Gen ${user.gen}` : '-' },
                    { icon: 'id', label: lang.DASHBOARD_CTF_FIELD || 'CTF Field', value: CTF_FIELDS[user?.ctfField] || user?.ctfField || '-' },
                    { icon: 'calendar', label: lang.DASHBOARD_JOIN_DATE || 'Join Date', value: user?.joinDate || '-' },
                    { icon: 'clock', label: lang.DASHBOARD_STATUS || 'Status', value: user?.status || 'active' },
                  ].map((item, i) => (
                    <div className="overview-card" key={i}>
                      <div className="overview-card-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20">
                          {item.icon === 'user' && <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>}
                          {item.icon === 'mail' && <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></>}
                          {item.icon === 'phone' && <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>}
                          {item.icon === 'id' && <><rect x="2" y="4" width="20" height="16" rx="2"/><line x1="6" y1="8" x2="18" y2="8"/><line x1="6" y1="12" x2="14" y2="12"/><line x1="6" y1="16" x2="10" y2="16"/></>}
                          {item.icon === 'gen' && <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>}
                          {item.icon === 'calendar' && <><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>}
                          {item.icon === 'shield' && <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>}
                          {item.icon === 'star' && <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>}
                          {item.icon === 'clock' && <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>}
                        </svg>
                      </div>
                      <div className="overview-card-info">
                        <h4>{item.label}</h4>
                        <p className={item.icon === 'clock' ? 'status-active' : ''}>{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {user?.bio && <div className="overview-bio"><h4>{lang.DASHBOARD_BIO || 'Bio'}</h4><p>{user.bio}</p></div>}
              </motion.div>
            )}

            {/* ==================== EDIT PROFILE ==================== */}
            {activeTab === 'profile' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {isMamNon && (
                  <div className="dashboard-message error" style={{ marginBottom: 20 }}>
                    🔒 Bạn đang có vị trí <strong>Mầm Non</strong>. Tất cả thông tin hồ sơ sẽ do Kỹ Thuật Viên quản lý. Vui lòng liên hệ Kỹ Thuật nếu cần thay đổi.
                  </div>
                )}
                <div className="profile-tab-layout">
                <fieldset disabled={!canEditProfile} style={{ border: 'none', padding: 0, margin: 0, opacity: canEditProfile ? 1 : 0.6 }}>
                <div className="avatar-upload-section">
                  <div className="avatar-preview-wrap">
                    {userAvatar ? (
                      <img
                        src={`/uploads/profiles/${userAvatar}`}
                        alt="Avatar"
                        className="avatar-preview"
                      />
                    ) : (
                      <div className="avatar-preview avatar-preview-initials">
                        {getInitials(user?.fullName)}
                      </div>
                    )}
                  </div>
                  <div className="avatar-upload-controls">
                    <h3>{lang.DASHBOARD_AVATAR || 'Profile Avatar'}</h3>
                    <p className="form-hint">{lang.DASHBOARD_AVATAR_HINT || 'PNG/JPG — tối đa 20MB. Sau khi chọn bạn có thể kéo & zoom để cắt ảnh vuông.'}</p>
                    <div className="avatar-upload-buttons">
                      <input
                        type="file"
                        ref={avatarInputRef}
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleAvatarChange}
                      />
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => avatarInputRef.current?.click()}
                      >
                        {lang.DASHBOARD_CHANGE_AVATAR || 'Change Avatar'}
                      </button>
                      {userAvatar && (
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={handleAvatarDelete}
                        >
                          {lang.DASHBOARD_REMOVE_AVATAR || 'Remove'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <form id="dashboard-profile-form" className="dashboard-form" onSubmit={handleProfileSave}>
                  <div className="form-group">
                    <label>{lang.DASHBOARD_USERNAME || 'Username'}</label>
                    <div className="username-display">
                      <span className="username-value">@{user?.username}</span>
                      <span className="username-link">/isper/{user?.username}</span>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>{lang.DASHBOARD_FULL_NAME || 'Full Name'}</label>
                    <input type="text" name="fullName" value={profileData.fullName} onChange={handleProfileChange} required />
                  </div>
                  <div className="form-group">
                    <label>{lang.DASHBOARD_PHONE || 'Phone'}</label>
                    <input type="text" name="phone" value={profileData.phone} onChange={handleProfileChange} />
                  </div>
                  <div className="form-group">
                    <label>{lang.DASHBOARD_MAJOR || 'Major'}</label>
                    <input type="text" name="major" value={profileData.major} onChange={handleProfileChange} placeholder={lang.DASHBOARD_MAJOR_PLACEHOLDER || 'e.g. Information Security'} />
                  </div>
                  <div className="form-group">
                    <label>{lang.DASHBOARD_BIO || 'Bio'}</label>
                    <textarea name="bio" value={profileData.bio} onChange={handleProfileChange} rows="4" placeholder={lang.DASHBOARD_BIO_PLACEHOLDER || 'Tell us about yourself...'} />
                  </div>
                  <div className="form-group">
                    <label>{lang.DASHBOARD_CTF_FIELD || 'CTF Field'} *</label>
                    <select name="ctfField" value={profileData.ctfField} onChange={handleProfileChange} required>
                      <option value="">{lang.DASHBOARD_SELECT_CTF || 'Chọn mảng CTF'}</option>
                      {Object.entries(CTF_FIELDS).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{lang.DASHBOARD_WEBSITES || 'Websites & Liên kết'}</label>
                    <p className="form-hint">{lang.DASHBOARD_LINKS_HINT || 'Thêm tối đa 10 liên kết (GitHub, LinkedIn, blog cá nhân...). Mỗi mục có nhãn + URL.'}</p>
                    {(profileData.websites || []).map((site, i) => (
                      <div key={i} className="multi-link-row">
                        <input
                          type="text"
                          placeholder={lang.DASHBOARD_LINK_LABEL_PH || 'Nhãn (VD: GitHub)'}
                          value={site.label || ''}
                          onChange={(e) => {
                            const next = [...profileData.websites];
                            next[i] = { ...next[i], label: e.target.value };
                            setProfileData({ ...profileData, websites: next });
                          }}
                          style={{ flex: '0 0 180px' }}
                        />
                        <input
                          type="url"
                          placeholder={lang.DASHBOARD_LINK_URL_PH || 'https://...'}
                          value={site.url || ''}
                          onChange={(e) => {
                            const next = [...profileData.websites];
                            next[i] = { ...next[i], url: e.target.value };
                            setProfileData({ ...profileData, websites: next });
                          }}
                          style={{ flex: 1 }}
                        />
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => {
                            const next = profileData.websites.filter((_, idx) => idx !== i);
                            setProfileData({ ...profileData, websites: next });
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    {(profileData.websites || []).length < 10 && (
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        style={{ marginTop: 6 }}
                        onClick={() => setProfileData({
                          ...profileData,
                          websites: [...(profileData.websites || []), { label: '', url: '' }]
                        })}
                      >
                        + Thêm liên kết
                      </button>
                    )}
                  </div>
                  <div className="form-group">
                    <label>{lang.DASHBOARD_PROFILE_THEME || 'Profile Theme'}</label>
                    <select name="profileTheme" value={profileData.profileTheme} onChange={handleProfileChange}>
                      <option value="">{lang.DASHBOARD_SELECT_THEME || '-- Select Theme --'}</option>
                      {PROFILE_THEMES.map(t => (
                        <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{lang.DASHBOARD_PROFILE_EFFECT || 'Profile Effect'}</label>
                    <select name="profileEffect" value={profileData.profileEffect} onChange={handleProfileChange}>
                      <option value="">{lang.DASHBOARD_SELECT_EFFECT || '-- Select Effect --'}</option>
                      {PROFILE_EFFECTS.map(e => (
                        <option key={e} value={e}>{e.charAt(0).toUpperCase() + e.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{lang.DASHBOARD_PROFILE_CURSOR || 'Con Trỏ Chuột Hồ Sơ'}</label>
                    <select name="profileCursor" value={profileData.profileCursor} onChange={handleProfileChange}>
                      <option value="">{lang.DASHBOARD_SELECT_CURSOR || '-- Chọn con trỏ --'}</option>
                      {PROFILE_CURSORS.map(c => (
                        <option key={c} value={c}>{PROFILE_CURSOR_LABELS[c] || c}</option>
                      ))}
                    </select>
                  </div>
                  {/* (Save button moved to the very bottom — after all profile sub-sections.) */}
                </form>

                {/* Photo Upload Section */}
                <div className="dashboard-photos-section">
                  <h3>{lang.DASHBOARD_PHOTOS || 'Profile Photos'}</h3>
                  <p className="form-hint">{lang.DASHBOARD_PHOTOS_HINT || 'Upload up to 5 photos.'}</p>
                  {userPhotos && userPhotos.length > 0 && (
                    <div className="photos-grid">
                      {userPhotos.map((photo, i) => (
                        <div className="photo-thumb" key={i}>
                          <img src={`/uploads/profiles/${photo}`} alt={`Profile ${i + 1}`} />
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="photo-upload-row">
                    <input type="file" ref={photoInputRef} accept="image/*" multiple className="file-input" />
                    <button type="button" className="btn btn-primary btn-sm" onClick={handlePhotoUpload}>
                      {lang.DASHBOARD_UPLOAD_PHOTOS || 'Upload Photos'}
                    </button>
                  </div>
                </div>

                {/* Music Playlist */}
                <div className="dashboard-photos-section">
                  <h3>Playlist Nhạc Nền Hồ Sơ</h3>
                  <p className="form-hint">
                    Upload nhạc (mỗi file tối đa 200MB, định dạng MP3/WAV/OGG/M4A/FLAC).
                  </p>

                  {/* Existing playlist (already saved on server) */}
                  {Array.isArray(user?.musicPlaylist) && user.musicPlaylist.length > 0 ? (
                    <div className="music-playlist-list">
                      {user.musicPlaylist.map((t, i) => (
                        <div key={t.id || i} className="music-track-row">
                          <div className="music-track-num">{(i + 1).toString().padStart(2, '0')}</div>
                          <div className="music-track-info">
                            <strong>{t.title || t.originalName}</strong>
                            <div className="form-hint">
                              {(t.size / 1024 / 1024).toFixed(2)} MB · {fmtDMY(t.uploadedAt)}
                            </div>
                            <audio controls src={`/uploads/music/${t.filename}`} style={{ width: '100%', maxWidth: 320, marginTop: 6 }} />
                          </div>
                          <div className="music-track-actions">
                            <button type="button" className="btn btn-outline btn-sm" onClick={() => handleMusicTrackRename(t)}>Đổi tên</button>
                            <button type="button" className="btn btn-danger btn-sm" onClick={() => handleMusicTrackDelete(t)}>Xoá</button>
                          </div>
                        </div>
                      ))}
                      <div style={{ marginTop: 8 }}>
                        <button type="button" className="btn btn-danger btn-sm" onClick={handleMusicClearAll}>
                          Xoá toàn bộ playlist
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="empty-text" style={{ textAlign: 'left' }}>Playlist trống.</p>
                  )}

                  {/* Music settings */}
                  <div className="music-settings-row">
                    <label className="music-setting-toggle">
                      <input
                        type="checkbox"
                        checked={musicSettings.autoPlay}
                        onChange={(e) => setMusicSettings({ ...musicSettings, autoPlay: e.target.checked })}
                      />
                      <span>{lang.DASHBOARD_MUSIC_AUTOPLAY_HINT || 'Tự động phát khi guest mở hồ sơ'}</span>
                    </label>
                    <div className="music-setting-loop">
                      <label>{lang.DASHBOARD_LOOP_MODE || 'Chế độ lặp:'}</label>
                      <select
                        value={musicSettings.loopMode}
                        onChange={(e) => setMusicSettings({ ...musicSettings, loopMode: e.target.value })}
                      >
                        <option value="none">Không lặp</option>
                        <option value="all">Lặp danh sách</option>
                        <option value="one">Lặp 1 bài</option>
                      </select>
                    </div>
                  </div>

                  {/* Upload queue */}
                  <h4 style={{ marginTop: 18, marginBottom: 8 }}>Hàng đợi tải lên ({pendingMusic.length})</h4>
                  <div className="photo-upload-row" style={{ alignItems: 'center' }}>
                    <input
                      type="file"
                      ref={musicInputRef}
                      accept="audio/*,.mp3,.wav,.ogg,.m4a,.flac,.aac"
                      className="file-input"
                      multiple
                    />
                    <input
                      type="text"
                      placeholder={lang.DASHBOARD_MUSIC_DISPLAY_PH || 'Tên hiển thị (tuỳ chọn, áp dụng cho file đầu tiên)'}
                      value={musicTitle}
                      onChange={(e) => setMusicTitle(e.target.value)}
                      style={{ flex: 1, minWidth: 160 }}
                    />
                    <button type="button" className="btn btn-outline btn-sm" onClick={handleMusicQueue}>
                      ➕ Thêm vào hàng đợi
                    </button>
                  </div>

                  {pendingMusic.length > 0 && (
                    <div className="music-pending-list" style={{ marginTop: 12 }}>
                      {pendingMusic.map(item => (
                        <div key={item.id} className={`music-pending-row status-${item.status}`}>
                          <div className="music-pending-name">
                            <strong>{item.title}</strong>
                            <span className="form-hint"> · {(item.file.size / 1024 / 1024).toFixed(2)} MB</span>
                          </div>
                          <div className="music-pending-progress">
                            <div className="music-pending-bar">
                              <div
                                className="music-pending-fill"
                                style={{ width: `${item.progress}%` }}
                              />
                            </div>
                            <span className="music-pending-pct">
                              {item.status === 'done' ? '✓ Xong'
                                : item.status === 'error' ? `✗ ${item.error || 'Lỗi'}`
                                : item.status === 'uploading' ? `${item.progress}%`
                                : 'Chờ...'}
                            </span>
                          </div>
                          {item.status !== 'uploading' && item.status !== 'done' && (
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => handleMusicQueueRemove(item.id)}
                            >✕</button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="admin-form-actions" style={{ marginTop: 14 }}>
                    {pendingMusic.length > 0 && !savingMusic && (
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => setPendingMusic([])}
                      >
                        Xoá hàng đợi
                      </button>
                    )}
                    {savingMusic && <span className="form-hint">{lang.COMMON_UPLOADING || '⏳ Đang upload...'}</span>}
                  </div>
                </div>

                {/* Personal Achievements */}
                <div className="dashboard-photos-section">
                  <h3>Thành Tựu Cá Nhân</h3>
                  <form className="admin-form" onSubmit={handlePersonalAchievementSubmit}>
                    <div className="admin-form-grid">
                      <div className="form-group form-group-full">
                        <label>{lang.COMMON_TITLE || 'Tiêu đề *'}</label>
                        <input
                          type="text"
                          value={personalAchievementForm.title}
                          onChange={(e) => setPersonalAchievementForm({ ...personalAchievementForm, title: e.target.value })}
                          required
                          placeholder={lang.DASHBOARD_ACHIEVEMENT_TITLE_PH || 'VD: Giải Nhất CTF Cyber SAFE 2024'}
                        />
                      </div>
                      <div className="form-group">
                        <label>{lang.DASHBOARD_ACHIEVEMENT_DATE || 'Ngày đạt được'}</label>
                        <DateInput
                          value={personalAchievementForm.date}
                          onChange={(v) => setPersonalAchievementForm({ ...personalAchievementForm, date: v })}
                        />
                      </div>
                      <div className="form-group">
                        <label>{lang.DASHBOARD_ACHIEVEMENT_IMAGES || 'Ảnh (tối đa 6 ảnh, 30MB/ảnh)'}</label>
                        <input
                          type="file"
                          ref={personalAchievementImagesRef}
                          accept="image/*"
                          multiple
                          className="file-input"
                        />
                      </div>
                      <div className="form-group form-group-full">
                        <label>{lang.COMMON_DESCRIPTION || 'Mô tả'}</label>
                        <textarea
                          rows="3"
                          value={personalAchievementForm.description}
                          onChange={(e) => setPersonalAchievementForm({ ...personalAchievementForm, description: e.target.value })}
                          placeholder={lang.DASHBOARD_ACHIEVEMENT_DESC_PH || 'Mô tả ngắn về thành tựu...'}
                        />
                      </div>
                    </div>

                    {personalAchievementEditing && (() => {
                      const ed = personalAchievements.find(a => a.id === personalAchievementEditing);
                      const imgs = ed?.images || [];
                      if (!imgs.length) return null;
                      return (
                        <div style={{ marginBottom: 12 }}>
                          <label>{lang.DASHBOARD_IMAGES_EXISTING || 'Ảnh hiện tại (tick để xoá khi lưu):'}</label>
                          <div className="achievement-edit-imgs">
                            {imgs.map(fn => (
                              <label key={fn} className={`achievement-edit-thumb ${personalAchievementRemoveImages.includes(fn) ? 'remove' : ''}`}>
                                <img src={`/uploads/achievements/${fn}`} alt="" />
                                <input
                                  type="checkbox"
                                  checked={personalAchievementRemoveImages.includes(fn)}
                                  onChange={() => togglePersonalAchievementRemoveImage(fn)}
                                />
                                <span>{lang.COMMON_DELETE || 'Xoá'}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    <div className="admin-form-actions">
                      <button type="submit" className="btn btn-primary btn-sm">
                        {personalAchievementEditing ? 'Cập nhật thành tựu' : 'Thêm thành tựu'}
                      </button>
                      {personalAchievementEditing && (
                        <button type="button" className="btn btn-outline btn-sm" onClick={resetPersonalAchievementForm}>
                          Huỷ
                        </button>
                      )}
                    </div>
                  </form>

                  {personalAchievements.length > 0 && (
                    <div className="resource-admin-list" style={{ marginTop: 16 }}>
                      {personalAchievements.map(item => (
                        <div key={item.id} className="event-admin-card">
                          {item.images && item.images[0] && (
                            <img
                              src={`/uploads/achievements/${item.images[0]}`}
                              alt={item.title}
                              className="event-admin-thumb"
                            />
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <strong>{item.title}</strong>
                            {item.description && <div className="form-hint">{item.description}</div>}
                            <div className="resource-item-meta">
                              {item.date && <span>🗓 {fmtDMY(item.date)}</span>}
                              {item.images && <span>🖼 {item.images.length} ảnh</span>}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                            <button type="button" className="btn btn-outline btn-sm" onClick={() => handlePersonalAchievementEdit(item)}>Sửa</button>
                            <button type="button" className="btn btn-danger btn-sm" onClick={() => handlePersonalAchievementDelete(item.id)}>Xoá</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Personal Blog Manager */}
                <div className="dashboard-photos-section">
                  <h3>Blog Cá Nhân</h3>
                  <form className="admin-form" onSubmit={handlePersonalPostSubmit}>
                    <div className="admin-form-grid">
                      <div className="form-group form-group-full">
                        <label>{lang.COMMON_TITLE || 'Tiêu đề *'}</label>
                        <input
                          type="text"
                          value={personalPostForm.title}
                          onChange={(e) => setPersonalPostForm({ ...personalPostForm, title: e.target.value })}
                          required
                        />
                      </div>
                      <div className="form-group form-group-full">
                        <label>{lang.COMMON_EXCERPT || 'Tóm tắt ngắn'}</label>
                        <input
                          type="text"
                          value={personalPostForm.excerpt}
                          onChange={(e) => setPersonalPostForm({ ...personalPostForm, excerpt: e.target.value })}
                        />
                      </div>
                      <div className="form-group form-group-full">
                        <label>{lang.DASHBOARD_TAGS_LABEL || 'Tags (phân tách bằng dấu phẩy)'}</label>
                        <input
                          type="text"
                          value={personalPostForm.tagsStr}
                          onChange={(e) => setPersonalPostForm({ ...personalPostForm, tagsStr: e.target.value })}
                          placeholder={lang.DASHBOARD_TAGS_PH || 'ctf, writeup, crypto'}
                        />
                      </div>
                      <div className="form-group form-group-full">
                        <label>{lang.DASHBOARD_CONTENT_MD || 'Nội dung (Markdown, GFM)'}</label>
                        <div style={{ marginBottom: 8 }}>
                          <input
                            type="file"
                            accept=".md,.markdown,text/markdown,text/plain"
                            className="file-input"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (!f) return;
                              if (f.size > 2 * 1024 * 1024) {
                                showMsg('error', 'File .md tối đa 2MB.');
                                e.target.value = '';
                                return;
                              }
                              const reader = new FileReader();
                              reader.onload = (ev) => {
                                const text = String(ev.target?.result || '');
                                // If it has YAML front-matter --- ... --- try to extract title/tags
                                let body = text;
                                let parsedTitle = '';
                                let parsedTags = '';
                                let parsedExcerpt = '';
                                const fm = text.match(/^---\s*\r?\n([\s\S]*?)\r?\n---\s*\r?\n?/);
                                if (fm) {
                                  const fmText = fm[1];
                                  body = text.slice(fm[0].length);
                                  const titleMatch = fmText.match(/^\s*title\s*:\s*(.+?)\s*$/m);
                                  if (titleMatch) parsedTitle = titleMatch[1].replace(/^["']|["']$/g, '').trim();
                                  const excerptMatch = fmText.match(/^\s*(?:excerpt|description|summary)\s*:\s*(.+?)\s*$/m);
                                  if (excerptMatch) parsedExcerpt = excerptMatch[1].replace(/^["']|["']$/g, '').trim();
                                  const tagsMatch = fmText.match(/^\s*tags\s*:\s*(.+?)\s*$/m);
                                  if (tagsMatch) {
                                    let t = tagsMatch[1].trim();
                                    t = t.replace(/^\[|\]$/g, '');
                                    parsedTags = t.split(/[,\s]+/).filter(Boolean).join(', ');
                                  }
                                }
                                // Fallback: if no frontmatter title, look for the first # heading
                                if (!parsedTitle) {
                                  const h1 = body.match(/^#\s+(.+)$/m);
                                  if (h1) parsedTitle = h1[1].trim();
                                }
                                setPersonalPostForm(prev => ({
                                  ...prev,
                                  title: prev.title || parsedTitle || f.name.replace(/\.(md|markdown)$/i, ''),
                                  excerpt: prev.excerpt || parsedExcerpt,
                                  tagsStr: prev.tagsStr || parsedTags,
                                  content: body.trim()
                                }));
                                showMsg('success', 'Đã nhập nội dung từ file .md.');
                              };
                              reader.onerror = () => showMsg('error', 'Không đọc được file.');
                              reader.readAsText(f, 'utf-8');
                              e.target.value = '';
                            }}
                          />
                          <span className="form-hint" style={{ marginLeft: 6 }}>
                            Nhập từ file <code>.md</code> (hỗ trợ YAML front-matter: <code>title</code>, <code>tags</code>, <code>excerpt</code>).
                          </span>
                        </div>
                        <textarea
                          rows="10"
                          value={personalPostForm.content}
                          onChange={(e) => setPersonalPostForm({ ...personalPostForm, content: e.target.value })}
                          placeholder={lang.DASHBOARD_BLOG_CONTENT_PH || `# Chào\n\nBài viết đầu tiên của mình...`}
                          style={{ fontFamily: 'var(--font-mono)', fontSize: '0.85rem' }}
                        />
                      </div>
                    </div>
                    <div className="admin-form-actions">
                      <button type="submit" className="btn btn-primary btn-sm">
                        {personalPostEditing ? 'Cập nhật bài' : 'Đăng bài'}
                      </button>
                      {personalPostEditing && (
                        <button type="button" className="btn btn-outline btn-sm" onClick={resetPersonalPostForm}>
                          Huỷ
                        </button>
                      )}
                    </div>
                  </form>

                  {personalPosts.length > 0 && (
                    <div className="resource-admin-list" style={{ marginTop: 16 }}>
                      {personalPosts.map(p => (
                        <div key={p.id} className="resource-admin-item">
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <strong>{p.title}</strong>
                            {p.excerpt && <div className="form-hint">{p.excerpt}</div>}
                            <div className="resource-item-meta">
                              <span>🕒 {fmtDMYTime(p.createdAt)}</span>
                              {p.tags && p.tags.length > 0 && <span>🏷 {p.tags.join(', ')}</span>}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                            <button type="button" className="btn btn-outline btn-sm" onClick={() => handlePersonalPostEdit(p)}>Sửa</button>
                            <button type="button" className="btn btn-danger btn-sm" onClick={() => handlePersonalPostDelete(p.id)}>Xoá</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                </fieldset>

                <aside className="profile-tab-preview">
                  <ProfilePreview
                    theme={profileData.profileTheme || 'classic'}
                    effect={profileData.profileEffect || 'none'}
                    cursor={profileData.profileCursor || 'default'}
                    user={user}
                  />
                </aside>
                </div>

                {/* ===== FLOATING SAVE BAR (submits the profile form above) ===== */}
                <div className="dashboard-save-bar">
                  <button
                    type="submit"
                    form="dashboard-profile-form"
                    className="btn btn-primary"
                  >
                    {lang.DASHBOARD_SAVE || 'Lưu thay đổi'}
                  </button>
                </div>
              </motion.div>
            )}

            {/* ==================== SECURITY ==================== */}
            {activeTab === 'security' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {/* Change Username (tap_su+ only — server enforces same gate) */}
                {canEditProfile && (
                <form className="dashboard-form" onSubmit={handleUsernameSave}>
                  <h3 className="dashboard-section-title">{lang.DASHBOARD_CHANGE_USERNAME || 'Đổi Username'}</h3>
                  <div className="form-group">
                    <label>{lang.DASHBOARD_CURRENT_USERNAME || 'Username hiện tại'}</label>
                    <input type="text" value={user?.username || ''} disabled readOnly />
                  </div>
                  <div className="form-group">
                    <label>{lang.DASHBOARD_NEW_USERNAME || 'Username mới'}</label>
                    <input
                      type="text"
                      name="newUsername"
                      value={usernameChangeData.newUsername}
                      onChange={handleUsernameChange}
                      required
                      placeholder={lang.DASHBOARD_NEW_USERNAME_PH || 'newusername'}
                      pattern="[A-Za-z0-9_]{3,30}"
                      title="3-30 ký tự, chỉ chữ/số/gạch dưới"
                    />
                  </div>
                  <div className="form-group">
                    <label>{lang.DASHBOARD_ACCOUNT_PASSWORD || 'Account Password'}</label>
                    <input
                      type="password"
                      name="currentPassword"
                      value={usernameChangeData.currentPassword}
                      onChange={handleUsernameChange}
                      required
                      placeholder={lang.DASHBOARD_ACCOUNT_PASSWORD_HINT || 'Enter your password to confirm'}
                    />
                  </div>
                  <button type="submit" className="btn btn-primary">{lang.DASHBOARD_UPDATE_USERNAME || 'Cập nhật'}</button>
                  <p className="form-hint" style={{ marginTop: 8 }}>
                    {lang.DASHBOARD_USERNAME_HINT || 'Sau khi đổi username, hãy đăng nhập lại để có hiệu lực.'}
                  </p>
                </form>
                )}

                {/* Change Email */}
                <form className="dashboard-form" onSubmit={handleEmailSave} style={{ marginTop: canEditProfile ? 32 : 0 }}>
                  <h3 className="dashboard-section-title">{lang.DASHBOARD_CHANGE_EMAIL || 'Change Email'}</h3>
                  <div className="form-group">
                    <label>{lang.DASHBOARD_CURRENT_EMAIL || 'Current Email'}</label>
                    <input type="email" value={user?.email || ''} disabled readOnly />
                  </div>
                  <div className="form-group">
                    <label>{lang.DASHBOARD_NEW_EMAIL || 'New Email'}</label>
                    <input type="email" name="newEmail" value={emailChangeData.newEmail} onChange={handleEmailChange} required placeholder={lang.DASHBOARD_NEW_EMAIL_PH || 'newemail@example.com'} />
                  </div>
                  <div className="form-group">
                    <label>{lang.DASHBOARD_ACCOUNT_PASSWORD || 'Account Password'}</label>
                    <input type="password" name="currentPassword" value={emailChangeData.currentPassword} onChange={handleEmailChange} required placeholder={lang.DASHBOARD_ACCOUNT_PASSWORD_HINT || 'Enter your password to confirm'} />
                  </div>
                  <button type="submit" className="btn btn-primary">{lang.DASHBOARD_UPDATE_EMAIL || 'Update Email'}</button>
                </form>

                {/* Change Password */}
                <form className="dashboard-form" onSubmit={handlePasswordSave} style={{ marginTop: 32 }}>
                  <h3 className="dashboard-section-title">{lang.DASHBOARD_CHANGE_PASSWORD_TITLE || 'Change Password'}</h3>
                  <div className="form-group">
                    <label>{lang.DASHBOARD_CURRENT_PASSWORD || 'Current Password'}</label>
                    <input type="password" name="currentPassword" value={passwordData.currentPassword} onChange={handlePasswordChange} required />
                  </div>
                  <div className="form-group">
                    <label>{lang.DASHBOARD_NEW_PASSWORD || 'New Password'}</label>
                    <input type="password" name="newPassword" value={passwordData.newPassword} onChange={handlePasswordChange} required placeholder={lang.DASHBOARD_NEW_PASSWORD_PLACEHOLDER || 'Min 6 characters'} />
                  </div>
                  <div className="form-group">
                    <label>{lang.DASHBOARD_CONFIRM_PASSWORD || 'Confirm New Password'}</label>
                    <input type="password" name="confirmPassword" value={passwordData.confirmPassword} onChange={handlePasswordChange} required />
                  </div>
                  <button type="submit" className="btn btn-primary">{lang.DASHBOARD_CHANGE_PASSWORD || 'Change Password'}</button>
                  {message.text && <div className={`dashboard-message ${message.type}`}>{message.text}</div>}
                </form>

                {/* ===== TWO-FACTOR AUTHENTICATION ===== */}
                <TwoFactorPanel
                  user={user}
                  authHeaders={authHeaders}
                  refreshUser={refreshUser}
                  showMsg={showMsg}
                />
              </motion.div>
            )}

            {/* ==================== MEMBER MANAGEMENT (ky_thuat only) ==================== */}
            {activeTab === 'members' && can.members && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {/* Success / error banner (bulk edit, single edit, delete, import...) */}
                {message.text && <div className={`dashboard-message ${message.type}`}>{message.text}</div>}
                {!editingMember && (
                <>
                {/* Action bar */}
                <div className="admin-actions">
                  <button className="btn btn-primary btn-sm" onClick={() => { setShowAddForm(!showAddForm); setShowBulkImport(false); }}>
                    + {lang.DASHBOARD_ADD_MEMBER || 'Add Member'}
                  </button>
                  <button className="btn btn-outline btn-sm" onClick={() => { setShowBulkImport(!showBulkImport); setShowAddForm(false); }}>
                    {lang.DASHBOARD_BULK_IMPORT || 'Bulk Import'}
                  </button>
                  {selectedIds.length > 0 && (
                    <>
                      <button className="btn btn-outline btn-sm" onClick={() => { setShowBulkEdit(!showBulkEdit); setShowAddForm(false); setShowBulkImport(false); }}>
                        {lang.DASHBOARD_BULK_EDIT || 'Bulk Edit'} ({selectedIds.length})
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={handleDeleteSelected}>
                        {lang.DASHBOARD_DELETE_SELECTED || 'Delete'} ({selectedIds.length})
                      </button>
                    </>
                  )}
                  <span className="admin-count">{members.length} {lang.DASHBOARD_TOTAL_MEMBERS || 'members'}</span>
                </div>

                {/* Bulk Edit Panel */}
                {showBulkEdit && selectedIds.length > 0 && (
                  <div className="admin-form-card">
                    <h3>{lang.DASHBOARD_BULK_EDIT || 'Bulk Edit'} — {selectedIds.length} {lang.DASHBOARD_SELECTED || 'selected'}</h3>
                    <p className="admin-form-hint">
                      {lang.DASHBOARD_BULK_EDIT_HINT || 'Leave a field empty to keep existing values for those fields.'}
                    </p>
                    <div className="admin-form-grid">
                      <div className="form-group">
                        <label>{lang.DASHBOARD_GEN || 'Gen'}</label>
                        <input type="text" value={bulkEditData.gen} onChange={e => setBulkEditData({ ...bulkEditData, gen: e.target.value })} placeholder={lang.DASHBOARD_BULK_GEN_PH || 'e.g. 7'} />
                      </div>
                      <div className="form-group">
                        <label>{lang.DASHBOARD_POSITION || 'Cấp Thành Viên'}</label>
                        <select value={bulkEditData.position} onChange={e => setBulkEditData({ ...bulkEditData, position: e.target.value })}>
                          <option value="">-- {lang.DASHBOARD_KEEP || 'Keep existing'} --</option>
                          {Object.entries(POSITION_LABELS).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>{lang.DASHBOARD_NEW_PASSWORD || 'New Password'}</label>
                        <input type="text" value={bulkEditData.password} onChange={e => setBulkEditData({ ...bulkEditData, password: e.target.value })} placeholder={lang.DASHBOARD_PW_LEAVE_BLANK || 'Leave blank to keep current'} />
                      </div>
                      <div className="form-group">
                        <label>Ngày gia nhập CLB</label>
                        <DateInput
                          value={bulkEditData.joinDate}
                          onChange={(v) => setBulkEditData(s => ({ ...s, joinDate: v }))}
                        />
                      </div>
                      <div className="form-group form-group-full">
                        <label>Cấp / thu hồi Ban</label>
                        {!(isKyThuat || isBanDieuHanhFull) && (
                          <p className="form-hint" style={{ marginTop: 0, marginBottom: 6 }}>
                            Bạn chỉ được cấp/thu hồi Ban Đào Tạo &amp; Ban Sự Kiện. Ban Điều Hành / Kỹ Thuật bị khóa.
                          </p>
                        )}
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 6 }}>
                          {[
                            { key: 'ban_dieu_hanh', label: 'Ban Điều Hành', restricted: true },
                            { key: 'ban_dao_tao', label: 'Ban Đào Tạo', restricted: false },
                            { key: 'ban_su_kien', label: 'Ban Sự Kiện', restricted: false },
                            { key: 'ky_thuat', label: 'Kỹ Thuật', restricted: true }
                          ].map(({ key: banKey, label, restricted }) => {
                            const locked = restricted && !(isKyThuat || isBanDieuHanhFull);
                            const add = bulkEditData.addBans.includes(banKey);
                            const rem = bulkEditData.removeBans.includes(banKey);
                            return (
                              <div key={banKey} style={{ border: '1px solid var(--border-primary)', borderRadius: 8, padding: '8px 12px', minWidth: 200, opacity: locked ? 0.45 : 1 }}>
                                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                                  {locked && '🔒 '}{label}
                                </div>
                                <label className="checkbox-label" style={{ display: 'block' }}>
                                  <input type="checkbox" disabled={locked} checked={add} onChange={() => {
                                    setBulkEditData(s => ({
                                      ...s,
                                      addBans: add ? s.addBans.filter(x => x !== banKey) : [...s.addBans, banKey],
                                      removeBans: s.removeBans.filter(x => x !== banKey)
                                    }));
                                  }} />
                                  ➕ Cấp
                                </label>
                                <label className="checkbox-label" style={{ display: 'block' }}>
                                  <input type="checkbox" disabled={locked} checked={rem} onChange={() => {
                                    setBulkEditData(s => ({
                                      ...s,
                                      removeBans: rem ? s.removeBans.filter(x => x !== banKey) : [...s.removeBans, banKey],
                                      addBans: s.addBans.filter(x => x !== banKey)
                                    }));
                                  }} />
                                  ➖ Thu hồi
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    <div className="admin-form-actions">
                      <button type="button" className="btn btn-primary btn-sm" onClick={handleBulkEdit}>
                        {lang.DASHBOARD_APPLY || 'Apply to Selected'}
                      </button>
                      <button type="button" className="btn btn-outline btn-sm" onClick={() => { setShowBulkEdit(false); setBulkEditData({ gen: '', position: '', password: '', joinDate: '', addBans: [], removeBans: [] }); }}>
                        {lang.DASHBOARD_CANCEL || 'Cancel'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Add Single Member Form */}
                {showAddForm && (
                  <div className="admin-form-card">
                    <h3>{lang.DASHBOARD_ADD_MEMBER || 'Add Member'}</h3>
                    <form className="admin-form" onSubmit={handleAddMember}>
                      <div className="admin-form-grid">
                        <div className="form-group">
                          <label>{lang.DASHBOARD_FULL_NAME || 'Full Name'} *</label>
                          <input type="text" value={newMember.fullName} onChange={e => setNewMember({...newMember, fullName: e.target.value})} required />
                        </div>
                        <div className="form-group">
                          <label>{lang.DASHBOARD_USERNAME_REQUIRED || 'Username *'}</label>
                          <input type="text" value={newMember.username} onChange={e => setNewMember({...newMember, username: e.target.value})} required />
                        </div>
                        <div className="form-group">
                          <label>{lang.DASHBOARD_PHONE || 'Phone'}</label>
                          <input type="text" value={newMember.phone} onChange={e => setNewMember({...newMember, phone: e.target.value})} />
                        </div>
                        <div className="form-group">
                          <label>{lang.DASHBOARD_EMAIL || 'Email'}</label>
                          <input type="email" value={newMember.email} onChange={e => setNewMember({...newMember, email: e.target.value})} />
                        </div>
                        <div className="form-group">
                          <label>{lang.DASHBOARD_CLASS || 'Class'}</label>
                          <input type="text" value={newMember.studentClass} onChange={e => setNewMember({...newMember, studentClass: e.target.value})} />
                        </div>
                        <div className="form-group">
                          <label>{lang.DASHBOARD_STUDENT_ID || 'Student ID'}</label>
                          <input type="text" value={newMember.studentId} onChange={e => setNewMember({...newMember, studentId: e.target.value})} />
                        </div>
                        <div className="form-group">
                          <label>{lang.DASHBOARD_GEN || 'Gen'}</label>
                          <input type="text" value={newMember.gen} onChange={e => setNewMember({...newMember, gen: e.target.value})} />
                        </div>
                        <div className="form-group">
                          <label>{lang.DASHBOARD_JOIN_DATE_LABEL || 'Ngày gia nhập CLB'}</label>
                          <DateInput value={newMember.joinDate} onChange={v => setNewMember({...newMember, joinDate: v})} />
                        </div>
                        <div className="form-group">
                          <label>{lang.LOGIN_FORM_PASSWORD || 'Password'} *</label>
                          <input type="text" value={newMember.password} onChange={e => setNewMember({...newMember, password: e.target.value})} required minLength={6} />
                        </div>
                        <div className="form-group">
                          <label>{lang.DASHBOARD_CTF_FIELD || 'CTF Field'}</label>
                          <select value={newMember.ctfField} onChange={e => setNewMember({...newMember, ctfField: e.target.value})}>
                            <option value="">Chọn mảng CTF</option>
                            {Object.entries(CTF_FIELDS).map(([key, label]) => (
                              <option key={key} value={key}>{label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>{lang.DASHBOARD_POSITION || 'Cấp Thành Viên'}</label>
                          <select value={newMember.position} onChange={e => setNewMember({...newMember, position: e.target.value})}>
                            {Object.entries(POSITION_LABELS).map(([key, label]) => (
                              <option key={key} value={key}>{label}</option>
                            ))}
                          </select>
                        </div>
                        {newMember.role.includes('ban_dieu_hanh') && (
                          <div className="form-group">
                            <label>{lang.DASHBOARD_TITLE || 'Chức Vụ'}</label>
                            <select value={newMember.title || ''} onChange={e => setNewMember({...newMember, title: e.target.value})}>
                              <option value="">-- Chọn chức vụ --</option>
                              {Object.entries(TITLE_LABELS).map(([key, label]) => (
                                <option key={key} value={key}>{label}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div className="form-group form-group-full">
                          <label>{lang.DASHBOARD_ROLE || 'Role'}</label>
                          <div className="role-checkboxes">
                            {Object.entries(ROLE_LABELS).map(([key, label]) => (
                              <label key={key} className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={newMember.role.includes(key)}
                                  onChange={() => handleNewMemberRoleToggle(key)}
                                />
                                {label}
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="admin-form-actions">
                        <button type="submit" className="btn btn-primary btn-sm">{lang.DASHBOARD_SAVE || 'Save'}</button>
                        <button type="button" className="btn btn-outline btn-sm" onClick={() => setShowAddForm(false)}>{lang.DASHBOARD_CANCEL || 'Cancel'}</button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Bulk Import */}
                {showBulkImport && (
                  <div className="admin-form-card">
                    <h3>{lang.DASHBOARD_BULK_IMPORT || 'Bulk Import'}</h3>
                    <p className="admin-form-hint">
                      {lang.DASHBOARD_BULK_HINT || 'Upload .xlsx or .csv file. Column order: Full Name, Username, Phone, Email, Class, Student ID, Course'}
                    </p>
                    <div className="admin-form-grid">
                      <div className="form-group">
                        <label>{lang.DASHBOARD_BULK_FILE || 'Select File'}</label>
                        <input type="file" ref={fileInputRef} accept=".xlsx,.xls,.csv" className="file-input" />
                      </div>
                      <div className="form-group">
                        <label>{lang.DASHBOARD_BULK_DEFAULT_PASS || 'Default Password'}</label>
                        <input type="text" value={bulkPassword} onChange={e => setBulkPassword(e.target.value)} />
                      </div>
                    </div>
                    <div className="admin-form-actions">
                      <button type="button" className="btn btn-primary btn-sm" onClick={handleBulkImport}>{lang.DASHBOARD_BULK_UPLOAD || 'Upload & Import'}</button>
                      <button type="button" className="btn btn-outline btn-sm" onClick={() => { setShowBulkImport(false); setBulkResult(null); }}>{lang.DASHBOARD_CANCEL || 'Cancel'}</button>
                    </div>
                    {bulkResult && (
                      <div className="bulk-result">
                        <p className="bulk-result-success">{bulkResult.message}</p>
                        {bulkResult.skipped && bulkResult.skipped.length > 0 && <p className="bulk-result-warn">Skipped: {bulkResult.skipped.join(', ')}</p>}
                        {bulkResult.errors && bulkResult.errors.length > 0 && <p className="bulk-result-error">Errors: {bulkResult.errors.join(', ')}</p>}
                      </div>
                    )}
                  </div>
                )}
                </>
                )}

                {/* Edit Member Form */}
                {editingMember && (
                  <div className="admin-form-card edit-member-card">
                    <h3>{lang.DASHBOARD_EDIT_MEMBER || 'Edit Member'}: <span className="mono">{editingMember.username}</span></h3>
                    <form className="admin-form" onSubmit={handleSaveEdit}>
                      <div className="admin-form-grid">
                        <div className="form-group">
                          <label>{lang.DASHBOARD_FULL_NAME || 'Full Name'} *</label>
                          <input type="text" name="fullName" value={editingMember.fullName || ''} onChange={handleEditChange} required />
                        </div>
                        <div className="form-group">
                          <label>{lang.DASHBOARD_USERNAME_REQUIRED || 'Username *'}</label>
                          <input type="text" name="username" value={editingMember.username || ''} onChange={handleEditChange} required />
                        </div>
                        <div className="form-group">
                          <label>{lang.DASHBOARD_EMAIL || 'Email'}</label>
                          <input type="email" name="email" value={editingMember.email || ''} onChange={handleEditChange} />
                        </div>
                        <div className="form-group">
                          <label>{lang.DASHBOARD_PHONE || 'Phone'}</label>
                          <input type="text" name="phone" value={editingMember.phone || ''} onChange={handleEditChange} />
                        </div>
                        <div className="form-group">
                          <label>{lang.DASHBOARD_CLASS || 'Class'}</label>
                          <input type="text" name="studentClass" value={editingMember.studentClass || ''} onChange={handleEditChange} />
                        </div>
                        <div className="form-group">
                          <label>{lang.DASHBOARD_STUDENT_ID || 'Student ID'}</label>
                          <input type="text" name="studentId" value={editingMember.studentId || ''} onChange={handleEditChange} />
                        </div>
                        <div className="form-group">
                          <label>{lang.DASHBOARD_GEN || 'Gen'}</label>
                          <input type="text" name="gen" value={editingMember.gen || ''} onChange={handleEditChange} />
                        </div>
                        <div className="form-group">
                          <label>{lang.DASHBOARD_JOIN_DATE_LABEL || 'Ngày gia nhập CLB'}</label>
                          <DateInput
                            value={editingMember.joinDate ? String(editingMember.joinDate).slice(0, 10) : ''}
                            onChange={(v) => setEditingMember(prev => ({ ...prev, joinDate: v }))}
                          />
                        </div>
                        <div className="form-group">
                          <label>{lang.DASHBOARD_NEW_PASSWORD || 'New Password'}</label>
                          <input type="password" name="password" value={editingMember.password || ''} onChange={handleEditChange} placeholder={lang.DASHBOARD_PW_LEAVE_BLANK || 'Leave blank to keep current'} />
                        </div>
                        <div className="form-group">
                          <label>{lang.DASHBOARD_POSITION || 'Position'}</label>
                          <select name="position" value={editingMember.position} onChange={handleEditChange}>
                            {Object.entries(POSITION_LABELS).map(([k, v]) => (
                              <option key={k} value={k}>{v}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>{lang.DASHBOARD_TITLE || 'Title'}</label>
                          <select name="title" value={editingMember.title} onChange={handleEditChange}>
                            <option value="">-- {lang.DASHBOARD_NONE || 'None'} --</option>
                            {Object.entries(TITLE_LABELS).map(([k, v]) => (
                              <option key={k} value={k}>{v}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>{lang.DASHBOARD_STATUS || 'Status'}</label>
                          <select name="status" value={editingMember.status} onChange={handleEditChange}>
                            <option value="active">Hoạt Động</option>
                            <option value="inactive">Khoá</option>
                          </select>
                        </div>
                        <div className="form-group form-group-full">
                          <label>{lang.DASHBOARD_ROLE || 'Roles'}</label>
                          <div className="role-checkbox-group">
                            {Object.entries(ROLE_LABELS).map(([k, v]) => (
                              <label key={k} className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={editingMember.role.includes(k)}
                                  onChange={() => handleEditRoleToggle(k)}
                                />
                                {v}
                              </label>
                            ))}
                          </div>
                        </div>

                        <div className="form-group form-group-full">
                          <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>Lịch sử Gen (chức vụ / ban đã từng giữ)</span>
                            <button type="button" className="btn btn-outline btn-sm" onClick={addGenHistoryEntry}>+ Thêm Gen</button>
                          </label>
                          <div className="gen-history-list">
                            {(editingMember.genHistory || []).map((h, i) => (
                              <div key={i} className="gen-history-row">
                                <div className="gen-history-grid">
                                  <div className="form-group">
                                    <label>Gen *</label>
                                    <input
                                      type="text"
                                      value={h.gen}
                                      onChange={e => updateGenHistoryEntry(i, { gen: e.target.value.replace(/\D/g, '') })}
                                      placeholder="VD: 6"
                                    />
                                  </div>
                                  <div className="form-group">
                                    <label>Cấp bậc tại gen đó</label>
                                    <select
                                      value={h.position}
                                      onChange={e => updateGenHistoryEntry(i, { position: e.target.value })}
                                    >
                                      <option value="">-- Không đổi --</option>
                                      {Object.entries(POSITION_LABELS).map(([k, v]) => (
                                        <option key={k} value={k}>{v}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div className="form-group">
                                    <label>Chức vụ tại gen đó</label>
                                    <select
                                      value={h.title}
                                      onChange={e => updateGenHistoryEntry(i, { title: e.target.value })}
                                    >
                                      <option value="">-- Không có --</option>
                                      {Object.entries(TITLE_LABELS).map(([k, v]) => (
                                        <option key={k} value={k}>{v}</option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                                <div className="form-group">
                                  <label>Ban (tại gen đó)</label>
                                  <div className="role-checkbox-group">
                                    {Object.entries(ROLE_LABELS).map(([k, v]) => (
                                      <label key={k} className="checkbox-label">
                                        <input
                                          type="checkbox"
                                          checked={(h.role || []).includes(k)}
                                          onChange={() => toggleGenHistoryRole(i, k)}
                                        />
                                        {v}
                                      </label>
                                    ))}
                                  </div>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                  <button type="button" className="btn btn-danger btn-sm" onClick={() => removeGenHistoryEntry(i)}>
                                    Xoá gen này
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="admin-form-actions">
                        <button type="submit" className="btn btn-primary btn-sm">{lang.DASHBOARD_SAVE || 'Save Changes'}</button>
                        <button type="button" className="btn btn-outline btn-sm" onClick={handleCancelEdit}>{lang.DASHBOARD_CANCEL || 'Cancel'}</button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Members Table — hidden while the bulk-import or add
                    forms are open so the admin isn't distracted by a huge
                    list underneath the upload widget. */}
                {!editingMember && !showBulkImport && !showAddForm && (
                <>
                <div className="member-search-row">
                  <input
                    type="search"
                    className="member-search-input"
                    value={memberSearch}
                    onChange={e => setMemberSearch(e.target.value)}
                    placeholder="Nhập nội dung tìm kiếm"
                  />
                  <select
                    className="member-search-input"
                    style={{ maxWidth: 200 }}
                    value={memberFilterBan}
                    onChange={e => setMemberFilterBan(e.target.value)}
                    aria-label="Lọc theo ban"
                  >
                    <option value="">Tất cả ban</option>
                    <option value="ban_dieu_hanh">Ban Điều Hành</option>
                    <option value="ban_dao_tao">Ban Đào Tạo</option>
                    <option value="ban_su_kien">Ban Sự Kiện</option>
                    <option value="ky_thuat">Kỹ Thuật</option>
                  </select>
                  <select
                    className="member-search-input"
                    style={{ maxWidth: 200 }}
                    value={memberFilterPosition}
                    onChange={e => setMemberFilterPosition(e.target.value)}
                    aria-label="Lọc theo cấp thành viên"
                  >
                    <option value="">Tất cả cấp</option>
                    <option value="mam_non">Mầm Non</option>
                    <option value="tap_su">Tập Sự</option>
                    <option value="culi_cap_cao">Culi Cấp Cao</option>
                    <option value="bo_lao">Bô Lão</option>
                    <option value="founder">Founder</option>
                  </select>
                  <select
                    className="member-search-input"
                    style={{ maxWidth: 180 }}
                    value={memberFilterGen}
                    onChange={e => setMemberFilterGen(e.target.value)}
                    aria-label="Lọc theo khóa Gen"
                  >
                    <option value="">Tất cả khóa (Gen)</option>
                    {memberGenOptions.map(gen => <option key={gen} value={gen}>Gen {gen}</option>)}
                  </select>
                  {(memberSearch || memberFilterBan || memberFilterPosition || memberFilterGen) && (
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={() => { setMemberSearch(''); setMemberFilterBan(''); setMemberFilterPosition(''); setMemberFilterGen(''); }}
                    >
                      Xoá bộ lọc
                    </button>
                  )}
                  {(memberSearch || memberFilterBan || memberFilterPosition || memberFilterGen) && (
                    <span className="form-hint" style={{ marginLeft: 8 }}>
                      Tìm thấy <strong>{filteredMembers.length}</strong> / {members.length}
                    </span>
                  )}
                </div>
                <Paginator total={membersPg.total} page={membersPg.page} pageSize={membersPg.pageSize} onChange={membersPg.onChange} position="top" />
                <div className="admin-table-wrap members-table-wrap">
                  <table className="admin-table members-admin-table">
                    <thead>
                      <tr>
                        <th><input type="checkbox" checked={selectedIds.length === filteredMembers.length && filteredMembers.length > 0} onChange={toggleSelectAll} /></th>
                        <th>#</th>
                        <th>{lang.DASHBOARD_FULL_NAME || 'Full Name'}</th>
                        <th>Username</th>
                        <th>{lang.DASHBOARD_EMAIL || 'Email'}</th>
                        <th>{lang.DASHBOARD_PHONE || 'Phone'}</th>
                        <th>{lang.DASHBOARD_CLASS || 'Class'}</th>
                        <th>{lang.DASHBOARD_STUDENT_ID || 'Student ID'}</th>
                        <th>{lang.DASHBOARD_GEN || 'Gen'}</th>
                        <th>Ngày gia nhập</th>
                        <th>{lang.DASHBOARD_ROLE || 'Role'}</th>
                        <th>{lang.DASHBOARD_POSITION || 'Cấp Thành Viên'}</th>
                        <th>{lang.DASHBOARD_TITLE || 'Chức Vụ'}</th>
                        <th>{lang.DASHBOARD_ACTIONS || 'Actions'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {membersPg.paged.map((m, i) => {
                        const realIndex = (membersPg.pageSize === 0 ? 0 : (membersPg.page - 1) * membersPg.pageSize) + i;
                        return (
                        <tr key={m.id} className={selectedIds.includes(m.id) ? 'selected' : ''}>
                          <td><input type="checkbox" checked={selectedIds.includes(m.id)} onChange={() => toggleSelect(m.id)} /></td>
                          <td>{realIndex + 1}</td>
                          <td>{m.fullName}</td>
                          <td><span className="mono">{m.username}</span></td>
                          <td>{m.email || '-'}</td>
                          <td>{m.phone || '-'}</td>
                          <td>{m.studentClass || '-'}</td>
                          <td>{m.studentId || '-'}</td>
                          <td>{m.gen ? `Gen ${m.gen}` : '-'}</td>
                          <td>{m.joinDate ? fmtDMY(m.joinDate) : '-'}</td>
                          <td>{getRoleBadges(m.role)}</td>
                          <td><span className="badge badge-position">{POSITION_LABELS[m.position] || m.position || '-'}</span></td>
                          <td>{m.title ? <span className="badge badge-title">{TITLE_LABELS[m.title] || m.title}</span> : '-'}</td>
                          <td>
                            <button className="btn btn-outline btn-sm" onClick={() => handleStartEdit(m)}>
                              {lang.DASHBOARD_EDIT || 'Edit'}
                            </button>
                          </td>
                        </tr>
                        );
                      })}
                      {filteredMembers.length === 0 && (
                        <tr><td colSpan="14" className="empty-row">{(memberSearch || memberFilterBan || memberFilterPosition || memberFilterGen) ? 'Không có thành viên nào khớp bộ lọc.' : (lang.DASHBOARD_NO_MEMBERS || 'No members found.')}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <Paginator total={membersPg.total} page={membersPg.page} pageSize={membersPg.pageSize} onChange={membersPg.onChange} position="bottom" />
                </>
                )}
              </motion.div>
            )}

            {/* ==================== ACHIEVEMENTS (ky_thuat only) ==================== */}
            {activeTab === 'achievements' && can.achievements && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {!showAchievementForm && !editingAchievement && (
                  <div className="admin-actions">
                    <button className="btn btn-primary btn-sm" onClick={() => {
                      setShowAchievementForm(true);
                      setEditingAchievement(null);
                      setAchievementForm({ title: '', description: '', date: '', tier: 'notable', rank: '', prize: '' });
                    }}>
                      + {lang.DASHBOARD_ADD_ACHIEVEMENT || 'Thêm Thành Tích'}
                    </button>
                  </div>
                )}

                {(showAchievementForm || editingAchievement) && (
                <div className="admin-form-card">
                  <h3>{editingAchievement
                    ? (lang.DASHBOARD_EDIT_ACHIEVEMENT || 'Sửa Thành Tích')
                    : (lang.DASHBOARD_ADD_ACHIEVEMENT || 'Add Achievement')}</h3>
                  <form className="admin-form" onSubmit={handleAddAchievement}>
                    <div className="admin-form-grid">
                      <div className="form-group">
                        <label>{lang.DASHBOARD_ACHIEVEMENT_NAME || 'Tên thành tích'} *</label>
                        <input type="text" value={achievementForm.title} onChange={e => setAchievementForm({...achievementForm, title: e.target.value})} required />
                      </div>
                      <div className="form-group">
                        <label>{lang.DASHBOARD_DATE || 'Date'} *</label>
                        <DateInput value={achievementForm.date} onChange={v => setAchievementForm({...achievementForm, date: v})} required />
                      </div>
                      <div className="form-group form-group-full">
                        <label>{lang.DASHBOARD_DESCRIPTION || 'Description'}</label>
                        <textarea value={achievementForm.description} onChange={e => setAchievementForm({...achievementForm, description: e.target.value})} rows="3" />
                      </div>
                      <div className="form-group">
                        <label>Tier / Hạng *</label>
                        <select value={achievementForm.tier} onChange={e => setAchievementForm({...achievementForm, tier: e.target.value})}>
                          <option value="champions">🏆 Champions (#1)</option>
                          <option value="runners_up">🥈 Runners-Up (#2)</option>
                          <option value="bronze">🥉 Bronze (#3)</option>
                          <option value="notable">⭐ Notable Placements</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Rank (thứ hạng số)</label>
                        <input type="number" min="1" value={achievementForm.rank} onChange={e => setAchievementForm({...achievementForm, rank: e.target.value})} placeholder="VD: 1, 2, 3, 5..." />
                      </div>
                      <div className="form-group">
                        <label>Prize / Giải thưởng</label>
                        <input type="text" value={achievementForm.prize} onChange={e => setAchievementForm({...achievementForm, prize: e.target.value})} placeholder="VD: $15,000" />
                      </div>
                      <div className="form-group form-group-full">
                        <label>{lang.DASHBOARD_IMAGES || 'Images'}{editingAchievement ? ` (${lang.DASHBOARD_IMAGES_APPEND_HINT || 'ảnh mới sẽ được thêm vào danh sách hiện có'})` : ''}</label>
                        <input type="file" ref={achievementImagesRef} accept="image/*" multiple className="file-input" />
                      </div>
                    </div>
                    <div className="admin-form-actions">
                      <button type="submit" className="btn btn-primary btn-sm">
                        {editingAchievement
                          ? (lang.DASHBOARD_UPDATE || 'Update')
                          : (lang.DASHBOARD_ADD || 'Add')}
                      </button>
                      <button type="button" className="btn btn-outline btn-sm" onClick={handleCancelEditAchievement}>
                        {lang.DASHBOARD_CANCEL || 'Cancel'}
                      </button>
                    </div>
                  </form>
                </div>
                )}

                {!showAchievementForm && !editingAchievement && (
                <div className="achievements-list">
                  <h3>Thành Tích</h3>
                  {achievements.length === 0 && <p className="empty-text">{lang.DASHBOARD_NO_ACHIEVEMENTS || 'No achievements yet.'}</p>}
                  {achievements.length > 0 && (
                    <Paginator total={achievementsPg.total} page={achievementsPg.page} pageSize={achievementsPg.pageSize} onChange={achievementsPg.onChange} position="top" />
                  )}
                  {achievementsPg.paged.map((ach) => (
                    <div className="achievement-item" key={ach.id || ach._id}>
                      <div className="achievement-info">
                        <h4>{ach.title}</h4>
                        <p className="achievement-date">
                          {ach.tier === 'champions' ? '🏆' : ach.tier === 'runners_up' ? '🥈' : ach.tier === 'bronze' ? '🥉' : '⭐'}
                          {' '}{ach.tier === 'champions' ? 'Champions' : ach.tier === 'runners_up' ? 'Runners-Up' : ach.tier === 'bronze' ? 'Bronze' : 'Notable'}
                          {ach.rank ? ` #${ach.rank}` : ''}
                          {ach.prize ? ` — ${ach.prize}` : ''}
                          {ach.date ? ` — ${fmtDMY(ach.date)}` : ''}
                        </p>
                        {ach.description && <p>{ach.description}</p>}
                        {ach.images && ach.images.length > 0 && (
                          <div className="achievement-images">
                            {ach.images.map((img, idx) => {
                              const src = typeof img === 'string'
                                ? (img.startsWith('http') || img.startsWith('/') ? img : `/uploads/achievements/${img}`)
                                : (img.url || '');
                              return <img key={idx} src={src} alt={ach.title} className="achievement-thumb" />;
                            })}
                          </div>
                        )}
                      </div>
                      <div className="achievement-actions" style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-outline btn-sm" onClick={() => handleStartEditAchievement(ach)}>
                          {lang.DASHBOARD_EDIT || 'Edit'}
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteAchievement(ach.id || ach._id)}>
                          {lang.DASHBOARD_DELETE || 'Delete'}
                        </button>
                      </div>
                    </div>
                  ))}
                  {achievements.length > 0 && (
                    <Paginator total={achievementsPg.total} page={achievementsPg.page} pageSize={achievementsPg.pageSize} onChange={achievementsPg.onChange} position="bottom" />
                  )}
                </div>
                )}
              </motion.div>
            )}

            {/* ==================== BLOG MANAGEMENT ==================== */}
            {activeTab === 'blogs' && canManageBlogs && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {!blogEditing && (
                <div className="admin-actions">
                  <button className="btn btn-primary btn-sm" onClick={() => {
                    setShowBlogForm(!showBlogForm);
                    setBlogEditing(null);
                    setBlogForm({ title: '', content: '', tags: '' });
                  }}>
                    + {lang.DASHBOARD_NEW_BLOG || 'New Blog Post'}
                  </button>
                </div>
                )}

                {showBlogForm && (
                  <div className="admin-form-card">
                    <h3>{blogEditing ? (lang.DASHBOARD_EDIT_BLOG || 'Edit Blog') : (lang.DASHBOARD_NEW_BLOG || 'New Blog Post')}</h3>
                    <form className="admin-form" onSubmit={handleBlogSubmit}>
                      <div className="form-group">
                        <label>{lang.COMMON_TITLE || 'Tiêu đề *'}</label>
                        <input type="text" value={blogForm.title} onChange={e => setBlogForm({...blogForm, title: e.target.value})} required />
                      </div>
                      <div className="form-group">
                        <label>{lang.DASHBOARD_TAGS || 'Tags'}</label>
                        <input type="text" value={blogForm.tags} onChange={e => setBlogForm({...blogForm, tags: e.target.value})} placeholder={lang.DASHBOARD_TAGS_PLACEHOLDER || 'Comma-separated tags'} />
                      </div>
                      <div className="form-group form-group-full">
                        <label>{lang.DASHBOARD_CONTENT || 'Content'} (Markdown)</label>
                        <div className="markdown-editor">
                          <div className="markdown-editor-pane">
                            <textarea
                              value={blogForm.content}
                              onChange={e => setBlogForm({...blogForm, content: e.target.value})}
                              rows="16"
                              placeholder={lang.DASHBOARD_MARKDOWN_PLACEHOLDER || 'Write your blog post in Markdown...'}
                            />
                          </div>
                          <div className="markdown-preview-pane">
                            <div className="markdown-preview-label">{lang.DASHBOARD_PREVIEW || 'Preview'}</div>
                            <div className="markdown-preview-content">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {blogForm.content || '*Nothing to preview*'}
                              </ReactMarkdown>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="admin-form-actions">
                        <button type="submit" className="btn btn-primary btn-sm">
                          {blogEditing ? (lang.DASHBOARD_UPDATE || 'Update') : (lang.DASHBOARD_PUBLISH || 'Publish')}
                        </button>
                        <button type="button" className="btn btn-outline btn-sm" onClick={() => {
                          setShowBlogForm(false);
                          setBlogEditing(null);
                          setBlogForm({ title: '', content: '', tags: '' });
                        }}>{lang.DASHBOARD_CANCEL || 'Cancel'}</button>
                      </div>
                    </form>
                  </div>
                )}

                <div className="blogs-list">
                  {!blogEditing && !showBlogForm && (
                    <h3>Blog</h3>
                  )}
                  {!blogEditing && !showBlogForm && blogs.length === 0 && <p className="empty-text">{lang.DASHBOARD_NO_BLOGS || 'No blog posts yet.'}</p>}
                  {!blogEditing && !showBlogForm && blogs.length > 0 && (
                    <Paginator total={blogsPg.total} page={blogsPg.page} pageSize={blogsPg.pageSize} onChange={blogsPg.onChange} position="top" />
                  )}
                  {!blogEditing && !showBlogForm && blogsPg.paged.map((blog) => (
                    <div className="blog-item" key={blog.id || blog._id}>
                      <div className="blog-item-info">
                        <h4>{blog.title}</h4>
                        <p className="blog-item-meta">{blog.createdAt ? fmtDMY(blog.createdAt) : ''} {blog.tags && <span className="blog-tags">{blog.tags}</span>}</p>
                        <p className="blog-item-excerpt">{(blog.content || '').substring(0, 150)}...</p>
                      </div>
                      <div className="blog-item-actions">
                        <button className="btn btn-outline btn-sm" onClick={() => handleEditBlog(blog)}>
                          {lang.DASHBOARD_EDIT || 'Edit'}
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteBlog(blog.id || blog._id)}>
                          {lang.DASHBOARD_DELETE || 'Delete'}
                        </button>
                      </div>
                    </div>
                  ))}
                  {!blogEditing && !showBlogForm && blogs.length > 0 && (
                    <Paginator total={blogsPg.total} page={blogsPg.page} pageSize={blogsPg.pageSize} onChange={blogsPg.onChange} position="bottom" />
                  )}
                </div>
              </motion.div>
            )}

            {/* ==================== RECRUITMENT CAMPAIGNS ==================== */}
            {activeTab === 'recruitment_campaigns' && can.recruitment && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="recruit-toggle-section">
                  <h3>Đợt tuyển theo Gen</h3>
                  <div className="recruit-toggle-row">
                    <select value={selectedCampaignId} onChange={e => fetchRecruitment(e.target.value)}>
                      {recruitmentCampaigns.map(c => <option key={c.id} value={c.id}>{c.name} {c.gen ? `(Gen ${c.gen})` : ''}</option>)}
                    </select>
                    <span className={`recruit-status ${recruitmentOpen ? 'open' : 'closed'}`}>
                      {recruitmentOpen ? 'Đang mở' : 'Đã đóng'}
                    </span>
                    {can.recruitmentCanToggle && (
                      <button className={`btn btn-sm ${recruitmentOpen ? 'btn-danger' : 'btn-primary'}`} onClick={handleToggleRecruitment}>
                        {recruitmentOpen ? 'Đóng đơn' : 'Mở đơn'}
                      </button>
                    )}
                  </div>
                </div>
                {can.recruitmentCanToggle && (
                  <form className="admin-form-card" onSubmit={handleCreateCampaign}>
                    <h3>Tạo đợt tuyển mới</h3>
                    <div className="admin-form-grid">
                      <div className="form-group"><label>Tên đợt</label><input value={campaignForm.name} onChange={e => setCampaignForm({ ...campaignForm, name: e.target.value })} placeholder="Tuyển thành viên Gen 8" required /></div>
                      <div className="form-group"><label>Gen</label><input value={campaignForm.gen} onChange={e => setCampaignForm({ ...campaignForm, gen: e.target.value })} placeholder="8" required /></div>
                      <div className="form-group"><label>Bộ câu hỏi dùng lại</label><select value={campaignForm.templateId} onChange={e => setCampaignForm({ ...campaignForm, templateId: e.target.value })}><option value="">Form trống</option>{recruitmentTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select></div>
                    </div>
                    <button className="btn btn-primary" type="submit">+ Tạo đợt tuyển</button>
                  </form>
                )}
                <div className="recruitment-campaign-grid">
                  {recruitmentCampaigns.map(c => (
                    <div key={c.id} className={`recruitment-campaign-card ${selectedCampaignId === c.id ? 'active' : ''}`}>
                      <button className="recruitment-campaign-main" onClick={() => fetchRecruitment(c.id)}>
                        <span className={`recruit-status ${c.isOpen ? 'open' : 'closed'}`}>{c.isOpen ? 'Đang mở' : 'Đã đóng'}</span>
                        <strong>{c.name}</strong><small>{c.gen ? `Gen ${c.gen}` : 'Dữ liệu cũ'}</small>
                      </button>
                      {can.recruitmentCanToggle && <div className="recruitment-campaign-actions"><button className="btn btn-outline btn-sm" onClick={() => setEditingCampaign({ ...c })}>Sửa</button><button className="btn btn-danger btn-sm" onClick={() => handleDeleteCampaign(c)}>Xóa</button></div>}
                    </div>
                  ))}
                </div>
                {editingCampaign && <form className="admin-form-card" onSubmit={handleUpdateCampaign}><div className="submissions-header"><h3>Sửa đợt tuyển</h3><button type="button" className="btn btn-outline btn-sm" onClick={() => setEditingCampaign(null)}>Đóng</button></div><div className="admin-form-grid"><div className="form-group"><label>Tiêu đề</label><input value={editingCampaign.name} onChange={e => setEditingCampaign({ ...editingCampaign, name: e.target.value })} required /></div><div className="form-group"><label>Gen</label><input value={editingCampaign.gen} onChange={e => setEditingCampaign({ ...editingCampaign, gen: e.target.value })} required /></div></div><button className="btn btn-primary" type="submit">Lưu thay đổi</button></form>}
              </motion.div>
            )}

            {/* ==================== RECRUITMENT QUESTIONS ==================== */}
            {activeTab === 'recruitment_questions' && can.recruitmentCanToggle && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="admin-form-card">
                  <div className="submissions-header"><h3>{editingTemplateId ? 'Chỉnh sửa bộ câu hỏi mẫu' : `Bộ câu hỏi · ${recruitmentCampaigns.find(c => c.id === selectedCampaignId)?.name || ''}`}</h3><div className="recruitment-question-source"><select value={editingTemplateId ? `template:${editingTemplateId}` : `campaign:${selectedCampaignId}`} onChange={e => { const [kind, id] = e.target.value.split(':'); if (kind === 'template') handleLoadTemplateEditor(id); else { setEditingTemplateId(''); setEditingTemplateName(''); fetchRecruitment(id); } }}><optgroup label="Form theo đợt">{recruitmentCampaigns.map(c => <option key={c.id} value={`campaign:${c.id}`}>{c.name}</option>)}</optgroup><optgroup label="Bộ câu hỏi đã lưu">{recruitmentTemplates.map(t => <option key={t.id} value={`template:${t.id}`}>{t.name}</option>)}</optgroup></select></div></div>
                  {editingTemplateId && <div className="recruitment-template-toolbar"><div className="form-group"><label>Tên bộ câu hỏi mẫu</label><input value={editingTemplateName} onChange={e => setEditingTemplateName(e.target.value)} /></div><button className="btn btn-danger btn-sm" onClick={handleDeleteTemplate}>Xóa bộ câu hỏi mẫu</button></div>}
                  {recruitQuestions.map((q, qIndex) => (
                    <div className={`question-card recruitment-sortable-question ${dragQuestionIndex === qIndex ? 'dragging' : ''}`} key={q.id || qIndex}
                      onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                      onDrop={e => { e.preventDefault(); handleQuestionDrop(qIndex); }}>
                      <div className="question-header"><span className="question-drag-handle" draggable="true" title="Kéo để sắp xếp"
                        onDragStart={e => { setDragQuestionIndex(qIndex); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(qIndex)); }}
                        onDragEnd={() => setDragQuestionIndex(null)} aria-label={`Kéo câu ${qIndex + 1} để sắp xếp`}>⠿</span><span className="question-number">#{qIndex + 1}</span><div className="question-order-actions"><button className="btn btn-outline btn-sm" disabled={qIndex === 0} onClick={() => moveRecruitQuestion(qIndex, qIndex - 1)} aria-label="Đưa câu hỏi lên">↑</button><button className="btn btn-outline btn-sm" disabled={qIndex === recruitQuestions.length - 1} onClick={() => moveRecruitQuestion(qIndex, qIndex + 1)} aria-label="Đưa câu hỏi xuống">↓</button><button className="btn btn-danger btn-sm" onClick={() => handleRemoveQuestion(qIndex)}>Xóa</button></div></div>
                      <div className="admin-form-grid">
                        <div className="form-group"><label>Câu hỏi</label><input value={q.text} onChange={e => handleQuestionChange(qIndex, 'text', e.target.value)} /></div>
                        <div className="form-group"><label>Loại</label><select value={q.type} onChange={e => handleQuestionChange(qIndex, 'type', e.target.value)}><option value="text">Văn bản</option><option value="multiple_choice">Chọn một đáp án</option></select></div>
                        <label className="checkbox-label"><input type="checkbox" checked={!!q.required} onChange={e => handleQuestionChange(qIndex, 'required', e.target.checked)} /> Bắt buộc</label>
                      </div>
                      {q.type === 'multiple_choice' && <div className="mc-options">{(q.options || []).map((opt, oi) => <div className="mc-option-row" key={oi}><input value={opt} onChange={e => handleOptionChange(qIndex, oi, e.target.value)} /><button className="btn btn-danger btn-sm" onClick={() => handleRemoveOption(qIndex, oi)}>×</button></div>)}<button className="btn btn-outline btn-sm" onClick={() => handleAddOption(qIndex)}>+ Lựa chọn</button></div>}
                    </div>
                  ))}
                  <div className="admin-form-actions"><button className="btn btn-outline" onClick={handleAddQuestion}>+ Thêm câu hỏi</button>{editingTemplateId ? <><button className="btn btn-primary" onClick={handleUpdateTemplate}>Lưu tên và nội dung mẫu</button><button className="btn btn-danger" onClick={handleDeleteTemplate}>Xóa mẫu</button></> : <><button className="btn btn-primary" onClick={() => handleSaveQuestions('')}>Lưu cho đợt này</button><button className="btn btn-outline" onClick={() => { const n = window.prompt('Tên bộ câu hỏi dùng lại:'); if (n) handleSaveQuestions(n); }}>Lưu thành mẫu</button></>}</div>
                </div>
              </motion.div>
            )}

            {/* ==================== RECRUITMENT APPLICANTS ==================== */}
            {activeTab === 'recruitment_applicants' && can.recruitment && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="admin-form-card">
                  <div className="submissions-header"><h3>Hồ sơ ứng viên ({filteredSubmissions.length})</h3><div><button className="btn btn-outline btn-sm" onClick={handleExportSubmissions}>Export XLSX</button>{selectedSubmissionIds.length > 0 && <button className="btn btn-danger btn-sm" onClick={handleBulkDeleteSubmissions}>Xóa {selectedSubmissionIds.length}</button>}</div></div>
                  <div className="recruitment-status-stats">{Object.entries(recruitmentStatuses).map(([key, label]) => <button key={key} className={`recruitment-stat status-${key} ${recruitmentStatusFilter === key ? 'active' : ''}`} onClick={() => setRecruitmentStatusFilter(recruitmentStatusFilter === key ? '' : key)}><span>{label}</span><strong>{submissions.filter(s => (s.status || 'submitted') === key).length}</strong></button>)}</div>
                  <div className="recruitment-filters"><select value={selectedCampaignId} onChange={e => fetchRecruitment(e.target.value)}>{recruitmentCampaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select><input value={recruitmentSearch} onChange={e => setRecruitmentSearch(e.target.value)} placeholder="Tìm tên, email..." /><select value={recruitmentStatusFilter} onChange={e => setRecruitmentStatusFilter(e.target.value)}><option value="">Tất cả trạng thái</option>{Object.entries(recruitmentStatuses).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
                  <div className="admin-table-wrap"><table className="admin-table"><thead><tr><th></th><th>#</th><th>Tên</th><th>Email</th><th>Trạng thái</th><th>Ngày nộp</th><th>Xem</th></tr></thead><tbody>
                    {submissionsPg.paged.map((sub, i) => <tr key={sub.id}><td><input type="checkbox" checked={selectedSubmissionIds.includes(sub.id)} onChange={() => setSelectedSubmissionIds(p => p.includes(sub.id) ? p.filter(x => x !== sub.id) : [...p, sub.id])} /></td><td>{i + 1}</td><td>{sub.fullName || '-'}</td><td>{sub.email || '-'}</td><td><select className={`recruit-status-select status-${sub.status || 'submitted'}`} value={sub.status || 'submitted'} onChange={e => handleRecruitmentStatus(sub, e.target.value)}>{Object.entries(recruitmentStatuses).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></td><td>{sub.submittedAt ? fmtDMY(sub.submittedAt) : '-'}</td><td><button className="btn btn-outline btn-sm" onClick={() => setSelectedSubmission(sub)} aria-label="Xem câu trả lời">◉</button></td></tr>)}
                    {filteredSubmissions.length === 0 && <tr><td colSpan="7" className="empty-row">Không có hồ sơ phù hợp.</td></tr>}
                  </tbody></table></div>
                  {filteredSubmissions.length > 0 && <Paginator total={submissionsPg.total} page={submissionsPg.page} pageSize={submissionsPg.pageSize} onChange={submissionsPg.onChange} position="bottom" />}
                </div>
              </motion.div>
            )}

            {/* ==================== RECRUITMENT EMAIL ==================== */}
            {activeTab === 'recruitment_email' && can.recruitment && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="admin-form-card"><h3>Gửi Email kết quả CV/PV</h3><p className="form-hint">Chỉ dùng cho kết quả vòng CV và vòng PV. Qua vòng PV có thể cấp tài khoản ISP Club. Cấu hình được lưu trên trình duyệt này.</p>
                  <section className="recruitment-email-editor-card"><h4>1. Thiết lập người gửi</h4><div className="admin-form-grid"><div className="form-group"><label>Sender Name</label><input value={recruitmentEmailTemplate.senderName || ''} onChange={e => setRecruitmentEmailTemplate({ ...recruitmentEmailTemplate, senderName: e.target.value })} placeholder="Mặc định từ SMTP" /></div><div className="form-group"><label>Sender Email</label><input type="email" value={recruitmentEmailTemplate.senderEmail || ''} onChange={e => setRecruitmentEmailTemplate({ ...recruitmentEmailTemplate, senderEmail: e.target.value })} placeholder="Mặc định từ SMTP" /></div></div></section>
                  <section className="recruitment-email-editor-card recruitment-subject-card"><div className="recruitment-editor-heading"><div><span className="recruitment-editor-step">02</span><h4>Tiêu đề mẫu</h4></div><code>{renderRecruitmentPreview(recruitmentEmailTemplate.subject)}</code></div><input className="recruitment-subject-input" value={recruitmentEmailTemplate.subject} onChange={e => setRecruitmentEmailTemplate({ ...recruitmentEmailTemplate, subject: e.target.value })} placeholder="[ISP Club] Kết quả {status} - {fullName}" /></section>
                  <section className="recruitment-email-editor-card"><h4>3. Biến chung tùy chỉnh</h4><p className="form-hint">Biến hệ thống: {'{fullName}'}, {'{recipient_name}'}, {'{email}'}, {'{status}'}, {'{username}'}, {'{password}'}, {'{loginUrl}'}.</p><div className="recruitment-extra-list">{(recruitmentEmailTemplate.extras || []).map((row, i) => <div className="recruitment-extra-row" key={i}><input placeholder="key" value={row.key} onChange={e => setRecruitmentEmailTemplate({ ...recruitmentEmailTemplate, extras: recruitmentEmailTemplate.extras.map((x, j) => j === i ? { ...x, key: e.target.value } : x) })} /><input placeholder="value" value={row.value} onChange={e => setRecruitmentEmailTemplate({ ...recruitmentEmailTemplate, extras: recruitmentEmailTemplate.extras.map((x, j) => j === i ? { ...x, value: e.target.value } : x) })} /><button className="btn btn-outline btn-sm" onClick={() => setRecruitmentEmailTemplate({ ...recruitmentEmailTemplate, extras: recruitmentEmailTemplate.extras.filter((_, j) => j !== i) })}>×</button></div>)}<button className="btn btn-outline btn-sm" onClick={() => setRecruitmentEmailTemplate({ ...recruitmentEmailTemplate, extras: [...(recruitmentEmailTemplate.extras || []), { key: '', value: '' }] })}>+ Thêm biến</button></div></section>
                  <section className="recruitment-email-editor-card"><h4>4. Ảnh nhúng trong email</h4><p className="form-hint">Tải tối đa 10 ảnh. Chèn placeholder như <code>{'{image1}'}</code> vào thuộc tính src: <code>{'<img src="{image1}" alt="">'}</code>.</p><label className="btn btn-outline recruitment-image-upload">{recruitmentEmailImageUploading ? 'Đang tải...' : '+ Tải ảnh lên'}<input type="file" accept="image/png,image/jpeg,image/gif,image/webp,image/bmp" multiple hidden disabled={recruitmentEmailImageUploading || recruitmentEmailImages.length >= 10} onChange={handleRecruitmentImageUpload} /></label>{recruitmentEmailImages.length > 0 && <div className="recruitment-email-images">{recruitmentEmailImages.map((image, i) => <div className="recruitment-email-image" key={image.name}><img src={`/uploads/recruitment-email/${image.name}`} alt={`Email ${i + 1}`} /><code>{`{image${i + 1}}`}</code><button type="button" onClick={() => handleRecruitmentImageDelete(image)}>×</button></div>)}</div>}</section>
                  <section className="recruitment-email-editor-card"><div className="recruitment-editor-heading"><h4>5. Nội dung HTML mẫu</h4><button className="btn btn-outline btn-sm" onClick={() => setRecruitmentEmailPreview(v => !v)}>{recruitmentEmailPreview ? 'Ẩn preview' : 'Xem preview'}</button></div><div className={`recruitment-template-split ${recruitmentEmailPreview ? 'with-preview' : ''}`}><textarea rows="16" value={recruitmentEmailTemplate.html} onChange={e => setRecruitmentEmailTemplate({ ...recruitmentEmailTemplate, html: e.target.value })} />{recruitmentEmailPreview && <iframe title="Email preview" sandbox="" srcDoc={renderRecruitmentPreview(recruitmentEmailTemplate.html)} />}</div></section>
                  <section className="recruitment-email-editor-card"><h4>6. Gửi email test</h4><div className="recruitment-test-row"><input type="email" value={recruitmentTestEmail} onChange={e => { setRecruitmentTestEmail(e.target.value); if (recruitmentTestResult.type) setRecruitmentTestResult({ type: '', text: '' }); }} placeholder="email-test@example.com" /><button className="btn btn-outline" onClick={handleRecruitmentTestEmail} disabled={recruitmentTestSending}>{recruitmentTestSending ? 'Đang gửi...' : 'Gửi test'}</button></div>{recruitmentTestResult.text && <div className={`recruitment-test-result ${recruitmentTestResult.type}`} role="status" aria-live="polite">{recruitmentTestResult.type === 'success' ? '✓ ' : recruitmentTestResult.type === 'error' ? '✕ ' : ''}{recruitmentTestResult.text}</div>}</section>
                  <div className="recruitment-email-filters"><select value={selectedCampaignId} onChange={e => { setRecruitmentEmailSelectedIds([]); fetchRecruitment(e.target.value); }}>{recruitmentCampaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select><select value={recruitmentStatusFilter} onChange={e => { setRecruitmentStatusFilter(e.target.value); setRecruitmentEmailSelectedIds([]); }}><option value="">Tất cả trạng thái</option>{Object.entries(recruitmentStatuses).filter(([k]) => k !== 'submitted').map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select><input value={recruitmentEmailSearch} onChange={e => setRecruitmentEmailSearch(e.target.value)} placeholder="Tìm theo tên hoặc email..." /></div>
                  <div className="recruitment-bulk-toolbar"><label className="checkbox-label"><input type="checkbox" checked={recruitmentEmailRecipients.length > 0 && recruitmentEmailRecipients.every(sub => recruitmentEmailSelectedIds.includes(sub.id))} onChange={e => setRecruitmentEmailSelectedIds(e.target.checked ? recruitmentEmailRecipients.map(sub => sub.id) : [])} /> Chọn tất cả ({recruitmentEmailRecipients.length})</label><span>Đã chọn {recruitmentEmailSelectedIds.length}</span><button className="btn btn-primary" disabled={!recruitmentEmailSelectedIds.length || recruitmentBulkSending} onClick={handleRecruitmentBulkEmail}>{recruitmentBulkSending ? 'Đang gửi...' : `Gửi đồng thời (${recruitmentEmailSelectedIds.length})`}</button></div>
                  {recruitmentBulkResult.text && <div className={`recruitment-test-result ${recruitmentBulkResult.type}`} role="status" aria-live="polite">{recruitmentBulkResult.text}</div>}
                  <div className="recruitment-email-list">{recruitmentEmailRecipients.map(sub => <div className="recruitment-email-row" key={sub.id}><input type="checkbox" checked={recruitmentEmailSelectedIds.includes(sub.id)} onChange={() => setRecruitmentEmailSelectedIds(prev => prev.includes(sub.id) ? prev.filter(id => id !== sub.id) : [...prev, sub.id])} aria-label={`Chọn ${sub.fullName}`} /><div><strong>{sub.fullName}</strong><span>{sub.email} · {recruitmentStatuses[sub.status] || sub.status}</span></div><span>{sub.notification?.status === 'sent' ? 'Đã gửi' : 'Chưa gửi'}</span><button className="btn btn-primary btn-sm" onClick={() => setRecruitmentNotify({ ...sub, createAccount: false, username: String(sub.email || '').split('@')[0], password: '', subject: recruitmentEmailTemplate.subject, html: recruitmentEmailTemplate.html })}>Soạn/Gửi</button></div>)}{recruitmentEmailRecipients.length === 0 && <p className="empty-text">Không tìm thấy ứng viên phù hợp.</p>}</div>
                </div>
              </motion.div>
            )}

            {selectedSubmission && createPortal(
              <div className="recruitment-modal-overlay recruitment-cv-overlay" onClick={() => setSelectedSubmission(null)} role="presentation">
                <div className="recruitment-modal recruitment-cv-modal" role="dialog" aria-modal="true" aria-labelledby="recruitment-cv-title" onClick={e => e.stopPropagation()}>
                  <div className="recruitment-cv-header">
                    <div><span className="recruitment-cv-kicker">Hồ sơ ứng viên</span><h3 id="recruitment-cv-title">{selectedSubmission.fullName}</h3><p>{selectedSubmission.email}</p></div>
                    <button className="recruitment-cv-close" onClick={() => setSelectedSubmission(null)} aria-label="Đóng hồ sơ">×</button>
                  </div>
                  <div className="recruitment-cv-answers">
                    {Object.entries(selectedSubmission.answers || {}).map(([k, v]) => { const q = (selectedSubmission.questionSnapshot || recruitQuestions).find(x => x.id === k); return <div className="submission-answer" key={k}><strong>{q?.question || q?.text || k}</strong><p>{Array.isArray(v) ? v.join(', ') : String(v || '—')}</p></div>; })}
                  </div>
                </div>
              </div>,
              document.body
            )}

            {recruitmentNotify && <div className="recruitment-modal-overlay" onClick={() => setRecruitmentNotify(null)}><form className="recruitment-modal" onSubmit={handleRecruitmentNotify} onClick={e => e.stopPropagation()}><h3>Gửi email · {recruitmentNotify.fullName}</h3><p>{recruitmentStatuses[recruitmentNotify.status] || recruitmentNotify.status}</p><div className="form-group"><label>Tiêu đề</label><input value={recruitmentNotify.subject || ''} onChange={e => setRecruitmentNotify({ ...recruitmentNotify, subject: e.target.value })} required /></div><div className="form-group"><label>Nội dung HTML</label><textarea rows="10" value={recruitmentNotify.html || ''} onChange={e => setRecruitmentNotify({ ...recruitmentNotify, html: e.target.value })} required /></div>{recruitmentNotify.status === 'interview_pass' && !recruitmentNotify.userId && can.members && <><label className="checkbox-label"><input type="checkbox" checked={!!recruitmentNotify.createAccount} onChange={e => setRecruitmentNotify({ ...recruitmentNotify, createAccount: e.target.checked })} /> Cấp tài khoản ISP Club</label>{recruitmentNotify.createAccount && <div className="admin-form-grid"><div className="form-group"><label>Username</label><input value={recruitmentNotify.username || ''} onChange={e => setRecruitmentNotify({ ...recruitmentNotify, username: e.target.value })} required /></div><div className="form-group"><label>Mật khẩu tạm</label><input value={recruitmentNotify.password || ''} onChange={e => setRecruitmentNotify({ ...recruitmentNotify, password: e.target.value })} minLength="6" required /></div></div>}</>}<div className="admin-form-actions"><button type="button" className="btn btn-outline" onClick={() => setRecruitmentNotify(null)}>Hủy</button><button className="btn btn-primary" type="submit">Gửi email</button></div></form></div>}


            {/* ==================== FORUM (Q&A - voz-style) ==================== */}
            {activeTab === 'forum' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {/* THREAD VIEW */}
                {forumThread ? (
                  <div className="forum-thread-view">
                    <div className="forum-header-bar">
                      <button className="btn btn-outline btn-sm" onClick={() => { setForumThread(null); setForumNewReply({ content: '', anonymous: false, imageFiles: [] }); }}>
                        ← Quay lại danh sách
                      </button>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h3 style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {forumThread.post.title}
                        </h3>
                        <div className="form-hint" style={{ marginTop: 2 }}>
                          📂 {forumSelectedTopic?.name} · 💬 {forumThread.replies.length} bình luận
                        </div>
                      </div>
                    </div>

                    <div className="forum-notice">
                      ⚠ <strong>Lưu ý văn hoá giao tiếp:</strong> Nội dung bài đăng & bình luận phải phù hợp tiêu chuẩn văn hoá của câu lạc bộ.
                      Tránh lời nói công kích, xúc phạm cá nhân hoặc tổ chức. Mọi bài vi phạm sẽ bị xoá và xử lý theo quy định.
                    </div>

                    {/* OP (thread starter) */}
                    <article className="forum-post op">
                      <aside className="forum-post-side">
                        {renderForumAvatar(forumThread.post)}
                        {renderForumMeta(forumThread.post)}
                      </aside>
                      <div className="forum-post-main">
                        <header className="forum-post-header">
                          <span className="forum-post-badge op-badge">#1 · Bài đăng chủ</span>
                          <span className="forum-post-time" title={fmtDMYTime(forumThread.post.createdAt)}>
                            {formatForumDate(forumThread.post.createdAt)}
                          </span>
                        </header>
                        <h2 className="forum-post-title">{forumThread.post.title}</h2>
                        <div className="forum-post-body">
                          {(forumThread.post.content || '').split(/\n\n+/).map((para, i) => (
                            <p key={i}>{para.split('\n').map((line, j, arr) => (
                              <React.Fragment key={j}>{line}{j < arr.length - 1 && <br/>}</React.Fragment>
                            ))}</p>
                          ))}
                          {Array.isArray(forumThread.post.images) && forumThread.post.images.length > 0 && (
                            <div className="forum-post-images">
                              {forumThread.post.images.map((img, i) => (
                                <a key={i} href={`/uploads/forum/${img}`} target="_blank" rel="noopener noreferrer">
                                  <img src={`/uploads/forum/${img}`} alt={`attachment-${i + 1}`} />
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                        <footer className="forum-post-footer">
                          {(isPrivileged || forumThread.post.authorId === user?.id) && (
                            <button className="btn btn-danger btn-sm" onClick={() => handleForumDeletePost(forumThread.post.id)}>
                              🗑 Xoá bài
                            </button>
                          )}
                        </footer>
                      </div>
                    </article>

                    {/* Replies */}
                    {forumThread.replies.length > 0 && (
                      <div className="forum-replies">
                        {forumThread.replies.map((r, idx) => (
                          <article key={r.id} className="forum-post reply">
                            <aside className="forum-post-side">
                              {renderForumAvatar(r)}
                              {renderForumMeta(r)}
                            </aside>
                            <div className="forum-post-main">
                              <header className="forum-post-header">
                                <span className="forum-post-badge">#{idx + 2}</span>
                                <span className="forum-post-time" title={fmtDMYTime(r.createdAt)}>
                                  {formatForumDate(r.createdAt)}
                                </span>
                              </header>
                              <div className="forum-post-body">
                                {(r.content || '').trim() && (r.content || '').split(/\n\n+/).map((para, i) => (
                                  <p key={i}>{para.split('\n').map((line, j, arr) => (
                                    <React.Fragment key={j}>{line}{j < arr.length - 1 && <br/>}</React.Fragment>
                                  ))}</p>
                                ))}
                                {Array.isArray(r.images) && r.images.length > 0 && (
                                  <div className="forum-post-images">
                                    {r.images.map((img, i) => (
                                      <a key={i} href={`/uploads/forum/${img}`} target="_blank" rel="noopener noreferrer">
                                        <img src={`/uploads/forum/${img}`} alt={`attachment-${i + 1}`} />
                                      </a>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <footer className="forum-post-footer">
                                {(isPrivileged || r.authorId === user?.id) && (
                                  <button className="btn btn-danger btn-sm" onClick={() => handleForumDeleteReply(r.id)}>
                                    🗑 Xoá
                                  </button>
                                )}
                              </footer>
                            </div>
                          </article>
                        ))}
                      </div>
                    )}

                    {/* Reply composer */}
                    <div className="forum-reply-composer">
                      <h4>{lang.DASHBOARD_REPLY_THIS_POST || 'Trả lời bài này'}</h4>
                      <form onSubmit={handleForumCreateReply}>
                        <textarea
                          rows="5"
                          placeholder={lang.DASHBOARD_REPLY_CONTENT_PH || 'Nhập nội dung trả lời... (có thể để trống nếu chỉ gửi ảnh)'}
                          value={forumNewReply.content}
                          onChange={e => setForumNewReply({ ...forumNewReply, content: e.target.value })}
                        />
                        {forumNewReply.imageFiles && forumNewReply.imageFiles.length > 0 && (
                          <div className="forum-image-previews">
                            {forumNewReply.imageFiles.map((f, i) => (
                              <div key={i} className="forum-image-preview">
                                <img src={URL.createObjectURL(f)} alt={f.name} />
                                <button type="button" className="forum-image-remove" onClick={() => {
                                  const next = forumNewReply.imageFiles.filter((_, idx) => idx !== i);
                                  setForumNewReply({ ...forumNewReply, imageFiles: next });
                                }}>✕</button>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="forum-reply-actions">
                          <label className="btn btn-outline btn-sm forum-attach-btn" style={{ cursor: 'pointer' }}>
                            📎 Gắn ảnh
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              style={{ display: 'none' }}
                              onChange={e => {
                                const picked = Array.from(e.target.files || []).slice(0, 8 - (forumNewReply.imageFiles?.length || 0));
                                setForumNewReply({
                                  ...forumNewReply,
                                  imageFiles: [...(forumNewReply.imageFiles || []), ...picked]
                                });
                                e.target.value = '';
                              }}
                            />
                          </label>
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={forumNewReply.anonymous}
                              onChange={e => setForumNewReply({ ...forumNewReply, anonymous: e.target.checked })}
                            />
                            🕵 Trả lời ẩn danh
                          </label>
                          <button type="submit" className="btn btn-primary btn-sm">📤 Gửi trả lời</button>
                        </div>
                      </form>
                    </div>
                  </div>
                ) : !forumSelectedTopic ? (
                  <>
                    <h3 className="dashboard-section-title">{lang.FORUM_TOPICS || 'Chủ đề Hỏi Đáp'}</h3>
                    {forumTopics.length === 0 && (
                      <p className="empty-text">{lang.FORUM_NO_TOPICS || 'Chưa có chủ đề nào. Admin có thể tạo chủ đề trong tab Quản Lý Hỏi Đáp.'}</p>
                    )}
                    <div className="forum-topics-grid">
                      {forumTopics.map(t => (
                        <div key={t.id} className="forum-topic-card" onClick={() => setForumSelectedTopic(t)}>
                          <h4>{t.name}</h4>
                          {t.description && <p className="forum-topic-desc">{t.description}</p>}
                          <div className="forum-topic-meta">
                            <span>{t.threadCount || 0} bài viết</span>
                            <span>💬 {t.replyCount || 0} bình luận</span>
                            {t.lastActivityAt && <span>🕒 {formatForumDate(t.lastActivityAt)}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="forum-header-bar">
                      <button className="btn btn-outline btn-sm" onClick={() => { setForumSelectedTopic(null); setForumShowComposer(false); setForumViewMode('public'); }}>
                        ← Quay lại chủ đề
                      </button>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h3 style={{ margin: 0 }}>{forumSelectedTopic.name}</h3>
                        {forumSelectedTopic.description && (
                          <div className="form-hint">{forumSelectedTopic.description}</div>
                        )}
                      </div>
                      <button className="btn btn-primary btn-sm" onClick={() => setForumShowComposer(!forumShowComposer)}>
                        + Đăng bài mới
                      </button>
                    </div>

                    <div className="forum-notice">
                      ⚠ <strong>Lưu ý văn hoá giao tiếp:</strong> Nội dung bài đăng & bình luận phải phù hợp tiêu chuẩn văn hoá của câu lạc bộ.
                      Tránh lời nói công kích, xúc phạm cá nhân hoặc tổ chức.
                    </div>

                    {forumShowComposer && (
                      <div className="admin-form-card">
                        <h3>Đăng bài mới trong "{forumSelectedTopic.name}"</h3>
                        <form className="admin-form" onSubmit={handleForumCreatePost}>
                          <div className="form-group form-group-full">
                            <label>{lang.COMMON_TITLE || 'Tiêu đề *'}</label>
                            <input
                              type="text"
                              value={forumNewPost.title}
                              onChange={e => setForumNewPost({ ...forumNewPost, title: e.target.value })}
                              required
                              placeholder={lang.DASHBOARD_POST_TITLE_PH || 'Nhập tiêu đề'}
                            />
                          </div>
                          <div className="form-group form-group-full">
                            <label>{lang.DASHBOARD_CONTENT_REQUIRED || 'Nội dung *'}</label>
                            <textarea
                              rows="8"
                              value={forumNewPost.content}
                              onChange={e => setForumNewPost({ ...forumNewPost, content: e.target.value })}
                              required
                            />
                          </div>
                          <div className="form-group form-group-full">
                            <label>{lang.DASHBOARD_ATTACH_IMAGES || 'Ảnh đính kèm (tối đa 8)'}</label>
                            {forumNewPost.imageFiles && forumNewPost.imageFiles.length > 0 && (
                              <div className="forum-image-previews">
                                {forumNewPost.imageFiles.map((f, i) => (
                                  <div key={i} className="forum-image-preview">
                                    <img src={URL.createObjectURL(f)} alt={f.name} />
                                    <button type="button" className="forum-image-remove" onClick={() => {
                                      const next = forumNewPost.imageFiles.filter((_, idx) => idx !== i);
                                      setForumNewPost({ ...forumNewPost, imageFiles: next });
                                    }}>✕</button>
                                  </div>
                                ))}
                              </div>
                            )}
                            <label className="btn btn-outline btn-sm forum-attach-btn" style={{ cursor: 'pointer', width: 'fit-content' }}>
                              📎 Gắn ảnh
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                style={{ display: 'none' }}
                                onChange={e => {
                                  const picked = Array.from(e.target.files || []).slice(0, 8 - (forumNewPost.imageFiles?.length || 0));
                                  setForumNewPost({
                                    ...forumNewPost,
                                    imageFiles: [...(forumNewPost.imageFiles || []), ...picked]
                                  });
                                  e.target.value = '';
                                }}
                              />
                            </label>
                          </div>
                          <div className="form-group form-group-full">
                            <label className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={forumNewPost.anonymous}
                                onChange={e => setForumNewPost({ ...forumNewPost, anonymous: e.target.checked })}
                              />
                              🕵 Đăng ẩn danh
                            </label>
                          </div>
                          <div className="admin-form-actions">
                            <button type="submit" className="btn btn-primary btn-sm">📤 Đăng bài</button>
                            <button type="button" className="btn btn-outline btn-sm" onClick={() => setForumShowComposer(false)}>Huỷ</button>
                          </div>
                        </form>
                      </div>
                    )}

                    {/* Split threads into public vs anonymous views — hidden while composing */}
                    {!forumShowComposer && (() => {
                      const publicPosts = forumPosts.filter(p => !p.anonymous);
                      const anonPosts = forumPosts.filter(p => p.anonymous);
                      const shownPosts = forumViewMode === 'anonymous' ? anonPosts : publicPosts;
                      const emptyText = forumViewMode === 'anonymous'
                        ? 'Chưa có bài ẩn danh nào trong chủ đề này.'
                        : 'Chưa có bài công khai nào. Hãy là người đầu tiên đăng bài!';
                      return (
                        <>
                          <div className="forum-mode-tabs">
                            <button
                              type="button"
                              className={`forum-mode-tab ${forumViewMode === 'public' ? 'active' : ''}`}
                              onClick={() => setForumViewMode('public')}
                            >
                              Hỏi Đáp Công Khai
                              <span className="forum-mode-count">{publicPosts.length}</span>
                            </button>
                            <button
                              type="button"
                              className={`forum-mode-tab ${forumViewMode === 'anonymous' ? 'active' : ''}`}
                              onClick={() => setForumViewMode('anonymous')}
                            >
                              Hỏi Đáp Ẩn Danh
                              <span className="forum-mode-count">{anonPosts.length}</span>
                            </button>
                          </div>

                          <div className="forum-thread-list">
                            {shownPosts.length === 0 && <p className="empty-text">{emptyText}</p>}
                            {shownPosts.map(p => (
                              <div key={p.id} className="forum-thread-row" onClick={() => openForumThread(p.id)}>
                                <div className="forum-thread-avatar">
                                  {renderForumAvatar(p)}
                                </div>
                                <div className="forum-thread-info">
                                  <div className="forum-thread-title">{p.title}</div>
                                  <div className="forum-thread-excerpt">{p.excerpt}</div>
                                  <div className="forum-thread-meta">
                                    <span>
                                      👤 {p.anonymous ? `Anonymous#${p.anonIndex || 1}` : (p.authorSnapshot?.fullName || 'Member')}
                                    </span>
                                    {!p.anonymous && p.authorSnapshot?.gen && <span>Gen {p.authorSnapshot.gen}</span>}
                                    {!p.anonymous && p.authorSnapshot?.position && (
                                      <span>{POSITION_LABELS[p.authorSnapshot.position] || p.authorSnapshot.position}</span>
                                    )}
                                    <span>🕒 {formatForumDate(p.lastReplyAt || p.createdAt)}</span>
                                  </div>
                                </div>
                                <div className="forum-thread-stats">
                                  <div className="forum-thread-stat">
                                    <strong>{p.replyCount || 0}</strong>
                                    <small>{lang.DASHBOARD_COMMENTS_LABEL || 'bình luận'}</small>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      );
                    })()}

                    {forumLoading && <p className="empty-text">Đang tải...</p>}
                  </>
                )}
              </motion.div>
            )}

            {/* ==================== FORUM ADMIN (ky_thuat/ban_dieu_hanh) ==================== */}
            {activeTab === 'forum_admin' && isPrivileged && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {/* Topics management (ky_thuat only) */}
                {isKyThuat && (
                  <div className="admin-form-card">
                    {!showForumTopicForm && !forumAdminEditingTopic && (
                      <div className="admin-actions" style={{ marginBottom: 12 }}>
                        <button className="btn btn-primary btn-sm" onClick={() => {
                          setShowForumTopicForm(true);
                          setForumAdminEditingTopic(null);
                          setForumAdminTopicForm({ name: '', description: '' });
                        }}>
                          + {lang.FORUM_NEW_TOPIC || 'Tạo Chủ Đề Mới'}
                        </button>
                      </div>
                    )}

                    {(showForumTopicForm || forumAdminEditingTopic) && (
                    <>
                    <h3>{forumAdminEditingTopic ? (lang.FORUM_EDIT_TOPIC || 'Sửa Chủ Đề') : (lang.FORUM_NEW_TOPIC || 'Tạo Chủ Đề Mới')}</h3>
                    <form className="admin-form" onSubmit={handleForumAdminCreateTopic}>
                      <div className="admin-form-grid">
                        <div className="form-group">
                          <label>{lang.FORUM_TOPIC_NAME || 'Tên chủ đề'} *</label>
                          <input type="text" value={forumAdminTopicForm.name} onChange={e => setForumAdminTopicForm({ ...forumAdminTopicForm, name: e.target.value })} required />
                        </div>
                        <div className="form-group">
                          <label>{lang.FORUM_TOPIC_DESC || 'Mô tả'}</label>
                          <input type="text" value={forumAdminTopicForm.description} onChange={e => setForumAdminTopicForm({ ...forumAdminTopicForm, description: e.target.value })} />
                        </div>
                      </div>
                      <div className="admin-form-actions">
                        <button type="submit" className="btn btn-primary btn-sm">
                          {forumAdminEditingTopic ? (lang.DASHBOARD_UPDATE || 'Cập nhật') : (lang.DASHBOARD_ADD || 'Tạo')}
                        </button>
                        <button type="button" className="btn btn-outline btn-sm" onClick={() => { setForumAdminEditingTopic(null); setShowForumTopicForm(false); setForumAdminTopicForm({ name: '', description: '' }); }}>
                          {lang.DASHBOARD_CANCEL || 'Huỷ'}
                        </button>
                      </div>
                    </form>
                    </>
                    )}

                    {!showForumTopicForm && !forumAdminEditingTopic && (
                    <>
                    <h4 style={{ marginTop: 8 }}>{lang.FORUM_TOPICS || 'Danh sách chủ đề'}</h4>
                    {forumTopics.length > 0 && (
                      <Paginator total={forumTopicsPg.total} page={forumTopicsPg.page} pageSize={forumTopicsPg.pageSize} onChange={forumTopicsPg.onChange} position="top" />
                    )}
                    <div className="admin-table-wrap">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>{lang.FORUM_TOPIC_NAME || 'Tên'}</th>
                            <th>{lang.FORUM_TOPIC_DESC || 'Mô tả'}</th>
                            <th>{lang.FORUM_PUBLIC || 'Công khai'}</th>
                            <th>{lang.FORUM_ANONYMOUS || 'Ẩn danh'}</th>
                            <th>{lang.DASHBOARD_ACTIONS || 'Hành động'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {forumTopicsPg.paged.map((t, i) => {
                            const realIdx = (forumTopicsPg.pageSize === 0 ? 0 : (forumTopicsPg.page - 1) * forumTopicsPg.pageSize) + i;
                            return (
                            <tr key={t.id}>
                              <td>{realIdx + 1}</td>
                              <td>{t.name}</td>
                              <td>{t.description || '-'}</td>
                              <td>{t.publicPostCount || 0}</td>
                              <td>{t.anonymousPostCount || 0}</td>
                              <td>
                                <button className="btn btn-outline btn-sm" onClick={() => handleForumAdminEditTopic(t)}>{lang.DASHBOARD_EDIT || 'Sửa'}</button>{' '}
                                <button className="btn btn-danger btn-sm" onClick={() => handleForumAdminDeleteTopic(t.id)}>{lang.DASHBOARD_DELETE || 'Xoá'}</button>
                              </td>
                            </tr>
                            );
                          })}
                          {forumTopics.length === 0 && (
                            <tr><td colSpan="6" className="empty-row">{lang.FORUM_NO_TOPICS || 'Chưa có chủ đề.'}</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    {forumTopics.length > 0 && (
                      <Paginator total={forumTopicsPg.total} page={forumTopicsPg.page} pageSize={forumTopicsPg.pageSize} onChange={forumTopicsPg.onChange} position="bottom" />
                    )}
                    </>
                    )}
                  </div>
                )}

                {/* Logs viewer */}
                {!forumAdminEditingTopic && !showForumTopicForm && (
                <div className="admin-form-card">
                  <h3>{lang.FORUM_POST_LOGS || 'Nhật Ký Bài Viết (bao gồm ẩn danh)'}</h3>
                  <p className="form-hint">{lang.FORUM_LOGS_HINT || 'Hiển thị đầy đủ thông tin người đăng, IP, user agent và thời gian cho mọi bài viết — kể cả ẩn danh.'}</p>
                  <div className="admin-form-grid">
                    <div className="form-group">
                      <label>{lang.FORUM_FILTER_TOPIC || 'Chủ đề'}</label>
                      <select value={forumAdminFilter.topicId} onChange={e => setForumAdminFilter({ ...forumAdminFilter, topicId: e.target.value })}>
                        <option value="">{lang.FORUM_ALL_TOPICS || 'Tất cả'}</option>
                        {forumTopics.map(t => (<option key={t.id} value={t.id}>{t.name}</option>))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>{lang.FORUM_FILTER_MODE || 'Loại'}</label>
                      <select value={forumAdminFilter.mode} onChange={e => setForumAdminFilter({ ...forumAdminFilter, mode: e.target.value })}>
                        <option value="all">{lang.FORUM_ALL || 'Tất cả'}</option>
                        <option value="public">{lang.FORUM_PUBLIC || 'Công khai'}</option>
                        <option value="anonymous">{lang.FORUM_ANONYMOUS || 'Ẩn danh'}</option>
                      </select>
                    </div>
                  </div>

                  {forumAdminLogs.length > 0 && (
                    <Paginator total={forumLogsPg.total} page={forumLogsPg.page} pageSize={forumLogsPg.pageSize} onChange={forumLogsPg.onChange} position="top" />
                  )}
                  <div className="admin-table-wrap">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>{lang.FORUM_TITLE || 'Tiêu đề'}</th>
                          <th>{lang.FORUM_TOPIC || 'Chủ đề'}</th>
                          <th>{lang.FORUM_AUTHOR || 'Người đăng'}</th>
                          <th>{lang.FORUM_MODE || 'Loại'}</th>
                          <th>IP</th>
                          <th>{lang.FORUM_TIME || 'Thời gian'}</th>
                          <th>{lang.DASHBOARD_ACTIONS || 'Hành động'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {forumLogsPg.paged.map((p, i) => {
                          const realIdx = (forumLogsPg.pageSize === 0 ? 0 : (forumLogsPg.page - 1) * forumLogsPg.pageSize) + i;
                          return (
                          <tr key={p.id}>
                            <td>{realIdx + 1}</td>
                            <td>{p.title}</td>
                            <td>{p.topicName || p.topicId}</td>
                            <td>
                              <div style={{ fontWeight: 600 }}>{p.authorName}</div>
                              <div className="mono" style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>@{p.authorUsername} (id={p.authorId})</div>
                            </td>
                            <td>
                              <span className={`badge ${p.anonymous ? 'badge-ky_thuat' : 'badge-thanh_vien'}`}>
                                {p.anonymous ? 'Ẩn danh' : 'Công khai'}
                              </span>
                            </td>
                            <td className="mono">{p.ip || '-'}</td>
                            <td>{fmtDMYTime(p.createdAt)}</td>
                            <td>
                              <button className="btn btn-outline btn-sm" onClick={() => openForumAdminThread(p.id)}>{lang.FORUM_VIEW || 'Xem'}</button>{' '}
                              <button className="btn btn-danger btn-sm" onClick={() => handleForumDeletePost(p.id)}>{lang.DASHBOARD_DELETE || 'Xoá'}</button>
                            </td>
                          </tr>
                          );
                        })}
                        {forumAdminLogs.length === 0 && (
                          <tr><td colSpan="8" className="empty-row">{lang.FORUM_NO_POSTS || 'Chưa có bài viết.'}</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {forumAdminLogs.length > 0 && (
                    <Paginator total={forumLogsPg.total} page={forumLogsPg.page} pageSize={forumLogsPg.pageSize} onChange={forumLogsPg.onChange} position="bottom" />
                  )}

                  {forumAdminThread && (() => {
                    const t = forumAdminThread;
                    const renderAdminEntry = (entry, indexLabel) => {
                      const snap = entry.authorSnapshot || {};
                      const isAnon = !!entry.anonymous;
                      return (
                        <div key={`${indexLabel}-${entry.id}`} className="forum-admin-entry">
                          <div className="forum-admin-entry-head">
                            <span className="forum-admin-entry-num">{indexLabel}</span>
                            {isAnon && (
                              <span className="forum-admin-entry-badge anon">🕵 Anonymous#{entry.anonIndex || '?'}</span>
                            )}
                            <span className="forum-admin-entry-time">
                              {fmtDMYTime(entry.createdAt)}
                            </span>
                          </div>
                          <div className="forum-admin-entry-identity">
                            <div>
                              <span className="form-hint">Người đăng thực sự:</span>{' '}
                              <strong>{snap.fullName || '—'}</strong>
                              {snap.username && <span className="mono"> (@{snap.username})</span>}
                            </div>
                            <div className="forum-admin-identity-pills">
                              {snap.gen && <span className="badge">Gen {snap.gen}</span>}
                              {snap.position && <span className="badge">{POSITION_LABELS[snap.position] || snap.position}</span>}
                              {Array.isArray(snap.roles) && snap.roles.map((r, i) => (
                                <span key={i} className="badge">{ROLE_LABELS[r] || r}</span>
                              ))}
                              {snap.title && <span className="badge badge-title">{TITLE_LABELS[snap.title] || snap.title}</span>}
                            </div>
                            <div className="forum-admin-ip-row">
                              <span className="form-hint">IP:</span>{' '}
                              <code className="mono">{entry.ip || '-'}</code>
                              {entry.userAgent && (
                                <>
                                  <span className="form-hint" style={{ marginLeft: 10 }}>UA:</span>{' '}
                                  <span className="form-hint mono" style={{ wordBreak: 'break-all' }}>{entry.userAgent}</span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="forum-admin-entry-body">
                            {entry.title && <h4 style={{ marginTop: 0 }}>{entry.title}</h4>}
                            <div style={{ whiteSpace: 'pre-wrap' }}>{entry.content}</div>
                          </div>
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            {indexLabel === '#1' ? (
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => { handleForumDeletePost(entry.id); setForumAdminThread(null); }}
                              >🗑 Xoá bài (kèm toàn bộ bình luận)</button>
                            ) : (
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={async () => {
                                  await handleForumDeleteReply(entry.id);
                                  // refresh admin view
                                  const res = await fetch(`/api/forum/posts/${t.post.id}`, { headers: authHeaders });
                                  const data = await res.json();
                                  if (res.ok) setForumAdminThread(data);
                                }}
                              >🗑 Xoá bình luận</button>
                            )}
                          </div>
                        </div>
                      );
                    };
                    return (
                      <div className="blog-modal-overlay blog-modal-overlay-bounded" style={{ top: bbv.topOffset, bottom: bbv.bottom }} onClick={() => setForumAdminThread(null)}>
                        <div className="blog-modal forum-admin-modal" onClick={e => e.stopPropagation()}>
                          <button className="blog-modal-close dash-modal-close" onClick={() => setForumAdminThread(null)} aria-label={lang.COMMON_CLOSE || 'Đóng'}>×</button>
                          <h1 className="blog-modal-title">{t.post.title}</h1>
                          {renderAdminEntry(t.post, '#1')}
                          {t.replies.map((r, i) => renderAdminEntry(r, `#${i + 2}`))}
                        </div>
                      </div>
                    );
                  })()}

                  {forumSelectedPost && (
                    <div className="blog-modal-overlay blog-modal-overlay-bounded" style={{ top: bbv.topOffset, bottom: bbv.bottom }} onClick={() => setForumSelectedPost(null)}>
                      <div className="blog-modal" onClick={e => e.stopPropagation()}>
                        <button className="blog-modal-close dash-modal-close" onClick={() => setForumSelectedPost(null)} aria-label={lang.COMMON_CLOSE || 'Đóng'}>×</button>
                        <h1 className="blog-modal-title">{forumSelectedPost.title}</h1>
                        <div className="blog-modal-meta">
                          <span>{forumSelectedPost.anonymous ? '🕵 [Ẩn Danh] ' : '👤 '}{forumSelectedPost.authorName} (@{forumSelectedPost.authorUsername})</span>
                          <span>IP: <span className="mono">{forumSelectedPost.ip || '-'}</span></span>
                          <span>{fmtDMYTime(forumSelectedPost.createdAt)}</span>
                        </div>
                        {forumSelectedPost.userAgent && (
                          <p className="form-hint" style={{ wordBreak: 'break-all' }}>UA: {forumSelectedPost.userAgent}</p>
                        )}
                        <div className="blog-modal-content" style={{ whiteSpace: 'pre-wrap' }}>{forumSelectedPost.content}</div>
                      </div>
                    </div>
                  )}
                </div>
                )}
              </motion.div>
            )}

            {/* ==================== TRAINING (Online Judge + CTF) ==================== */}
            {activeTab === 'training' && (!adminMode || can.trainingAdmin || can.ctfAdmin) && (() => {
              // Determine which sub-modes are available in this context.
              const ojAvailable = !adminMode || can.trainingAdmin;
              const ctfAvailable = !adminMode || can.ctfAdmin;
              // Effective mode: fall back to whichever is available.
              const effMode = (trainingMode === 'ctf' && ctfAvailable) ? 'ctf'
                : (trainingMode === 'oj' && ojAvailable) ? 'oj'
                : (ojAvailable ? 'oj' : 'ctf');
              return (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  {/* Category selector */}
                  <div className="training-mode-tabs" style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
                    {ojAvailable && (
                      <button type="button"
                        className={`dashboard-tab ${effMode === 'oj' ? 'active' : ''}`}
                        onClick={() => setTrainingMode('oj')}>
                        Training — Online Judge
                      </button>
                    )}
                    {ctfAvailable && (
                      <button type="button"
                        className={`dashboard-tab ${effMode === 'ctf' ? 'active' : ''}`}
                        onClick={() => setTrainingMode('ctf')}>
                        Training CTF
                      </button>
                    )}
                  </div>

                  {effMode === 'oj' && (
                    adminMode
                      ? <Training mode="admin" canAdmin={true} />
                      : <Training mode="member" canAdmin={false} />
                  )}
                  {effMode === 'ctf' && (
                    adminMode
                      ? <Ctf mode="admin" />
                      : <Ctf mode="member" />
                  )}
                </motion.div>
              );
            })()}

            {/* ==================== CONTRIBUTORS (ADMIN) ==================== */}
            {activeTab === 'contributors' && adminMode && can.contributors && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <ContributorsAdmin token={token} />
              </motion.div>
            )}

            {/* ==================== SCREENING (ADMIN) ==================== */}
            {activeTab === 'screening' && adminMode && can.screening && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <ScreeningAdmin token={token} lang={lang} />
              </motion.div>
            )}

            {activeTab === 'nomination_admin' && adminMode && can.nominationAdmin && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <NominationAdmin
                  data={nominationAdminData}
                  authHeaders={authHeaders}
                  onRefresh={() => fetch('/api/nominations/admin', { headers: authHeaders }).then(r => r.json()).then(setNominationAdminData)}
                  showMsg={showMsg}
                />
              </motion.div>
            )}

            {activeTab === 'nomination' && !adminMode && user?.position === 'mam_non' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <NominationUser
                  status={nominationStatus}
                  authHeaders={authHeaders}
                  onRefresh={() => fetch('/api/nominations/status', { headers: authHeaders }).then(r => r.json()).then(setNominationStatus)}
                  showMsg={showMsg}
                />
              </motion.div>
            )}

            {/* ==================== MAIL CAMPAIGN (ADMIN) ==================== */}
            {activeTab === 'mail_campaign' && adminMode && can.mailCampaign && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <MailCampaignAdmin token={token} lang={lang} />
              </motion.div>
            )}

            {/* ==================== ABOUT-PAGE GALLERY (KỸ THUẬT) ==================== */}
            {activeTab === 'about_gallery' && adminMode && can.aboutGallery && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <AboutGalleryAdmin token={token} lang={lang} />
              </motion.div>
            )}

            {activeTab === 'event_calendar' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <EventCalendar
                  token={token}
                  mode={(adminMode && can.eventCalendar) ? 'manager' : 'member'}
                />
              </motion.div>
            )}

            {/* ==================== RESOURCES (KHO TÀI LIỆU) ==================== */}
            {activeTab === 'resources' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="resource-type-tabs">
                  {[
                    { key: 'document', label: 'Tài Liệu' },
                    { key: 'video', label: 'Video Bài Học' },
                    { key: 'tool', label: 'Công Cụ' }
                  ].map(t => (
                    <button
                      key={t.key}
                      className={`resource-type-tab ${resourceActiveType === t.key ? 'active' : ''}`}
                      onClick={() => { setResourceActiveType(t.key); setResourceSelectedCat(null); }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* Upload button for non-mam_non users */}
                {!isMamNon && !showResourceUserUpload && !resourceSelectedCat && (
                  <div style={{ marginBottom: 12 }}>
                    <button className="btn btn-primary btn-sm" onClick={() => setShowResourceUserUpload(true)}>
                      + Tải Tài Liệu Lên
                    </button>
                  </div>
                )}

                {/* User upload form */}
                {!isMamNon && showResourceUserUpload && (
                  <div className="admin-form-card" style={{ marginBottom: 16 }}>
                    <h3>Tải Tài Liệu Lên</h3>
                    <form className="admin-form" onSubmit={handleResourceUserUpload}>
                      <div className="admin-form-grid">
                        <div className="form-group">
                          <label>Chọn mục *</label>
                          <select
                            value={resourceUserForm.categoryId}
                            onChange={e => setResourceUserForm({ ...resourceUserForm, categoryId: e.target.value })}
                            required
                          >
                            <option value="">-- Chọn mục --</option>
                            {(resources[resourceActiveType] || []).map(cat => (
                              <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Tiêu đề *</label>
                          <input
                            type="text"
                            value={resourceUserForm.title}
                            onChange={e => setResourceUserForm({ ...resourceUserForm, title: e.target.value })}
                            required
                          />
                        </div>
                        <div className="form-group form-group-full">
                          <label>Mô tả</label>
                          <textarea
                            rows="2"
                            value={resourceUserForm.description}
                            onChange={e => setResourceUserForm({ ...resourceUserForm, description: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Tải file (tối đa 3GB)</label>
                          <input type="file" ref={resourceUserFileRef} className="file-input" />
                        </div>
                        <div className="form-group">
                          <label>URL (nếu không tải file)</label>
                          <input
                            type="url"
                            value={resourceUserForm.url}
                            onChange={e => setResourceUserForm({ ...resourceUserForm, url: e.target.value })}
                            placeholder="https://..."
                          />
                        </div>
                      </div>
                      {resourceUploadProgress !== null && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                            <span>Đang tải lên...</span>
                            <strong>{resourceUploadProgress}%</strong>
                          </div>
                          <div style={{ background: 'var(--bg-tertiary, #e5e7eb)', height: 10, borderRadius: 6, overflow: 'hidden' }}>
                            <div style={{
                              width: resourceUploadProgress + '%',
                              height: '100%',
                              background: 'linear-gradient(90deg, var(--accent-primary, #4f8eff), var(--accent-teal, #0ea5e9))',
                              transition: 'width 0.3s ease',
                              borderRadius: 6
                            }} />
                          </div>
                        </div>
                      )}
                      <div className="admin-form-actions">
                        <button type="submit" className="btn btn-primary btn-sm" disabled={resourceUploadProgress !== null}>
                          {resourceUploadProgress !== null ? `Đang tải... ${resourceUploadProgress}%` : 'Tải lên'}
                        </button>
                        <button type="button" className="btn btn-outline btn-sm" onClick={() => {
                          setShowResourceUserUpload(false);
                          setResourceUserForm({ title: '', description: '', url: '', categoryId: '' });
                          if (resourceUserFileRef.current) resourceUserFileRef.current.value = '';
                        }}>
                          Huỷ
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {!resourceSelectedCat ? (
                  !showResourceUserUpload && (
                  <>
                    {(resources[resourceActiveType] || []).length === 0 && (
                      <p className="empty-text">{lang.DASHBOARD_RESOURCE_EMPTY_USER || 'Chưa có mục nào'}</p>
                    )}
                    <div className="forum-topics-grid">
                      {(resources[resourceActiveType] || []).map(cat => (
                        <div key={cat.id} className="forum-topic-card" onClick={() => setResourceSelectedCat(cat)}>
                          <h4>{cat.name}</h4>
                          {cat.description && <p className="forum-topic-desc">{cat.description}</p>}
                          <div className="forum-topic-meta">
                            <span>📦 {(cat.items || []).length} tài liệu</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                  )
                ) : (
                  <>
                    <div className="forum-header-bar">
                      <button className="btn btn-outline btn-sm" onClick={() => setResourceSelectedCat(null)}>
                        ← Quay lại
                      </button>
                      <h3 style={{ margin: 0 }}>{resourceSelectedCat.name}</h3>
                      <span />
                    </div>
                    {resourceSelectedCat.description && (
                      <p className="form-hint" style={{ marginBottom: 16 }}>{resourceSelectedCat.description}</p>
                    )}
                    <div className="resource-items-list">
                      {(resourceSelectedCat.items || []).length === 0 && (
                        <p className="empty-text">Chưa có tài liệu nào trong mục này.</p>
                      )}
                      {(resourceSelectedCat.items || []).map(item => (
                        <div key={item.id} className="resource-item-card">
                          <div className="resource-item-info">
                            <h4>{item.title}</h4>
                            {item.description && <p>{item.description}</p>}
                            <div className="resource-item-meta">
                              <span>👤 {item.addedByName}</span>
                              <span>🕒 {fmtDMYTime(item.createdAt)}</span>
                              {item.fileSize > 0 && <span>📦 {(item.fileSize / 1024 / 1024).toFixed(2)} MB</span>}
                            </div>
                            {item.allowedPositions && item.allowedPositions.length > 0 ? (
                              <div className="resource-item-roles">
                                {item.allowedPositions.map(p => (
                                  <span key={p} className="badge">🔒 {POSITION_LABELS[p] || p}</span>
                                ))}
                              </div>
                            ) : (item.allowedRoles && item.allowedRoles.length > 0) && (
                              <div className="resource-item-roles">
                                {item.allowedRoles.map(r => (
                                  <span key={r} className={`badge badge-${r}`}>🔒 {ROLE_LABELS[r] || r}</span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="resource-item-actions">
                            {item.fileName && (
                              <a
                                className="btn btn-primary btn-sm"
                                href={`/api/resources/${resourceActiveType}/categories/${resourceSelectedCat.id}/items/${item.id}/download?token=${encodeURIComponent(token)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                ⬇ Tải xuống
                              </a>
                            )}
                            {item.url && (
                              <a
                                className="btn btn-outline btn-sm"
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                🔗 Mở link
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </motion.div>
            )}

            {/* ==================== RESOURCES ADMIN (ky_thuat only) ==================== */}
            {activeTab === 'resources_admin' && can.resourcesAdmin && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="resource-type-tabs">
                  {[
                    { key: 'document', label: 'Tài Liệu' },
                    { key: 'video', label: 'Video Bài Học' },
                    { key: 'tool', label: 'Công Cụ' }
                  ].map(t => (
                    <button
                      key={t.key}
                      className={`resource-type-tab ${resourceAdminType === t.key ? 'active' : ''}`}
                      onClick={() => {
                        setResourceAdminType(t.key);
                        setResourceCatEditing(null);
                        setResourceItemEditing(null);
                        setShowResourceCatForm(false);
                        setShowResourceItemForm(false);
                        setResourceCatForm({ name: '', description: '' });
                        setResourceItemForm({ title: '', description: '', url: '', categoryId: '', allowedPositions: [] });
                      }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* Add buttons (only when no form / edit is active) */}
                {!showResourceCatForm && !resourceCatEditing && !showResourceItemForm && !resourceItemEditing && (
                  <div className="admin-actions" style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    <button className="btn btn-primary btn-sm" onClick={() => {
                      setShowResourceCatForm(true);
                      setResourceCatEditing(null);
                      setResourceCatForm({ name: '', description: '' });
                    }}>+ {lang.DASHBOARD_ADD_CATEGORY || 'Thêm Mục Mới'}</button>
                    <button className="btn btn-primary btn-sm" onClick={() => {
                      setShowResourceItemForm(true);
                      setResourceItemEditing(null);
                      setResourceItemForm({ title: '', description: '', url: '', categoryId: '', allowedPositions: [] });
                    }}>+ {lang.DASHBOARD_ADD_RESOURCE || 'Thêm Tài Liệu Mới'}</button>
                  </div>
                )}

                {/* Category form */}
                {(showResourceCatForm || resourceCatEditing) && !showResourceItemForm && !resourceItemEditing && (
                <div className="admin-form-card">
                  <h3>{resourceCatEditing ? 'Sửa Mục' : 'Thêm Mục Mới'} ({resourceAdminType === 'document' ? 'Tài Liệu' : resourceAdminType === 'video' ? 'Video' : 'Công Cụ'})</h3>
                  <form className="admin-form" onSubmit={handleResourceCatSubmit}>
                    <div className="admin-form-grid">
                      <div className="form-group">
                        <label>{lang.DASHBOARD_CATEGORY_NAME_REQUIRED || 'Tên mục *'}</label>
                        <input
                          type="text"
                          value={resourceCatForm.name}
                          onChange={e => setResourceCatForm({ ...resourceCatForm, name: e.target.value })}
                          placeholder={resourceAdminType === 'document' ? 'VD: Mật mã học' : resourceAdminType === 'video' ? 'VD: Web Exploitation cơ bản' : 'VD: Công cụ reverse engineering'}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>{lang.COMMON_DESCRIPTION || 'Mô tả'}</label>
                        <input
                          type="text"
                          value={resourceCatForm.description}
                          onChange={e => setResourceCatForm({ ...resourceCatForm, description: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="admin-form-actions">
                      <button type="submit" className="btn btn-primary btn-sm">
                        {resourceCatEditing ? 'Cập nhật' : 'Tạo mục'}
                      </button>
                      <button type="button" className="btn btn-outline btn-sm" onClick={() => { setResourceCatEditing(null); setShowResourceCatForm(false); setResourceCatForm({ name: '', description: '' }); }}>
                        Huỷ
                      </button>
                    </div>
                  </form>
                </div>
                )}

                {/* Item upload form */}
                {(showResourceItemForm || resourceItemEditing) && !showResourceCatForm && !resourceCatEditing && (
                <div className="admin-form-card">
                  <h3>{resourceItemEditing ? 'Sửa Tài Liệu' : 'Thêm Tài Liệu Vào Mục'}</h3>
                  <form className="admin-form" onSubmit={handleResourceItemSubmit}>
                    <div className="admin-form-grid">
                      <div className="form-group">
                        <label>{lang.DASHBOARD_SELECT_CATEGORY_REQUIRED || 'Chọn mục *'}</label>
                        <select
                          value={resourceItemForm.categoryId}
                          onChange={e => setResourceItemForm({ ...resourceItemForm, categoryId: e.target.value })}
                          disabled={!!resourceItemEditing}
                          required
                        >
                          <option value="">-- Chọn mục --</option>
                          {(resources[resourceAdminType] || []).map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>{lang.COMMON_TITLE || 'Tiêu đề *'}</label>
                        <input
                          type="text"
                          value={resourceItemForm.title}
                          onChange={e => setResourceItemForm({ ...resourceItemForm, title: e.target.value })}
                          required
                        />
                      </div>
                      <div className="form-group form-group-full">
                        <label>{lang.COMMON_DESCRIPTION || 'Mô tả'}</label>
                        <textarea
                          rows="2"
                          value={resourceItemForm.description}
                          onChange={e => setResourceItemForm({ ...resourceItemForm, description: e.target.value })}
                        />
                      </div>
                      {!resourceItemEditing && (
                        <div className="form-group">
                          <label>{lang.DASHBOARD_RESOURCE_FILE_LABEL || 'Tải file (tuỳ chọn, tối đa 10GB — hỗ trợ mọi loại file kể cả video)'}</label>
                          <input type="file" ref={resourceFileRef} className="file-input" />
                        </div>
                      )}
                      <div className="form-group">
                          <label>{lang.DASHBOARD_RESOURCE_URL_LABEL || 'URL (nếu không tải file)'}</label>
                        <input
                          type="url"
                          value={resourceItemForm.url}
                          onChange={e => setResourceItemForm({ ...resourceItemForm, url: e.target.value })}
                          placeholder={lang.DASHBOARD_LINK_URL_PH || 'https://...'}
                        />
                      </div>
                      <div className="form-group form-group-full">
                        <label>{lang.DASHBOARD_RESOURCE_PERM_LABEL || 'Quyền xem tài liệu (theo chức vụ)'}</label>
                        <div className="role-checkbox-row">
                          {Object.entries(POSITION_LABELS).map(([posKey, posLabel]) => (
                            <label key={posKey} className="role-checkbox">
                              <input
                                type="checkbox"
                                checked={(resourceItemForm.allowedPositions || []).includes(posKey)}
                                onChange={() => toggleResourceAllowedPosition(posKey)}
                              />
                              <span>{posLabel}</span>
                            </label>
                          ))}
                        </div>
                        <p className="form-hint">
                          {lang.DASHBOARD_RESOURCE_PERM_HINT || 'Bỏ chọn để mọi thành viên có thể xem tài liệu'}
                        </p>
                      </div>
                    </div>
                    {resourceAdminUploadProgress !== null && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                          <span>Đang tải lên...</span>
                          <strong>{resourceAdminUploadProgress}%</strong>
                        </div>
                        <div style={{ background: 'var(--bg-tertiary, #e5e7eb)', height: 10, borderRadius: 6, overflow: 'hidden' }}>
                          <div style={{
                            width: resourceAdminUploadProgress + '%',
                            height: '100%',
                            background: 'linear-gradient(90deg, var(--accent-primary, #4f8eff), var(--accent-teal, #0ea5e9))',
                            transition: 'width 0.3s ease',
                            borderRadius: 6
                          }} />
                        </div>
                      </div>
                    )}
                    <div className="admin-form-actions">
                      <button type="submit" className="btn btn-primary btn-sm" disabled={resourceAdminUploadProgress !== null}>
                        {resourceAdminUploadProgress !== null
                          ? `Đang tải... ${resourceAdminUploadProgress}%`
                          : (resourceItemEditing ? 'Cập nhật' : 'Thêm tài liệu')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => {
                          setResourceItemEditing(null);
                          setShowResourceItemForm(false);
                          setResourceItemForm({ title: '', description: '', url: '', categoryId: '', allowedPositions: [] });
                          if (resourceFileRef.current) resourceFileRef.current.value = '';
                        }}
                      >
                        Huỷ
                      </button>
                    </div>
                  </form>
                </div>
                )}

                {/* Categories + items list */}
                {!resourceCatEditing && !resourceItemEditing && !showResourceCatForm && !showResourceItemForm && (
                <ResourceListPaged
                  cats={resources[resourceAdminType] || []}
                  emptyText={lang.DASHBOARD_RESOURCE_EMPTY_ADMIN || 'Chưa có mục nào.'}
                  fmtDMYTime={fmtDMYTime}
                  POSITION_LABELS={POSITION_LABELS}
                  onCatEdit={handleResourceCatEdit}
                  onCatDelete={handleResourceCatDelete}
                  onItemEdit={handleResourceItemEdit}
                  onItemDelete={handleResourceItemDelete}
                />
                )}
              </motion.div>
            )}

            {/* ==================== EVENTS ADMIN (ky_thuat only) ==================== */}
            {activeTab === 'events_admin' && can.eventsAdmin && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {!showEventForm && !eventEditing && (
                  <div className="admin-actions" style={{ marginBottom: 12 }}>
                    <button className="btn btn-primary btn-sm" onClick={() => { setShowEventForm(true); setEventEditing(null); }}>
                      + {lang.DASHBOARD_NEW_EVENT || 'Tạo Sự Kiện Mới'}
                    </button>
                  </div>
                )}
                {(showEventForm || eventEditing) && (
                <div className="admin-form-card">
                  <h3>{eventEditing ? 'Sửa Sự Kiện' : 'Tạo Sự Kiện Mới'}</h3>
                  <form className="admin-form" onSubmit={handleEventSubmit}>
                    <div className="admin-form-grid">
                      <div className="form-group form-group-full">
                        <label>{lang.COMMON_TITLE || 'Tiêu đề *'}</label>
                        <input
                          type="text"
                          value={eventForm.title}
                          onChange={e => setEventForm({ ...eventForm, title: e.target.value })}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>{lang.DASHBOARD_EVENT_DATE || 'Ngày diễn ra'}</label>
                        <DateInput
                          value={eventForm.eventDate}
                          onChange={v => setEventForm({ ...eventForm, eventDate: v })}
                        />
                      </div>
                      <div className="form-group">
                        <label>{lang.DASHBOARD_EVENT_LOCATION || 'Địa điểm'}</label>
                        <input
                          type="text"
                          value={eventForm.location}
                          onChange={e => setEventForm({ ...eventForm, location: e.target.value })}
                          placeholder={lang.DASHBOARD_EVENT_LOCATION_PH || 'VD: Phòng A1-201, PTIT'}
                        />
                      </div>
                      <div className="form-group">
                        <label>{lang.DASHBOARD_STATUS_LABEL || 'Trạng thái'}</label>
                        <select
                          value={eventForm.status}
                          onChange={e => setEventForm({ ...eventForm, status: e.target.value })}
                        >
                          <option value="upcoming">Sắp diễn ra</option>
                          <option value="ongoing">Đang diễn ra</option>
                          <option value="past">Đã kết thúc</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>{lang.DASHBOARD_EVENT_COVER || 'Ảnh bìa (cover)'}</label>
                        <input
                          type="file"
                          ref={eventCoverRef}
                          accept="image/*"
                          className="file-input"
                          onChange={handleEventCoverChange}
                        />
                        {eventCoverPreview && (
                          <div style={{ marginTop: 8 }}>
                            <img
                              src={eventCoverPreview}
                              alt="cover preview"
                              style={{ maxWidth: 220, borderRadius: 8, border: '1px solid var(--border-primary)' }}
                            />
                            {eventEditing && !eventRemoveCover && (
                              <div style={{ marginTop: 6 }}>
                                <button
                                  type="button"
                                  className="btn btn-outline btn-sm"
                                  onClick={() => { setEventRemoveCover(true); setEventCoverPreview(''); if (eventCoverRef.current) eventCoverRef.current.value = ''; }}
                                >
                                  Xoá ảnh bìa hiện tại
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="form-group form-group-full">
                        <label>{lang.COMMON_EXCERPT || 'Tóm tắt ngắn'}</label>
                        <input
                          type="text"
                          value={eventForm.excerpt}
                          onChange={e => setEventForm({ ...eventForm, excerpt: e.target.value })}
                          placeholder={lang.DASHBOARD_EVENT_EXCERPT_PH || 'Một dòng giới thiệu sự kiện...'}
                        />
                      </div>
                      <div className="form-group form-group-full">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <label style={{ margin: 0 }}>{lang.DASHBOARD_EVENT_CONTENT_LABEL || 'Nội dung (Markdown) — Editor & Preview'}</label>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              type="button"
                              className="btn btn-outline btn-sm"
                              onClick={() => eventInlineRef.current?.click()}
                            >
                              📷 Chèn ảnh
                            </button>
                            <input
                              type="file"
                              accept="image/*"
                              ref={eventInlineRef}
                              style={{ display: 'none' }}
                              onChange={handleEventInlineImage}
                            />
                          </div>
                        </div>
                        <div className="md-split">
                          <textarea
                            ref={eventContentRef}
                            rows="18"
                            value={eventForm.content}
                            onChange={e => setEventForm({ ...eventForm, content: e.target.value })}
                            placeholder={lang.DASHBOARD_EVENT_CONTENT_PH || `# Tiêu đề\n\nMô tả sự kiện...\n\n![mô tả](url-ảnh)\n\n- Bullet 1\n- Bullet 2`}
                            style={{ fontFamily: 'var(--font-mono)', fontSize: '0.85rem' }}
                          />
                          <div className="markdown-preview blog-modal-content md-split-preview">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {eventForm.content || (lang.DASHBOARD_EVENT_MD_PREVIEW_EMPTY || '_(Nội dung trống — gõ ở ô bên trái để xem preview)_')}
                            </ReactMarkdown>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="admin-form-actions">
                      <button type="submit" className="btn btn-primary btn-sm">
                        {eventEditing ? 'Cập nhật sự kiện' : 'Tạo sự kiện'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={resetEventForm}
                      >
                        Huỷ
                      </button>
                    </div>
                  </form>
                </div>
                )}

                {/* Events list */}
                {!eventEditing && !showEventForm && (
                <div className="admin-form-card">
                  <h3>Sự Kiện ({events.length})</h3>
                  {events.length === 0 && <p className="empty-text">Chưa có sự kiện nào.</p>}
                  {events.length > 0 && (
                    <Paginator total={eventsPg.total} page={eventsPg.page} pageSize={eventsPg.pageSize} onChange={eventsPg.onChange} position="top" />
                  )}
                  <div className="resource-admin-list">
                    {eventsPg.paged.map(ev => (
                      <div key={ev.id} className="event-admin-card">
                        {ev.coverImage && (
                          <img
                            src={`/uploads/events/${ev.coverImage}`}
                            alt={ev.title}
                            className="event-admin-thumb"
                          />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                            <strong>{ev.title}</strong>
                            <span className={`event-status-badge status-${ev.status || 'upcoming'}`} style={{ position: 'static' }}>
                              {ev.status === 'past' ? 'Đã kết thúc' : ev.status === 'ongoing' ? 'Đang diễn ra' : 'Sắp diễn ra'}
                            </span>
                          </div>
                          {ev.excerpt && <div className="form-hint" style={{ marginBottom: 4 }}>{ev.excerpt}</div>}
                          <div className="resource-item-meta">
                            {ev.eventDate && <span>🗓 {fmtDMY(ev.eventDate)}</span>}
                            {ev.location && <span>📍 {ev.location}</span>}
                            <span>👤 {ev.createdByName}</span>
                            <span>🕒 {fmtDMYTime(ev.createdAt)}</span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                          <button className="btn btn-outline btn-sm" onClick={() => handleEventEdit(ev)}>Sửa</button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleEventDelete(ev.id)}>Xoá</button>
                        </div>
                      </div>
                    ))}
                  </div>
                  {events.length > 0 && (
                    <Paginator total={eventsPg.total} page={eventsPg.page} pageSize={eventsPg.pageSize} onChange={eventsPg.onChange} position="bottom" />
                  )}
                </div>
                )}
              </motion.div>
            )}

            {/* ==================== SMTP SETTINGS (ky_thuat only) ==================== */}
            {activeTab === 'smtp' && can.smtp && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="admin-form-card">
                  <h3>{lang.DASHBOARD_SMTP_TITLE || 'SMTP Configuration'}</h3>
                  <div className="admin-form-grid">
                    <div className="form-group">
                      <label>{lang.DASHBOARD_SMTP_HOST || 'SMTP Host'}</label>
                      <input type="text" name="host" value={smtpConfig.host} onChange={handleSmtpChange} placeholder={lang.DASHBOARD_SMTP_HOST_PH || 'smtp.gmail.com'} />
                    </div>
                    <div className="form-group">
                      <label>{lang.DASHBOARD_SMTP_PORT || 'Port'}</label>
                      <input type="number" name="port" value={smtpConfig.port} onChange={handleSmtpChange} placeholder={lang.DASHBOARD_SMTP_PORT_PH || '587'} />
                    </div>
                    <div className="form-group">
                      <label className="checkbox-label">
                        <input type="checkbox" name="secure" checked={!!smtpConfig.secure} onChange={handleSmtpChange} />
                        {lang.DASHBOARD_SMTP_SECURE || 'Use SSL/TLS (port 465)'}
                      </label>
                    </div>
                    <div className="form-group">
                      <label>{lang.DASHBOARD_SMTP_USER || 'Username (Email)'}</label>
                      <input type="text" name="user" value={smtpConfig.user} onChange={handleSmtpChange} placeholder={lang.DASHBOARD_SMTP_USER_PH || 'your@gmail.com'} />
                    </div>
                    <div className="form-group">
                      <label>{lang.DASHBOARD_SMTP_PASSWORD || 'Password / App Password'}</label>
                      <input type="password" name="password" value={smtpConfig.password} onChange={handleSmtpChange} placeholder={lang.DASHBOARD_SMTP_PW_PLACEHOLDER || 'Leave blank to keep current'} />
                    </div>
                    <div className="form-group">
                      <label>{lang.DASHBOARD_SMTP_FROM_NAME || 'From Name'}</label>
                      <input type="text" name="fromName" value={smtpConfig.fromName} onChange={handleSmtpChange} placeholder={lang.DASHBOARD_SMTP_FROM_NAME_PH || 'ISP Club'} />
                    </div>
                    <div className="form-group">
                      <label>{lang.DASHBOARD_SMTP_FROM_EMAIL || 'From Email'}</label>
                      <input type="email" name="fromEmail" value={smtpConfig.fromEmail} onChange={handleSmtpChange} placeholder={lang.DASHBOARD_SMTP_FROM_EMAIL_PH || 'noreply@ispclub.com'} />
                    </div>
                  </div>
                  <div className="admin-form-actions">
                    <button type="button" className="btn btn-primary btn-sm" onClick={handleSaveSmtp}>
                      {lang.DASHBOARD_SMTP_SAVE || 'Save Configuration'}
                    </button>
                  </div>
                </div>

                <div className="admin-form-card">
                  <h3>{lang.DASHBOARD_SMTP_TEST_TITLE || 'Test Connection'}</h3>
                  <div className="admin-form-grid">
                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                      <label>{lang.DASHBOARD_SMTP_TEST_TO || 'Test recipient email'}</label>
                      <input type="email" value={smtpTestTo} onChange={(e) => setSmtpTestTo(e.target.value)} placeholder={lang.DASHBOARD_SMTP_TEST_TO_PH || 'test@example.com'} />
                    </div>
                  </div>
                  <div className="admin-form-actions">
                    <button type="button" className="btn btn-outline btn-sm" onClick={handleTestSmtp} disabled={smtpTesting}>
                      {smtpTesting ? (lang.DASHBOARD_SMTP_TESTING || 'Testing...') : (lang.DASHBOARD_SMTP_TEST || 'Test & Send Email')}
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Dashboard;
